import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import CashCounterModal from '../components/customer/CashCounterModal';
import EftposTerminalModal from '../components/customer/EftposTerminalModal';
import FailedPaymentBanner from '../components/customer/FailedPaymentBanner';
import ActiveOrderBanner from '../components/customer/ActiveOrderBanner';
import OrderEditDialog from '../components/OrderEditDialog';
import CustomMixDialog from '../components/CustomMixDialog';
import SettleTabDialog from '../components/SettleTabDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, ShoppingBag, Plus, Minus, Wine, CreditCard, Banknote, Receipt, UtensilsCrossed, Flame, GlassWater, Gift, Star, Sparkles, Leaf, Drumstick, Zap, User, LogOut, KeyRound, Phone, ChevronLeft, Eye, EyeOff, Search, X, ArrowRight, BellRing, Check, Menu, LayoutDashboard, BadgeCheck, Calendar, Target, Wand2, Music, AlertCircle } from 'lucide-react';
import AppleLogo from '../components/icons/AppleLogo';
// Local alias so existing JSX (<Apple ... />) keeps working with no further
// touch-ups — the floating menu, cart, retry button etc. all use this name.
const Apple = AppleLogo;
import SpinWheel from '../components/SpinWheel';
import FeedbackPrompt from '../components/FeedbackPrompt';
import MusicRequestModal from '../components/MusicRequestModal';
import FloatingDraggable from '../components/FloatingDraggable';
import { tableLabel } from '../lib/tableLabel';

const categoryConfig = [
  { key: 'all', label: 'All' },
  { key: 'drinks', label: 'Drinks', icon: GlassWater },
  { key: 'food', label: 'Food', icon: UtensilsCrossed },
  { key: 'shisha', label: 'Shisha', icon: Flame },
];

const tagStyles = {
  veg: { label: 'Veg', cls: 'bg-emerald-500/20 text-emerald-400', icon: Leaf },
  'non-veg': { label: 'Non-Veg', cls: 'bg-red-500/20 text-red-400', icon: Drumstick },
  popular: { label: 'Popular', cls: 'bg-gold/20 text-gold', icon: Star },
  spicy: { label: 'Spicy', cls: 'bg-orange-500/20 text-orange-400', icon: Zap },
  mild: { label: 'Mild', cls: 'bg-blue-accent/20 text-blue-accent' },
};

const spiceLevels = ['Mild', 'Medium', 'Hot'];

// Color palette per spice level: Mild=green, Medium=orange, Hot=red.
const SPICE_STYLES = {
  Mild:   { active: 'border-emerald-500/60 bg-emerald-500/10 text-emerald-400', inactive: 'border-white/10 text-zinc-400 hover:border-emerald-500/30' },
  Medium: { active: 'border-orange-500/60 bg-orange-500/10 text-orange-400',    inactive: 'border-white/10 text-zinc-400 hover:border-orange-500/30' },
  Hot:    { active: 'border-red-500/60 bg-red-500/10 text-red-400',             inactive: 'border-white/10 text-zinc-400 hover:border-red-500/30' },
};
const SPICE_BADGE = {
  Mild:   'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  Medium: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  Hot:    'text-red-400 bg-red-500/10 border-red-500/20',
};
const flavorIcons = { sweet: '🍯', minty: '🌿', strong: '💨', classic: '🔥', custom: '🎨' };

const UPSELL_PROMPTS = [
  { trigger: 'shisha', message: 'Upgrade to Premium Darkside Shisha', category: 'shisha', subcategory: 'Premium Darkside' },
  { trigger: 'shisha', message: 'Add a drink with your shisha', category: 'drinks' },
  { trigger: 'food', message: 'Try a platter for better value', category: 'food', subcategory: 'Platters' },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  // Absolute URL (e.g. Unsplash CDN) — pass through unchanged
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${API_BASE}/api/files/${imagePath}`;
};

export default function CustomerPage() {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const { user, customerLogin, customerSignin, customerSigninPick, logout } = useAuth();
  // Takeaway mode: when there is no QR code in the URL, the user is browsing for pickup.
  // Skips table lookup, hides the service bell, restricts menu to non-shisha items,
  // forces card payment, and posts the order to /api/orders/takeaway.
  const isTakeawayMode = !qrCode;
  const [table, setTable] = useState(null);
  const [tableError, setTableError] = useState('');
  const [menu, setMenu] = useState([]);
  const [kitchenOpen, setKitchenOpen] = useState(true);
  const [lastCallUntil, setLastCallUntil] = useState(null);
  const [lastCallLeft, setLastCallLeft] = useState(0);
  const [cart, setCart] = useState(() => {
    // Hydrate cart from localStorage so it persists across app close / background.
    // Stored entry expires after 6 hours (typical dine-in session); keyed per QR code so
    // different tables don't collide on shared devices.
    try {
      const raw = localStorage.getItem(`majnu:cart:${qrCode || 'default'}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      const SIX_HOURS = 6 * 60 * 60 * 1000;
      if (parsed?.savedAt && (Date.now() - parsed.savedAt) > SIX_HOURS) return [];
      return Array.isArray(parsed?.items) ? parsed.items : [];
    } catch { return []; }
  });
  const [activeCategory, setActiveCategory] = useState(() => {
    // Per owner directive (Feb 2026): remember the customer's last
    // top-level tab so a regular who always orders Drinks lands there
    // straight after scanning the QR — shaves a tap off every order.
    // Only remembered for the real-customer routes (table / takeaway);
    // staff iframe ?staff=1 always defaults to 'all' so they start
    // from a clean state when taking an order for a walk-in.
    try {
      if (typeof window === 'undefined') return 'all';
      const params = new URLSearchParams(window.location.search);
      if (params.get('staff') === '1') return 'all';
      const cached = window.localStorage.getItem('majnu:customer:lastTab');
      if (cached && ['all', 'drinks', 'food', 'shisha', 'activities'].includes(cached)) {
        return cached;
      }
    } catch { /* localStorage unavailable in some iframe contexts */ }
    return 'all';
  });
  // Per-category subcategory filter. 'all' = show everything.
  const [activeSub, setActiveSub] = useState({ shisha: 'all', drinks: 'all', food: 'all' });
  // Drinks 2-tier nav (June 2026): customer first picks a top-level
  // SECTION (Cocktails / Spirits / Beer & Wine / Mocktails / Hot Drinks /
  // Soft Drinks & Shakes / Specials), then optionally drills into a leaf
  // subcategory chip below. 'all' = show every section's leaves.
  // Like the top tab above, the section choice is remembered for the
  // next QR scan.
  const [activeDrinksSection, setActiveDrinksSection] = useState(() => {
    try {
      if (typeof window === 'undefined') return 'all';
      const params = new URLSearchParams(window.location.search);
      if (params.get('staff') === '1') return 'all';
      return window.localStorage.getItem('majnu:customer:lastDrinksSection') || 'all';
    } catch { return 'all'; }
  });
  const [drinksSections, setDrinksSections] = useState([]);  // [{section, subcategories}, …]
  const [searchQuery, setSearchQuery] = useState('');
  const [loginForm, setLoginForm] = useState({ first_name: '', last_name: '', email: '', phone: '', postcode: '', password: '' });
  const [signinForm, setSigninForm] = useState({ identifier: '', password: '' });
  // When more than one account matches phone+password, this holds the candidates
  // so the customer can pick which one to sign in to.
  const [signinPickerAccounts, setSigninPickerAccounts] = useState(null);
  const [signinPickLoading, setSigninPickLoading] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [authMode, setAuthMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [loginLoading, setLoginLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileEditMode, setProfileEditMode] = useState(false);
  // Customer top-right nav drawer
  const [navOpen, setNavOpen] = useState(false);
  const [activeNavView, setActiveNavView] = useState(null); // 'dashboard' | 'profile' | 'membership' | null
  const [specials, setSpecials] = useState([]);
  const [specialsLoading, setSpecialsLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm: '' });
  const [orderLoading, setOrderLoading] = useState(false);
  // Default to 'tab' — owner spec Feb 2026 (FINAL ORDERING WORKFLOW): every
  // in-house customer order opens a tab. Staff iframe (`?staff=1`) overrides
  // this from the cart picker. Takeaway flow ignores this and uses 'card'.
  const [paymentMethod, setPaymentMethod] = useState('tab');
  // Settle-tab modal — opens from the cart "Settle Tab" button OR the
  // header tab badge once a tab is active.
  const [settleTabOpen, setSettleTabOpen] = useState(false);
  // For "Tab" payment method: how the customer is paying the $30 deposit.
  // Defaults to 'cash' but the customer can flip to 'card' (EFTPOS at the
  // counter). Trust-based — bartender verifies the collection on site.
  const [tabDepositMethod, setTabDepositMethod] = useState('cash');
  const [tabOpening, setTabOpening] = useState(false);
  // Tab deposit / top-up flows (2026-02): a tab opens immediately so the
  // customer can start ordering, but the $30 deposit is recorded as
  // PENDING. Staff confirm it via the bartender tray, or the customer can
  // self-pay $30 via Square. When the running unpaid balance hits $250,
  // the order endpoint returns a 402 with a structured payload — we capture
  // it here and prompt the customer to top up at least $200.
  const [tabDepositLoading, setTabDepositLoading] = useState(false);
  const [tabTopupPrompt, setTabTopupPrompt] = useState(null); // { tab_id, unpaid_balance, projected_order_total, topup_minimum, ... }
  const [tabTopupLoading, setTabTopupLoading] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  // Tipping is intentionally OFF on the customer flow per owner request —
  // no inline pills, no guest modal, no member selector. Constants kept so
  // the cart/order payloads still send `tip_amount: 0` to the existing
  // API contract without changes.
  const tipAmount = 0;
  const [myOrders, setMyOrders] = useState([]);
  // "My Usuals" — combos a customer has ordered 3+ times. Populated via
  // /api/customer/usuals (works for members via auth cookie and for guests
  // via guest_id query param). Surfaces as a one-tap quick-add chip row.
  const [usuals, setUsuals] = useState([]);
  // Show ALL non-terminal orders + recently-completed ones (backend returns
  // completed orders for the last 30 min so the customer keeps seeing their
  // order even after staff delivers).
  const activeMyOrders = myOrders.filter(o => !['cancelled', 'voided'].includes(o.order_status));
  // When the banner appears/disappears (because order data arrives or is
  // completed >30min ago), force a header re-measurement. ResizeObserver
  // SHOULD catch this, but iOS Safari can lag a frame which leaves the first
  // menu item momentarily clipped. This belt-and-braces fix runs after the
  // banner JSX commits so the new height is captured immediately.
  useEffect(() => {
    if (headerRef.current) {
      // Use rAF so the measurement happens after the layout pass.
      const id = requestAnimationFrame(() => {
        if (headerRef.current) setHeaderH(headerRef.current.offsetHeight + 8);
      });
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [activeMyOrders.length]);
  const [cartOpen, setCartOpen] = useState(false);
  // Auto-open the cart sheet when the customer returns from Square's checkout
  // success page. PaymentSuccess flips sessionStorage 'majnu:autoOpenCart'.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('majnu:autoOpenCart') === '1') {
        sessionStorage.removeItem('majnu:autoOpenCart');
        setCartOpen(true);
      }
    } catch { /* sessionStorage may be disabled */ }
  }, []);
  const [tab, setTab] = useState(null);
  const [spinEligible, setSpinEligible] = useState(false);
  const [showSpin, setShowSpin] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [feedbackOrder, setFeedbackOrder] = useState(null);
  const [customizeItem, setCustomizeItem] = useState(null);
  // Variant (e.g. Single / Double / Bottle 1L) the customer chose on the
  // card before the customize dialog opened. Drives the dialog's Base
  // price + Total and the final cart line. See openCustomize / Feb 2026
  // bug fix comment.
  const [customizeVariant, setCustomizeVariant] = useState(null);
  // Selections for the universal grouped add-ons (spirits): single-select
  // groups store a string (the option name), multi-select groups store an
  // array of option names. Reset whenever the customize dialog opens.
  const [groupSelections, setGroupSelections] = useState({});
  // Custom mix dialog: separate from the standard customise dialog because
  // its UI is rich (flavour slots + rebalancing sliders) and the data model
  // diverges (mix_flavours array vs head_label / spice_level).
  const [mixItem, setMixItem] = useState(null);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [selectedSpice, setSelectedSpice] = useState('Medium');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedHead, setSelectedHead] = useState(null);
  // Map of menu_item.id -> chosen variant label (e.g. "Glass" / "Bottle"). Defaults to first variant.
  const [selectedVariants, setSelectedVariants] = useState({});
  // Map of menu_item.id -> chosen spice level for food cards. Defaults to 'Medium'.
  const [selectedSpices, setSelectedSpices] = useState({});
  // Map of menu_item.id -> chef instructions draft (food cards). Empty when no note.
  const [instructionsDraft, setInstructionsDraft] = useState({});
  // Which item.id currently has its note textarea expanded on the card.
  const [noteOpenFor, setNoteOpenFor] = useState(null);

  // When the user signs in as a customer (via any path — direct sign-in, member
  // dialog, or token refresh), automatically exit guest mode so the header
  // greeting flips from "GUEST" to the regular member view. Without this, a
  // user who started as guest and later signed in still sees the orange
  // GUEST chip because the localStorage flag is stale.
  const isCustomer = user && user.role === 'customer';
  // Staff-mode order placement — manager/director/waiter visits the customer
  // page via `?staff=1` to place an order on behalf of the table. Skip the
  // auth gate and treat them as a named "Staff" guest. Payment defaults to
  // cash at checkout (still overridable in the cart). Only effective if the
  // signed-in user actually has staff privileges.
  const staffOrderMode = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const flag = sp.get('staff') === '1';
      const allowed = ['manager', 'director', 'waiter', 'bartender'];
      return flag && user && allowed.includes(user.role);
    } catch { return false; }
  })();
  // Guest mode — allows customers to browse and order without an account.
  // Persisted per-table so a refresh doesn't bounce them back to the auth screen.
  const [guestMode, setGuestMode] = useState(() => {
    try { return localStorage.getItem(`majnu:guest:${qrCode || 'default'}`) === '1'; }
    catch { return false; }
  });
  const [guestName, setGuestName] = useState(() => {
    try { return localStorage.getItem(`majnu:guestName:${qrCode || 'default'}`) || ''; }
    catch { return ''; }
  });
  // Auto-enter guest mode for staff so they can shop immediately.
  useEffect(() => {
    if (staffOrderMode && !guestMode) {
      setGuestMode(true);
      setGuestName('Staff Order');
    }
  }, [staffOrderMode, guestMode]);
  // Staff mode → cash by default at checkout.
  useEffect(() => {
    if (staffOrderMode) setPaymentMethod('cash');
  }, [staffOrderMode]);

  // Kitchen hours — food ordering is blocked 12am→5pm venue time. Poll every
  // 2 min so a phone left open past midnight flips to "kitchen closed" on its own.
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get('/kitchen/status');
        setKitchenOpen(data.kitchen_open !== false);
        if (data.last_call && data.seconds_to_close > 0) {
          setLastCallUntil(Date.now() + data.seconds_to_close * 1000);
        } else {
          setLastCallUntil(null);
        }
      } catch { /* keep last known state */ }
    };
    check();
    const t = setInterval(check, 120000);
    return () => clearInterval(t);
  }, []);

  // Last-call countdown ticker (23:30→midnight). Ticks locally every second;
  // at zero we optimistically flip to "kitchen closed" (backend enforces it).
  useEffect(() => {
    if (!lastCallUntil) { setLastCallLeft(0); return undefined; }
    const tick = () => {
      const left = Math.max(0, Math.round((lastCallUntil - Date.now()) / 1000));
      setLastCallLeft(left);
      if (left === 0) {
        setLastCallUntil(null);
        setKitchenOpen(false);
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [lastCallUntil]);

  const lastCallClock = `${Math.floor(lastCallLeft / 60)}:${String(lastCallLeft % 60).padStart(2, '0')}`;
  useEffect(() => {
    if (isCustomer && guestMode) {
      setGuestMode(false);
      try { localStorage.removeItem(`majnu:guest:${qrCode || 'default'}`); } catch { /* sandboxed */ }
    }
  }, [isCustomer, guestMode, qrCode]);
  const canShop = isCustomer || guestMode || isTakeawayMode || staffOrderMode;
  // Manager-only "Custom Product" capability — for ad-hoc charges (damages,
  // off-menu items, manual fees). Visible only in staff order mode AND only
  // to manager/director. Customers never see this UI; the backend also
  // hard-blocks the endpoint with a 403 for any other role.
  const canAddCustomProduct = staffOrderMode && user && ['manager', 'director'].includes(user.role);
  const [customProductOpen, setCustomProductOpen] = useState(false);
  const [customProductDraft, setCustomProductDraft] = useState({ name: '', price: '', qty: 1, notes: '' });
  // Post-order signup CTA dialog
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  // Pay-at-counter blocker. Persisted to localStorage so a refresh / closed
  // tab / accidental browser back can't be used to escape the prompt — the
  // only way out is staff marking the order paid (which we detect by
  // watching myOrders for the order_id to flip to payment_status='paid'
  // or order_status in {voided, cancelled}).
  // Track whether an alert was restored from localStorage on mount vs.
  // set during this session. The bell auto-fire effect only fires for
  // **transient** sets (i.e. when the customer just placed a cash/EFTPOS
  // order this session) — never for restored alerts. Without this guard,
  // every QR scan that lands on a device with stale localStorage causes
  // the service bell to ring immediately (owner-reported bug Feb 28 2026).
  const restoredAlertsRef = useRef({ cash: false, eftpos: false });
  // TTL (ms) — alerts older than this expire from localStorage on the
  // next mount, even before /orders polls back to confirm the underlying
  // order is paid/voided. Belt-and-braces guard against shared devices,
  // long-idle tabs, and any other route to stale alert state. 10 min is
  // a comfortable upper bound on how long a payment flow could legitimately
  // take (manager bringing the EFTPOS, customer finding their wallet, etc).
  const ALERT_TTL_MS = 10 * 60 * 1000;
  const _readAlert = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Auto-evict expired entries. Legacy entries without `setAt` are
      // assumed stale and dropped — they're from before the TTL guard
      // shipped and we have no way to know when they were set.
      if (!parsed?.setAt || (Date.now() - parsed.setAt) > ALERT_TTL_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed;
    } catch { return null; }
  };
  const [cashCounterAlert, setCashCounterAlertState] = useState(() => {
    const parsed = _readAlert('majnu:cashCounterAlert');
    if (parsed) restoredAlertsRef.current.cash = true;
    return parsed;
  });
  const setCashCounterAlert = useCallback((next) => {
    // Any explicit set during the session counts as transient — clears the
    // restored flag so the bell-auto-fire effect can do its job.
    restoredAlertsRef.current.cash = false;
    setCashCounterAlertState(next);
    try {
      if (next) {
        // Stamp `setAt` so future mounts can TTL-expire the entry.
        const stamped = next.setAt ? next : { ...next, setAt: Date.now() };
        localStorage.setItem('majnu:cashCounterAlert', JSON.stringify(stamped));
      } else {
        localStorage.removeItem('majnu:cashCounterAlert');
      }
    } catch { /* sandboxed */ }
  }, []);
  // EFTPOS terminal blocker — manager comes to the table with the EFTPOS
  // terminal. Same dismiss contract as cashCounterAlert: only the
  // backend (payment_status='paid' or order voided) can clear it.
  const [eftposAlert, setEftposAlertState] = useState(() => {
    const parsed = _readAlert('majnu:eftposAlert');
    if (parsed) restoredAlertsRef.current.eftpos = true;
    return parsed;
  });
  const setEftposAlert = useCallback((next) => {
    restoredAlertsRef.current.eftpos = false;
    setEftposAlertState(next);
    try {
      if (next) {
        const stamped = next.setAt ? next : { ...next, setAt: Date.now() };
        localStorage.setItem('majnu:eftposAlert', JSON.stringify(stamped));
      } else {
        localStorage.removeItem('majnu:eftposAlert');
      }
    } catch { /* sandboxed */ }
  }, []);
  const [memberForm, setMemberForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '' });
  const [memberSubmitting, setMemberSubmitting] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  // Guest checkout dialog (just asks for name before placing order)
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);
  const [guestNameDraft, setGuestNameDraft] = useState('');

  // Music request modal — floating "🎵 Request a Song" button. Hidden when
  // the backend has no YouTube API key configured (probed once on mount).
  const [musicModalOpen, setMusicModalOpen] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(false);
  // Live queue snapshot — small "N up next" pill above the music button so
  // guests can see the vibe of the queue, and "Your song · #X" once their
  // own request is in flight. Polled every 20s while the feature is enabled.
  const [musicQueue, setMusicQueue] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get('/youtube/status').then(({ data }) => {
      if (alive) setMusicEnabled(!!data?.enabled);
    }).catch(() => { /* probe failure → keep disabled */ });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!musicEnabled) return undefined;
    let alive = true;
    const fetchQueue = async () => {
      try {
        const { data } = await api.get('/music-queue');
        if (alive) setMusicQueue(Array.isArray(data) ? data : []);
      } catch (_e) { /* silent — pill just hides */ }
    };
    fetchQueue();
    const t = setInterval(fetchQueue, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [musicEnabled]);

  // ----- TAKEAWAY-SPECIFIC STATE -----
  // Persisted per-device so a returning user doesn't have to retype.
  const [takeawayPhone, setTakeawayPhone] = useState(() => {
    try { return localStorage.getItem('majnu:takeawayPhone') || ''; } catch { return ''; }
  });
  // ASAP / +15 / +30 / +45 (minutes)
  const [pickupTime, setPickupTime] = useState('ASAP');
  // Modal opens on first takeaway placeOrder if name/phone are missing.
  const [takeawayInfoOpen, setTakeawayInfoOpen] = useState(false);
  const [takeawayDraft, setTakeawayDraft] = useState({ name: '', phone: '' });

  const fetchTable = useCallback(async () => {
    if (isTakeawayMode) {
      // Mark this device as last using takeaway so PaymentSuccess can route back here
      try { localStorage.setItem('majnu:lastReturnPath', '/takeaway'); } catch { /* localStorage may be disabled in private mode */ }
      return;
    }
    try {
      const { data } = await api.get(`/tables/qr/${qrCode}`); setTable(data);
      // Remember this QR so PaymentSuccess's "Back to Menu" can land us in the cart instead of the Square page
      try { localStorage.setItem('majnu:lastReturnPath', `/table/${qrCode}`); } catch { /* localStorage may be disabled in private mode */ }
    } catch { setTableError('Invalid table QR code'); }
  }, [qrCode, isTakeawayMode]);
  const fetchMenu = useCallback(async () => {
    try {
      // Managers in Staff Order Mode need the hidden "Custom Product" line
      // item too — /menu/all returns it, /menu hides it for normal
      // customers (is_manager_only filter).
      const endpoint = (staffOrderMode && user && ['manager', 'director'].includes(user.role))
        ? '/menu/all'
        : '/menu';
      const { data } = await api.get(endpoint);
      // Shisha and Activities are dine-in only — strip from the menu when this page is in takeaway mode
      const filtered = isTakeawayMode ? (data || []).filter(i => i.category !== 'shisha' && i.category !== 'activities') : data;
      // Pin Custom Product (is_custom_charge=true) to the very top so
      // managers can grab it without scrolling.
      const sorted = [...(filtered || [])].sort((a, b) => {
        const av = a.is_custom_charge ? 1 : 0;
        const bv = b.is_custom_charge ? 1 : 0;
        return bv - av;
      });
      setMenu(sorted);
      // Pull the 2-tier drinks nav config once. Cached for the life of
      // the page — re-fetched only on page reload.
      if (drinksSections.length === 0) {
        try {
          const { data: secs } = await api.get('/menu/drinks-sections');
          setDrinksSections(Array.isArray(secs) ? secs : []);
        } catch (e) { /* non-fatal — frontend falls back to flat chips */ }
      }
    } catch (err) { console.error('[CustomerPage] error:', err); }
  }, [isTakeawayMode, staffOrderMode, user]);
  const [favourites, setFavourites] = useState([]);
  const fetchFavourites = useCallback(async () => {
    try { const { data } = await api.get('/orders/my-favourites?limit=4'); setFavourites(data || []); }
    catch { setFavourites([]); }
  }, []);
  // Per-device anonymous guest identifier. Lets the same browser/PWA see its
  // own active orders without re-auth. Generated once and stored forever,
  // but can be rewritten by the server-side `resolve` endpoint so the same
  // person typing the same name on the same table within 4h always gets
  // back the SAME id — survives cleared cookies, second devices, incognito.
  // Walk-in guest identity is scoped PER-QR (Feb 2026 session-isolation fix).
  // Storing a global `majnu:guestId` allowed a customer who scanned T13 to
  // change the URL to T1 and instantly see their previous tab on T1 — i.e.
  // the QR scan didn't actually bind the session to a single table. We now
  // key the id under `majnu:guestId:<qrCode>` so:
  //   • Refreshing the SAME table URL keeps your tab (same key).
  //   • Switching URL to ANY other table mints a fresh id → no leakage.
  //   • Takeaway uses key `__takeaway` so it stays stable across visits.
  // Legacy `majnu:guestId` values are migrated into the per-QR slot on first
  // load so existing tabs stay attached to the customer that placed them.
  const guestIdStorageKey = `majnu:guestId:${qrCode || '__takeaway'}`;
  const [guestId, setGuestId] = useState(() => {
    try {
      let g = localStorage.getItem(guestIdStorageKey);
      if (!g) {
        // One-time migration: if a legacy global id exists AND this is the
        // first QR the customer has scanned since the migration, adopt it
        // so we don't orphan an in-flight tab. After that, drop the global.
        const legacy = localStorage.getItem('majnu:guestId');
        const lastQr = localStorage.getItem('majnu:lastQrCode');
        if (legacy && (!lastQr || lastQr === qrCode)) {
          g = legacy;
        } else {
          g = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `g_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        }
        localStorage.setItem(guestIdStorageKey, g);
        try { localStorage.removeItem('majnu:guestId'); } catch { /* sandboxed */ }
      }
      return g;
    } catch { return null; }
  });

  // Server-side guest identity resolver. Call this BEFORE placing a guest
  // order or opening a tab whenever we know the customer's display name.
  // It returns either the same `guestId` or — if the server can match an
  // active session under (table, name) within the last 4h — the original
  // session's id. We replace local state + localStorage with the result so
  // every subsequent request hits the same session row.
  const resolveGuestIdentity = useCallback(async (name) => {
    if (!table?.id || !name) return guestId;
    try {
      const { data } = await api.post('/guest-identity/resolve', {
        table_id: table.id,
        customer_name: name,
        guest_id: guestId || null,
      });
      const resolved = data?.guest_id;
      if (resolved && resolved !== guestId) {
        try { localStorage.setItem(guestIdStorageKey, resolved); } catch { /* sandboxed */ }
        setGuestId(resolved);
        return resolved;
      }
      return guestId;
    } catch {
      // If the resolver fails, fall back to the client-side id rather than
      // blocking the order. Worst case the customer gets a new session row.
      return guestId;
    }
  }, [table?.id, guestId]);

  // Persist the last QR the customer scanned so the GlobalServiceBell can
  // resolve the table context on any other route (Reservations, Payment
  // Success, etc.). Cleared only when the customer signs out on the takeaway
  // / table flows; left alone otherwise so a returning customer keeps the
  // bell wired up.
  useEffect(() => {
    if (qrCode && !isTakeawayMode) {
      try { localStorage.setItem('majnu:lastQrCode', qrCode); } catch { /* sandboxed */ }
    }
  }, [qrCode, isTakeawayMode]);

  // ---- 3-hour table session (Feb 2026 owner ask) ----
  // "Customers are able to ring the service bell from home? I want the
  // session from the time they scan the QR to be active only for 3 hrs
  // and then they should scan the QR code again to start a new session
  // except from the last order they placed".
  //
  // Implementation:
  //   - Stamp `majnu:tableSession:{tableId} = { startedAt }` in
  //     localStorage when the QR is first scanned on a table.
  //   - Treat the session as still alive if `now - max(startedAt,
  //     lastOrderAt_on_this_table) < 3h`. So every fresh order extends
  //     the window by another 3h — a long sitting customer doesn't get
  //     kicked out, but somebody who walks out and tries to ring from
  //     home eventually expires.
  //   - When expired we only hide the bell (and the place-order CTA
  //     downstream) — the customer can still see their last order on
  //     the page, matching the "except from the last order they placed"
  //     caveat.
  //   - Members (`isCustomer`) are EXEMPT — they can ring from anywhere
  //     because they're authenticated against an account.
  //   - Takeaway flow is exempt — there's no QR-scoped session there.
  const SESSION_TTL_MS = 3 * 60 * 60 * 1000;  // 3 hours
  const sessionStorageKey = table?.id ? `majnu:tableSession:${table.id}` : null;

  // Stamp the session-started timestamp the first time we land on a
  // valid table (QR scanned). Subsequent loads keep the existing
  // startedAt so the 3h clock doesn't reset on every page reload —
  // EXCEPT when the existing stamp is already past TTL, in which case
  // a fresh scan should reset the clock (this is the "scan QR again
  // to start a new session" path the owner asked for).
  useEffect(() => {
    if (!sessionStorageKey || isTakeawayMode || isCustomer) return;
    try {
      const existing = localStorage.getItem(sessionStorageKey);
      let startedAt = 0;
      if (existing) {
        const parsed = JSON.parse(existing);
        startedAt = Date.parse(parsed?.startedAt || '') || 0;
      }
      const isStale = startedAt && (Date.now() - startedAt) > SESSION_TTL_MS;
      if (!existing || isStale) {
        localStorage.setItem(sessionStorageKey, JSON.stringify({
          startedAt: new Date().toISOString(),
        }));
      }
    } catch { /* sandboxed */ }
  }, [sessionStorageKey, isTakeawayMode, isCustomer, SESSION_TTL_MS]);

  // Tick state so the derived `sessionExpired` flag re-evaluates without
  // requiring an order refetch — 60s granularity is plenty for a 3h TTL.
  const [sessionNow, setSessionNow] = useState(() => Date.now());
  useEffect(() => {
    if (isCustomer || isTakeawayMode || !table?.id) return undefined;
    const id = setInterval(() => setSessionNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [isCustomer, isTakeawayMode, table?.id]);

  // Derived: is the QR-scoped session still active? Members & takeaway
  // are always treated as active. Guests on a table are active iff the
  // most recent activity (QR scan OR last order on this table) is < 3h.
  const sessionExpired = useMemo(() => {
    if (isCustomer || isTakeawayMode || !table?.id) return false;
    let lastActivity = 0;
    try {
      const raw = sessionStorageKey ? localStorage.getItem(sessionStorageKey) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.startedAt) lastActivity = Date.parse(parsed.startedAt) || 0;
      }
    } catch { /* sandboxed */ }
    // Each order on this table by this guest extends the window.
    if (Array.isArray(myOrders)) {
      for (const o of myOrders) {
        if (o?.table_id !== table.id) continue;
        const ts = Date.parse(o?.created_at || '') || 0;
        if (ts > lastActivity) lastActivity = ts;
      }
    }
    if (!lastActivity) return false;  // never stamped — don't lock out
    return (sessionNow - lastActivity) > SESSION_TTL_MS;
  }, [isCustomer, isTakeawayMode, table?.id, sessionStorageKey, sessionNow, myOrders, SESSION_TTL_MS]);

  const fetchOrders = useCallback(async () => {
    // Members → /orders (auth required). Guests → /orders/guest with our
    // device guest_id so the customer can revisit and add more.
    if (isCustomer) {
      try {
        const { data } = await api.get('/orders');
        setMyOrders(data);
        myOrdersLoadedRef.current = true;
      } catch (err) { console.error('[CustomerPage] error:', err); }
    } else if (table?.id && guestId) {
      try {
        const { data } = await api.get(`/orders/guest?table_id=${table.id}&guest_id=${guestId}`);
        setMyOrders(data || []);
        myOrdersLoadedRef.current = true;
      } catch (err) { console.error('[CustomerPage] error:', err); }
    }
  }, [isCustomer, table?.id, guestId]);

  // Fetch "My Usual" combos for the current identity (customer auth OR
  // guest_id). 404/401 just returns []. Refreshes after every order placement.
  const fetchUsuals = useCallback(async () => {
    try {
      const url = isCustomer
        ? '/customer/usuals'
        : (guestId ? `/customer/usuals?guest_id=${encodeURIComponent(guestId)}` : null);
      if (!url) { setUsuals([]); return; }
      const { data } = await api.get(url);
      setUsuals(Array.isArray(data) ? data : []);
    } catch (_err) { setUsuals([]); }
  }, [isCustomer, guestId]);
  const fetchTab = useCallback(async () => {
    // Works for both members (lookup by user.id) and walk-in guests
    // (lookup by per-device guestId). Either key resolves to the same active
    // tab document on the backend.
    // Feb 2026 session-isolation: pass the current table_id so the backend
    // refuses to surface a tab that was opened on a different table — even
    // if a URL-tampering attacker knows the guest_id.
    const key = user?.id || guestId;
    if (!key) return;
    try {
      const qs = table?.id ? `?table_id=${encodeURIComponent(table.id)}` : '';
      const { data } = await api.get(`/payments/tab/${key}${qs}`);
      setTab(data);
    } catch (err) { console.error('[CustomerPage] error:', err); }
  }, [user, guestId, table?.id]);

  // ---- Sibling tabs on the same table (Feb 2026 owner ask) ----
  // The customer's "Settle Tab" dialog needs to know about OTHER tabs on
  // the same table so we can offer a "Settle the whole table" option to
  // whoever is picking up the bill for the group. We fetch the
  // /tables/overview aggregate (same source the till uses), filter to
  // this table, and exclude my own tab so the figure shown is the
  // others-only total (added to my own owed in the dialog).
  const [tableTabs, setTableTabs] = useState([]);
  const fetchTableTabs = useCallback(async () => {
    if (!table?.id) { setTableTabs([]); return; }
    try {
      // Feb 2026 IDOR fix — was hitting `/tables/overview` which is now
      // staff-only. Customers / members must use the narrower
      // `/tabs/by-table/{id}` endpoint that returns ONLY tabs owned by
      // the calling guest_id or signed-in member. Stops any
      // walk-in-with-account from seeing (or joining) another
      // customer's tab on a different table.
      const qs = guestId ? `?guest_id=${encodeURIComponent(guestId)}` : '';
      const { data } = await api.get(`/tabs/by-table/${table.id}${qs}`);
      const tabs = (data || []).map((t) => ({
        id: t.id,
        customer_name: t.customer_name,
        current_total: Number(t.current_total || 0),
        paid_amount: Number(t.paid_amount || 0),
      }));
      setTableTabs(tabs);
    } catch (err) {
      // Silent fallback — keeps the dialog functional even if the
      // public aggregate endpoint errors for some reason.
      setTableTabs([]);
    }
  }, [table?.id, guestId]);
  useEffect(() => {
    if (!isTakeawayMode && table?.id) fetchTableTabs();
  }, [isTakeawayMode, table?.id, fetchTableTabs, tab?.id]);
  const fetchSpinEligibility = useCallback(async () => {
    if (!isCustomer) return;
    try { const { data } = await api.get('/rewards/eligibility'); setSpinEligible(data.eligible); } catch (err) { console.error('[CustomerPage] error:', err); }
  }, [isCustomer]);
  const fetchRewards = useCallback(async () => {
    if (!isCustomer) return;
    try { const { data } = await api.get('/rewards/wallet'); setRewards(data); } catch (err) { console.error('[CustomerPage] error:', err); }
  }, [isCustomer]);

  // ---- Service Request state ----
  const [serviceRequest, setServiceRequest] = useState(null); // { id, status, accepted_by_name, ... } or null
  const [serviceRequestLoading, setServiceRequestLoading] = useState(false);

  // Persist cart to localStorage on every change so it survives app close / background.
  useEffect(() => {
    try {
      // localStorage persists CART (item_id + qty + variant) for this QR
      // table. No PII, no auth tokens — safe to leave in localStorage. Auth
      // is via HttpOnly cookies (see /app/backend/server.py).
      const key = `majnu:cart:${qrCode || 'default'}`;
      if (cart.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), items: cart }));
      }
    } catch { /* localStorage may be disabled in private mode */ }
  }, [cart, qrCode]);

  const fetchServiceRequest = useCallback(async () => {
    if (!isCustomer && !(guestMode && guestId)) return;
    try {
      const url = isCustomer
        ? '/service-requests/my-table'
        : `/service-requests/my-table?guest_id=${encodeURIComponent(guestId)}`;
      const { data } = await api.get(url);
      setServiceRequest(data && data.id ? data : null);
    } catch (err) { console.error('[CustomerPage] error:', err); }
  }, [isCustomer, guestMode, guestId]);
  const callService = async (requestType = 'help') => {
    if (serviceRequestLoading) return;
    setServiceRequestLoading(true);
    try {
      const payload = { table_id: table.id, request_type: requestType };
      if (!isCustomer) {
        payload.guest_id = guestId;
        // Backend defaults to "A guest" when name is empty — guests should
        // be able to ring without being forced through a name-entry step.
        if (guestName) payload.customer_name = guestName;
      }
      const { data } = await api.post('/service-requests', payload);
      setServiceRequest(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not call service');
    } finally {
      setServiceRequestLoading(false);
    }
  };

  const cancelService = async () => {
    if (!serviceRequest || serviceRequestLoading) return;
    setServiceRequestLoading(true);
    try {
      const url = isCustomer
        ? `/service-requests/${serviceRequest.id}/cancel`
        : `/service-requests/${serviceRequest.id}/cancel?guest_id=${encodeURIComponent(guestId)}`;
      await api.put(url);
      setServiceRequest(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
      // refresh in case staff just accepted
      fetchServiceRequest();
    } finally {
      setServiceRequestLoading(false);
    }
  };

  // Customer says "they already came over" — dismisses the accepted/pending
  // service request manually. Different from cancel which is for "I changed
  // my mind". Backend marks `resolved_by_customer=true` for audit.
  const dismissService = async () => {
    if (!serviceRequest || serviceRequestLoading) return;
    setServiceRequestLoading(true);
    try {
      const url = isCustomer
        ? `/service-requests/${serviceRequest.id}/dismiss`
        : `/service-requests/${serviceRequest.id}/dismiss?guest_id=${encodeURIComponent(guestId)}`;
      await api.put(url);
      setServiceRequest(null);
      toast.success('Thanks — marked as done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark as done');
      fetchServiceRequest();
    } finally {
      setServiceRequestLoading(false);
    }
  };

  useEffect(() => { fetchTable(); fetchMenu(); }, [fetchTable, fetchMenu]);

  // Failed-payment banner: poll every 8s while the table is loaded so customers
  // who just got bounced from Square checkout see a clear "your last payment didn't go through" message.
  const [lastFailedAttempt, setLastFailedAttempt] = useState(null);
  const fetchLastAttempt = useCallback(async () => {
    if (!table?.id) return;
    try {
      // Feb 2026: backend now scopes the lookup by caller identity
      // (auth cookie or guest_id) so other customers on the same table
      // can't see your failed-payment items / Square retry URL.
      const params = new URLSearchParams({ table_id: table.id });
      if (guestId) params.set('guest_id', guestId);
      const { data } = await api.get(`/payments/last-attempt?${params.toString()}`);
      setLastFailedAttempt(data?.has_attempt ? data : null);
    } catch { /* silent — non-critical */ }
  }, [table?.id, guestId]);
  useEffect(() => { if (table?.id) { fetchLastAttempt(); const i = setInterval(fetchLastAttempt, 8000); return () => clearInterval(i); } }, [table?.id, fetchLastAttempt]);
  const dismissFailedAttempt = () => setLastFailedAttempt(null);
  useEffect(() => { if (isCustomer) { fetchOrders(); fetchTab(); fetchSpinEligibility(); fetchRewards(); fetchServiceRequest(); fetchFavourites(); fetchUsuals(); } }, [isCustomer, fetchOrders, fetchTab, fetchSpinEligibility, fetchRewards, fetchServiceRequest, fetchFavourites, fetchUsuals]);
  // Guests also need their tab status so the UI can swap "Open Tab — Pay $30"
  // for the active tab banner once they've registered the upfront deposit.
  useEffect(() => { if (!isCustomer && guestId) { fetchTab(); fetchUsuals(); } }, [isCustomer, guestId, fetchTab, fetchUsuals]);
  useEffect(() => { if (isCustomer) { const i = setInterval(fetchOrders, 10000); return () => clearInterval(i); } }, [isCustomer, fetchOrders]);

  // Keep the local `tab` state in sync with the server every 15s so a tab
  // closed/voided/force-cleared by staff (or by the customer cancelling
  // their last tab order) is reflected in the cart UI promptly — instead
  // of leaving the customer with a stale "Tab Active" banner and a
  // mysterious "No active tab" error when they hit Place Order.
  // (The order POST also has a self-heal retry path; this polling makes
  // the UI honest BEFORE the customer even tries.)
  useEffect(() => {
    if (!isCustomer && !guestId) return;
    const i = setInterval(fetchTab, 15000);
    return () => clearInterval(i);
  }, [isCustomer, guestId, fetchTab]);

  // Guest polling for cash-counter modal: when a guest has the blocker open
  // we MUST keep polling /orders so we can detect the staff marking it paid.
  // Regular guest polling (line ~507) only fires when on /table; this covers
  // any other route the guest may have wandered to.
  useEffect(() => {
    if (isCustomer) return; // members already polled above
    if (!cashCounterAlert?.orderId) return;
    fetchOrders();
    const i = setInterval(fetchOrders, 5000);
    return () => clearInterval(i);
  }, [isCustomer, cashCounterAlert, fetchOrders]);

  // Track whether myOrders has been successfully fetched at least once so we
  // can disambiguate "loading" from "definitively missing" in the watcher
  // below. Without this, the initial empty array on first paint would
  // wrongly clear the modal before /orders responds.
  const myOrdersLoadedRef = useRef(false);

  // Pay-at-counter blocker: auto-fire a `payment` service request the
  // FIRST time the modal appears for a given order. This makes the staff
  // bell panel light up immediately — without the customer needing to
  // press the in-modal "Ring service bell" button. The ref keeps us from
  // re-firing every render or after the customer manually rings.
  const bellAutoFiredRef = useRef(new Set());
  useEffect(() => {
    const oid = cashCounterAlert?.orderId;
    if (!oid || bellAutoFiredRef.current.has(oid)) return;
    if (!table?.id) return;
    // Skip auto-fire when the alert was restored from localStorage (stale
    // session). Staff already got notified when the order was first placed.
    if (restoredAlertsRef.current.cash) {
      bellAutoFiredRef.current.add(oid);
      return;
    }
    bellAutoFiredRef.current.add(oid);
    api.post('/service-requests', {
      table_id: table.id,
      request_type: 'payment',
      guest_id: guestId,
      customer_name: cashCounterAlert?.name || undefined,
      note: `Awaiting cash payment for order #${oid.slice(0, 8).toUpperCase()}`,
    }).catch(() => { /* dedup-by-table on the backend means this is best-effort */ });
  }, [cashCounterAlert, table?.id, guestId]);

  // EFTPOS counterpart: same idea, different request_type so staff see
  // the EFTPOS-specific copy ("Time to Collect Payment via EFTPOS") on
  // their bell tray.
  useEffect(() => {
    const oid = eftposAlert?.orderId;
    if (!oid || bellAutoFiredRef.current.has(`eftpos:${oid}`)) return;
    if (!table?.id) return;
    if (restoredAlertsRef.current.eftpos) {
      bellAutoFiredRef.current.add(`eftpos:${oid}`);
      return;
    }
    bellAutoFiredRef.current.add(`eftpos:${oid}`);
    api.post('/service-requests', {
      table_id: table.id,
      request_type: 'payment_eftpos',
      guest_id: guestId,
      customer_name: eftposAlert?.name || undefined,
      note: `Time to Collect Payment via EFTPOS — order #${oid.slice(0, 8).toUpperCase()} ($${(eftposAlert?.total || 0).toFixed(2)})`,
    }).catch(() => { /* dedup-by-table on backend; best-effort */ });
  }, [eftposAlert, table?.id, guestId]);

  // Auto-clear the cash-counter blocker the moment the backend reports the
  // order is paid (staff hit "Cash Received" / "Paid Card") or it was
  // voided / cancelled. The customer never has a way to dismiss it
  // themselves — this is the only path that closes the modal.
  //
  // IMPORTANT: the customer-facing /orders endpoint deliberately filters
  // voided/cancelled orders out of the response (privacy). So when a
  // manager voids the order, it doesn't show up here as `status=voided`
  // — it just disappears entirely. We treat "alert order absent from the
  // refreshed list" as an implicit void/cancel signal and clear too.
  useEffect(() => {
    if (!cashCounterAlert?.orderId) return;
    if (!myOrdersLoadedRef.current) return; // wait for first successful fetch
    const o = myOrders.find(x => x.id === cashCounterAlert.orderId);
    if (o) {
      if (o.payment_status === 'paid' || o.payment_status === 'refunded') {
        setCashCounterAlert(null);
        toast.success('Payment confirmed by staff. Thank you!', { duration: 5000 });
      } else if (['voided', 'cancelled'].includes(o.order_status)) {
        setCashCounterAlert(null);
        toast.message('Your order was cancelled by staff.', { duration: 5000 });
      }
    } else {
      // Order is gone from the customer-visible list. Either:
      //   (a) manager voided/cancelled it (most common cause of this bug)
      //   (b) the order completed > 30 min ago (already long-paid, fine to clear)
      // Either way, the blocker has no purpose anymore. Clear it.
      setCashCounterAlert(null);
      toast.message('Your order was cancelled by staff.', { duration: 5000 });
    }
  }, [myOrders, cashCounterAlert, setCashCounterAlert]);

  // EFTPOS counterpart auto-clear watcher. Same logic shape: payment_status
  // hits 'paid' (manager tapped Paid (Card) after running the EFTPOS) →
  // clear; order disappears from /orders → assume voided → clear.
  useEffect(() => {
    if (!eftposAlert?.orderId) return;
    if (!myOrdersLoadedRef.current) return;
    const o = myOrders.find(x => x.id === eftposAlert.orderId);
    if (o) {
      if (o.payment_status === 'paid' || o.payment_status === 'refunded') {
        setEftposAlert(null);
        toast.success('EFTPOS payment confirmed. Thank you!', { duration: 5000 });
      } else if (['voided', 'cancelled'].includes(o.order_status)) {
        setEftposAlert(null);
        toast.message('Your order was cancelled by staff.', { duration: 5000 });
      }
    } else {
      setEftposAlert(null);
      toast.message('Your order was cancelled by staff.', { duration: 5000 });
    }
  }, [myOrders, eftposAlert, setEftposAlert]);
  // Guest polling — fetch this device's active orders every 10s once we know
  // the table. Lets a guest place an order, close the cart, and still see it
  // in their Orders tab plus add more to it.
  useEffect(() => {
    if (isCustomer) return;
    if (!table?.id || !guestId) return;
    fetchOrders();
    const i = setInterval(fetchOrders, 10000);
    return () => clearInterval(i);
  }, [isCustomer, table?.id, guestId, fetchOrders]);
  // Poll service-request status every 6s so the "Service is on the way" banner updates when staff accepts.
  useEffect(() => {
    if (!isCustomer) return;
    const i = setInterval(fetchServiceRequest, 6000);
    return () => clearInterval(i);
  }, [isCustomer, fetchServiceRequest]);

  // ---- Closing-time announcement banner ----
  // Polls every 30s. The backend fires "last_call" at 00:00 Melbourne and
  // "closing" at 00:30. We render a sticky gold banner at the top so even
  // customers who blocked push notifications get a nudge.
  const [announcement, setAnnouncement] = useState(null);
  // IDs the customer has tapped "Hide" on. Persisted to sessionStorage so
  // the banner doesn't re-appear after a refresh within the same visit
  // (but a fresh session — i.e. tomorrow night — will see it again).
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState(() => {
    try {
      const raw = sessionStorage.getItem('majnu:dismissedAnnouncements');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const dismissAnnouncement = useCallback((id) => {
    if (!id) return;
    setDismissedAnnouncementIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try { sessionStorage.setItem('majnu:dismissedAnnouncements', JSON.stringify(Array.from(next))); } catch {/* ignore */}
      return next;
    });
  }, []);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/announcements/active');
        if (!cancelled) setAnnouncement(data?.announcement || null);
      } catch (_err) {
        if (!cancelled) setAnnouncement(null);
      }
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault(); setLoginLoading(true);
    if (loginForm.password.length < 4) { toast.error('Password must be at least 4 characters'); setLoginLoading(false); return; }
    try { await customerLogin(loginForm, table.id); toast.success('Welcome to Majnu!'); fetchMenu(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Signup failed'); }
    finally { setLoginLoading(false); }
  };

  const handleSignin = async (e) => {
    e.preventDefault(); setLoginLoading(true);
    try {
      const result = await customerSignin(signinForm.identifier, signinForm.password, table.id);
      if (result?.multiple) {
        setSigninPickerAccounts(result.accounts);
      } else {
        toast.success('Welcome back!'); fetchMenu();
      }
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Invalid email/phone or password'); }
    finally { setLoginLoading(false); }
  };

  const handleSigninPick = async (account) => {
    setSigninPickLoading(account.id);
    try {
      await customerSigninPick(signinForm.identifier, signinForm.password, account.id, table.id);
      setSigninPickerAccounts(null);
      toast.success(`Welcome back, ${account.first_name || account.email.split('@')[0]}!`);
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign in');
    } finally {
      setSigninPickLoading(null);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault(); setLoginLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password-otp', { email: resetEmail });
      setResetResult({ email_masked: data?.email_masked || '', step: 'verify' });
      toast.success(data?.message || 'Code sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send code'); }
    finally { setLoginLoading(false); }
  };

  const handleResetVerify = async (e) => {
    e.preventDefault(); setLoginLoading(true);
    try {
      await api.post('/auth/verify-otp-reset', {
        email: resetEmail,
        code: resetResult.code,
        new_password: resetResult.new_password,
      });
      setResetResult({ ...resetResult, step: 'done' });
      toast.success('Password reset! You can sign in now.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Verification failed'); }
    finally { setLoginLoading(false); }
  };

  const handleProfileSave = async () => {
    setProfileLoading(true);
    try {
      await api.put('/auth/customer-profile', profileForm);
      toast.success('Profile updated');
      setProfileEditMode(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setProfileLoading(false); }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_password.length < 4) { toast.error('Min 4 characters'); return; }
    try {
      await api.put('/auth/customer-change-password', { new_password: passwordForm.new_password });
      toast.success('Password changed');
      setPasswordForm({ new_password: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openProfile = async () => {
    try {
      const { data } = await api.get('/auth/customer-profile');
      setProfileForm({ first_name: data.first_name || '', last_name: data.last_name || '', email: data.email || '', phone: data.phone || '', postcode: data.postcode || '' });
      setProfileEditMode(false);  // open in read-only view
      setShowProfile(true);
    } catch (err) { console.error('[CustomerPage] error:', err); }
  };

  const fetchSpecials = async () => {
    setSpecialsLoading(true);
    try {
      const { data } = await api.get('/menu/specials');
      setSpecials(Array.isArray(data) ? data : []);
    } catch {
      setSpecials([]);
    } finally {
      setSpecialsLoading(false);
    }
  };

  const openNavView = async (view) => {
    setNavOpen(false);
    if (view === 'dashboard') {
      setActiveNavView('dashboard');
      fetchSpecials();
    } else if (view === 'profile') {
      await openProfile();
    } else if (view === 'membership') {
      setActiveNavView('membership');
    }
  };

  const openCustomize = (item) => {
    if (item.category === 'food' && !kitchenOpen && !staffOrderMode) {
      toast.error('The kitchen is closed — food is available again from 5:00pm');
      return;
    }
    setCustomizeItem(item);
    setSelectedSpice('Medium');
    setSelectedAddOns([]);
    setGroupSelections({});
    // Default head to the first option (typically Standard Clay $0)
    setSelectedHead(item.head_options && item.head_options.length > 0 ? item.head_options[0] : null);
    // Carry the variant the customer chose on the card INTO the dialog.
    // Bug fix Feb 2026: spirits like Macallan 12 also carry addon_groups
    // (Serving Style / Ice Preference / Garnish), so picking "Double" or
    // "Bottle" on the card was routing through customize and silently
    // dropping the variant — every line landed at the Single base price.
    // We stash the chosen variant here so the dialog's Base price + Total
    // + final cart line use the variant price.
    const variants = Array.isArray(item.variants) ? item.variants : [];
    if (variants.length > 0) {
      const chosenLabel = selectedVariants[item.id] || variants[0].label;
      const chosen = variants.find(v => v.label === chosenLabel) || variants[0];
      setCustomizeVariant(chosen);
    } else {
      setCustomizeVariant(null);
    }
  };

  // Flatten the current `groupSelections` into the flat
  // `{name, price, group_id}` shape that backend + cart already understand.
  const flattenGroupSelections = (item) => {
    const groups = item?.addon_groups || [];
    const out = [];
    groups.forEach(g => {
      const sel = groupSelections[g.id];
      if (g.select_mode === 'single') {
        if (typeof sel === 'string' && sel) {
          const opt = (g.options || []).find(o => o.name === sel);
          if (opt) out.push({ name: opt.name, price: Number(opt.price || 0), group_id: g.id });
        }
      } else {
        const arr = Array.isArray(sel) ? sel : [];
        arr.forEach(name => {
          const opt = (g.options || []).find(o => o.name === name);
          if (opt) out.push({ name: opt.name, price: Number(opt.price || 0), group_id: g.id });
        });
      }
    });
    return out;
  };

  const addToCartWithOptions = () => {
    const item = customizeItem;
    const groupAddOns = flattenGroupSelections(item);
    const allAddOns = [...selectedAddOns, ...groupAddOns];
    const addOnTotal = allAddOns.reduce((s, a) => s + Number(a.price || 0), 0);
    const headPrice = selectedHead ? Number(selectedHead.price || 0) : 0;
    const headLabel = selectedHead ? selectedHead.label : null;
    // Bug fix Feb 2026: items with variants (e.g. spirits with Single/Double/Bottle)
    // that ALSO have addon_groups route through this dialog. Use the variant
    // price as the base — otherwise every line lands at the Single base price
    // even when the customer picked Double / Bottle on the card.
    const variantPrice = customizeVariant ? Number(customizeVariant.price) : Number(item.price);
    const variantLabel = customizeVariant ? customizeVariant.label : null;
    const nameWithVariant = variantLabel ? `${item.name} (${variantLabel})` : item.name;
    const displayName = headLabel ? `${nameWithVariant} + ${headLabel}` : nameWithVariant;
    const cartItem = {
      ...item,
      name: displayName,
      qty: 1,
      selectedSpice: (item.has_spice_level || item.category === 'food') ? selectedSpice : null,
      selectedAddOns: allAddOns,
      addOnTotal,
      unitPrice: variantPrice + addOnTotal + headPrice,
      headLabel,
      variantLabel,
    };
    setCart(prev => [...prev, { ...cartItem, cartId: Date.now().toString() }]);
    setCustomizeItem(null);
    checkUpsell(item);
  };

  /**
   * Drop a custom-mix shisha into the cart. The dialog hands us the resolved
   * displayName + unitPrice + mix_flavours array, so this is just plumbing.
   */
  const addMixToCart = ({ mix_flavours, head_label, head_price, addOns, displayName, unitPrice }) => {
    if (!mixItem) return;
    const cartItem = {
      ...mixItem,
      name: displayName,
      qty: 1,
      selectedSpice: null,
      selectedAddOns: [...addOns],
      addOnTotal: addOns.reduce((s, a) => s + Number(a.price || 0), 0),
      unitPrice,
      headLabel: head_label,
      headPrice: head_price,
      mixFlavours: mix_flavours,
    };
    setCart(prev => [...prev, { ...cartItem, cartId: Date.now().toString() }]);
    setMixItem(null);
    checkUpsell(mixItem);
  };

  /**
   * One-tap re-order from "My Usuals". Resolves the saved combo against
   * the current menu (so price/availability is fresh) and drops a cart
   * line with the same variant + add_ons. Skips the customize dialog.
   */
  const addUsualToCart = (usual) => {
    if (!usual?.item_id) return;
    const menuItem = menu.find(m => m.id === usual.item_id);
    if (!menuItem || menuItem.is_available === false) {
      toast.error('That drink is currently unavailable');
      return;
    }
    if (menuItem.category === 'food' && !kitchenOpen && !staffOrderMode) {
      toast.error('The kitchen is closed — food is available again from 5:00pm');
      return;
    }
    const addOns = Array.isArray(usual.add_ons) ? usual.add_ons.map(a => ({
      name: a.name, price: Number(a.price || 0), group_id: a.group_id || null,
    })) : [];
    const variant = usual.variant_label
      ? (menuItem.variants || []).find(v => v.label === usual.variant_label)
      : null;
    const unitPrice = (variant ? Number(variant.price) : Number(menuItem.price))
      + addOns.reduce((s, a) => s + Number(a.price || 0), 0);
    const displayName = variant ? `${menuItem.name} (${variant.label})` : menuItem.name;
    setCart(prev => [...prev, {
      ...menuItem,
      name: displayName,
      qty: 1,
      cartId: Date.now().toString(),
      variantLabel: usual.variant_label || null,
      unitPrice,
      selectedAddOns: addOns,
      addOnTotal: addOns.reduce((s, a) => s + Number(a.price || 0), 0),
      headLabel: null,
      selectedSpice: null,
    }]);
    toast.success(`Added your usual · ${displayName}`);
  };


  /**
   * Manager-only: drop an ad-hoc "Custom Product" line into the cart with
   * a custom name/price/qty/notes. Backend enforces the role + validates.
   */
  const addCustomProductToCart = () => {
    const name = (customProductDraft.name || '').trim();
    const price = parseFloat(customProductDraft.price);
    const qty = parseInt(customProductDraft.qty, 10) || 1;
    const notes = (customProductDraft.notes || '').trim();
    if (!name) { toast.error('Enter a product name'); return; }
    if (!(price > 0)) { toast.error('Enter a price greater than $0'); return; }
    if (qty < 1) { toast.error('Quantity must be at least 1'); return; }
    setCart(prev => [...prev, {
      id: 'custom-product-charge',
      cartId: Date.now().toString(),
      name: `Custom · ${name}`,
      qty,
      unitPrice: price,
      isCustomCharge: true,
      customName: name,
      customPrice: price,
      customNotes: notes,
      category: 'manual_charge',
    }]);
    setCustomProductDraft({ name: '', price: '', qty: 1, notes: '' });
    setCustomProductOpen(false);
    toast.success('Custom product added to cart');
  };

  const quickAdd = (item) => {
    // Manager-only "Custom Product" line — opens the ad-hoc charge dialog
    // instead of adding directly. The dialog lets the manager set name +
    // price + qty + notes before it lands in the cart.
    if (item.is_custom_charge) {
      setCustomProductDraft({ name: '', price: '', qty: 1, notes: '' });
      setCustomProductOpen(true);
      return;
    }
    // Custom mix shisha gets its own bespoke dialog — flavour slots + sliders
    // + the same upsell options the standard shisha has.
    if (item.is_custom_mix) {
      setMixItem(item);
      return;
    }
    // Kitchen closed 12am→5pm — no food orders (staff order mode exempt).
    if (item.category === 'food' && !kitchenOpen && !staffOrderMode) {
      toast.error('The kitchen is closed — food is available again from 5:00pm');
      return;
    }
    // Items with mandatory choices (add-ons, or head selector) must go through customize.
    // Spice level is now selected inline on the card itself, so it no longer forces the dialog.
    // Shisha specifically: ALWAYS route through the customize dialog so the
    // customer is shown ice / fresh fruit / extra coal upsells before
    // committing — even on heads that don't define a custom add-on list,
    // because the backend always seeds a global default set of add-ons.
    const hasHeadOptions = Array.isArray(item.head_options) && item.head_options.length > 0;
    const hasAddonGroups = Array.isArray(item.addon_groups) && item.addon_groups.length > 0;
    if (item.category === 'shisha' || hasHeadOptions || hasAddonGroups || (item.add_ons && item.add_ons.length > 0)) {
      openCustomize(item);
      return;
    }
    // Variant-aware add (e.g. Glass / Bottle for wines)
    const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
    const chosenLabel = hasVariants ? (selectedVariants[item.id] || item.variants[0].label) : null;
    const chosenVariant = hasVariants ? item.variants.find(v => v.label === chosenLabel) : null;
    const unitPrice = chosenVariant ? Number(chosenVariant.price) : item.price;
    const displayName = chosenVariant ? `${item.name} (${chosenVariant.label})` : item.name;
    const chosenSpice = item.category === 'food' ? (selectedSpices[item.id] || 'Medium') : null;
    const chosenInstructions = item.category === 'food' ? ((instructionsDraft[item.id] || '').trim() || null) : null;

    setCart(prev => {
      const existing = prev.find(c =>
        c.id === item.id &&
        !c.selectedAddOns?.length &&
        (c.variantLabel || null) === (chosenLabel || null) &&
        (c.selectedSpice || null) === (chosenSpice || null) &&
        (c.instructions || null) === (chosenInstructions || null)
      );
      if (existing) return prev.map(c => c.cartId === existing.cartId ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, {
        ...item,
        name: displayName,
        qty: 1,
        cartId: Date.now().toString(),
        unitPrice,
        addOnTotal: 0,
        selectedAddOns: [],
        selectedSpice: chosenSpice,
        instructions: chosenInstructions,
        variantLabel: chosenLabel,
      }];
    });
    checkUpsell(item);
  };

  const upsellShownRef = useRef(false);
  // Measure the real height of the fixed header so the body padding-top and
  // sticky section headings stay in sync no matter how the header grows (e.g.
  // subcategory dropdown adds a row, narrow phones wrap the greeting line,
  // ActiveOrderBanner appears after data loads). Without this, the category
  // bar visually overlaps menu headings.
  const headerRef = useRef(null);
  // Staff-order mode renders a fixed gold banner above the header — bake its
  // height into headerH so all sticky bars below the header drop by the
  // banner's height automatically.
  const STAFF_BANNER_H = 36;
  const bannerOffset = staffOrderMode ? STAFF_BANNER_H : 0;
  const [headerH, setHeaderH] = useState(180);
  // Measured height of the closing-time announcement banner (last_call /
  // closing). We add this to the wrapper's paddingTop so the menu content
  // sits below the (taller, multi-line) banner instead of being covered by it.
  const announcementRef = useRef(null);
  const [announcementH, setAnnouncementH] = useState(0);
  const reMeasureHeader = useCallback(() => {
    if (headerRef.current) {
      // +8px buffer so any sub-pixel rounding or margin-collapse quirk on
      // Safari iOS doesn't reveal menu items behind the header.
      setHeaderH(headerRef.current.offsetHeight + 8 + bannerOffset);
    }
  }, [bannerOffset]);
  useEffect(() => {
    if (!headerRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const el = headerRef.current;
    const ro = new ResizeObserver(reMeasureHeader);
    ro.observe(el);
    reMeasureHeader();
    // Also re-measure on window resize (orientation change) and on the next
    // microtask in case the banner mounts a frame later than ResizeObserver.
    const onWin = () => reMeasureHeader();
    window.addEventListener('resize', onWin);
    window.addEventListener('orientationchange', onWin);
    const t = setTimeout(reMeasureHeader, 50);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onWin);
      window.removeEventListener('orientationchange', onWin);
      clearTimeout(t);
    };
  }, [reMeasureHeader]);
  // ResizeObserver doesn't always fire when the subcategory dropdown inside
  // the header mounts/unmounts on category change (Safari quirk). Force a
  // re-measure whenever the active category or active sub flips so the
  // sticky section headers below the fixed header pick up the new height.
  useEffect(() => {
    const t1 = setTimeout(reMeasureHeader, 0);
    const t2 = setTimeout(reMeasureHeader, 80);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [activeCategory, activeSub, reMeasureHeader]);

  // Track the height of the closing announcement banner so menu content
  // shifts down by exactly that amount (banner is `fixed`, so it doesn't
  // push content automatically). Uses a callback ref so we measure the
  // moment the DOM node attaches/detaches.
  const isAnnouncementVisible = !!(announcement && !dismissedAnnouncementIds.has(announcement.id));
  const announcementRoRef = useRef(null);
  const announcementSetRef = useCallback((node) => {
    if (announcementRoRef.current) {
      try { announcementRoRef.current.disconnect(); } catch { /* noop */ }
      announcementRoRef.current = null;
    }
    announcementRef.current = node;
    if (!node) { setAnnouncementH(0); return; }
    const measure = () => setAnnouncementH(node.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      announcementRoRef.current = ro;
    }
    // Re-measure once layout settles (font load, image decode, etc.).
    setTimeout(measure, 120);
  }, []);
  useEffect(() => {
    if (!isAnnouncementVisible) setAnnouncementH(0);
  }, [isAnnouncementVisible]);
  const checkUpsell = (_item) => {
    // Upsell prompts disabled per owner directive (Feb 2026): no
    // "Upgrade to Premium…" / "Add a drink…" toasts during customer
    // ordering. Left as a no-op so the call sites elsewhere keep working
    // and we can re-enable later by restoring the body.
    return;
  };

  const updateQty = (cartId, delta) => {
    setCart(prev => prev.map(c => c.cartId === cartId ? { ...c, qty: Math.max(0, c.qty + delta) } : c).filter(c => c.qty > 0));
  };

  const quickRemove = (item) => {
    // Decrement the most recently added cart line for this menu item.
    setCart(prev => {
      const matches = prev.filter(c => c.id === item.id);
      if (matches.length === 0) return prev;
      const target = matches[matches.length - 1];
      return prev
        .map(c => c.cartId === target.cartId ? { ...c, qty: c.qty - 1 } : c)
        .filter(c => c.qty > 0);
    });
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.unitPrice * c.qty, 0);
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  // Cash promo: if the customer has picked "Pay Cash At Bar" we surface
  // the 10% discount preview in the cart so they can see what they'll
  // actually owe. The backend re-computes the final total when staff
  // applies the Cash 10% Off pill on Live Orders — this is a display-only
  // estimate. Disabled on takeaway (card-only) and for the Tab flow
  // (the discount there is bound to settlement, not per-order).
  const cashDiscountEligible = !isTakeawayMode && paymentMethod === 'cash' && cartTotal > 0;
  const cashDiscountAmount = cashDiscountEligible ? Number((cartTotal * 0.10).toFixed(2)) : 0;

  // Tipping disabled on the customer flow. `tipAmount` is declared once at
  // component top so the cart/order payloads stay backwards-compatible.
  const grandTotal = cartTotal + tipAmount - cashDiscountAmount;

  // Live tab running-total — sums the customer's visible placed orders.
  // The server's `tab.current_total` is the source of truth long-term, but
  // it doesn't refresh until the next polling tick, which leaves the
  // customer staring at "$0.00 so far" right after placing their first
  // order. Computing the floor client-side from `activeMyOrders` gives
  // immediate feedback; we still take MAX(server, client) so once the
  // server catches up we surface whichever is higher.
  const placedOrdersTotal = activeMyOrders
    .filter(o => o.payment_status !== 'paid' && o.payment_status !== 'refunded')
    .reduce((sum, o) => sum + Number(o.total || 0), 0);
  const tabRunningTotal = Math.max(Number(tab?.current_total || 0), placedOrdersTotal);
  // Outstanding tab value — the actual amount the customer still owes
  // (running total minus everything they've already paid: deposit,
  // advance, top-ups). This is what the cart surfaces front-and-centre
  // per owner ask Feb 2026: "the tab value so far is not showing up
  // to the customer it should be shown very clearly in the cart".
  const tabPaidAmount = Number(tab?.paid_amount || 0);
  const tabOutstanding = Math.max(0, tabRunningTotal - tabPaidAmount);

  const placeOrder = async (opts = {}) => {
    if (cart.length === 0) return;
    // Allow callers (e.g. submitGuestName) to pass the just-typed guest name
    // without depending on the React state update having flushed.
    const effectiveGuestName = (opts.overrideGuestName || guestName || '').trim();

    // ----- RESOLVE CANONICAL GUEST IDENTITY UP-FRONT -----
    // For non-member flows (walk-in guest OR staff-takes-order-on-behalf),
    // hit /guest-identity/resolve ONCE so we have a single canonical
    // guest_id that both `/payments/open-tab` and `/orders` use. Without
    // this, the open-tab call uses the (possibly stale) React-state
    // `guestId` while the order POST uses a server-resolved id — and a
    // mismatch shows up as `400 "No active tab"` despite open-tab having
    // returned 200. (Owner-reported bug May 30: tab created with id A,
    // order looking for id B → "Could not reopen your tab".)
    //
    // RETRY OVERRIDE: the self-heal path below re-invokes placeOrder with
    // opts._tabAutoHealedGid set to the guest_id of the FRESHLY opened
    // tab. We MUST honour that without re-resolving — otherwise a
    // resolver tie-breaker change between the two calls reintroduces the
    // exact bug we're trying to heal (see IMG_2611 May 31 2026 report).
    // `staffOrderMode` (bar-panel iframe) is always a walk-in flow: the
    // bartender is logged in but the TAB belongs to the walk-in customer
    // at the table, NOT the bartender. Force the guest-identity path so
    // every staff-driven tab is keyed by guest_id+table — this is what
    // prevents the bartender's customer_id from collecting every staff
    // tab onto a single doc (IMG_2715 root cause).
    let canonicalGid = (!user || staffOrderMode) ? guestId : null;
    if (opts._tabAutoHealedGid) {
      canonicalGid = opts._tabAutoHealedGid;
    } else if ((!isCustomer || staffOrderMode) && table?.id && effectiveGuestName) {
      try {
        const resolved = await resolveGuestIdentity(effectiveGuestName);
        if (resolved) canonicalGid = resolved;
      } catch { /* fall back to closure guestId */ }
    }

    // ----- AUTO-OPEN TAB IF NEEDED -----
    // Customer picked "Tab" but hasn't opened one yet → silently open it here
    // before the order POST. Feb 2026: NO deposit is taken — the tab opens
    // with `deposit_method: cash` (default) which just flags the tab as
    // eligible for the 10%-off-cash-on-settle promo. Customer settles the
    // FULL tab at the end of their visit, no upfront money moves.
    //
    // Skip when called as the self-heal retry — the parent already opened
    // a fresh tab and we don't want to open another one (would create a
    // race between the parent's setTab and this call's open-tab response).
    //
    // ALSO skip when we're a walk-in guest with no name yet — the dine-in
    // branch below will pop the guest-name dialog and re-invoke placeOrder
    // with `overrideGuestName` set. Opening the tab here with an empty
    // `customer_name` was the Feb 2026 "Guest" ghost-tab bug: the very
    // first click opened a nameless tab, then the second click (after the
    // dialog) saw `!tab === false` and silently skipped this block,
    // leaving the tab card permanently blank on the till.
    const guestNeedsNameDialog =
      !isCustomer && !isTakeawayMode && !effectiveGuestName;
    if (
      !isTakeawayMode &&
      paymentMethod === 'tab' &&
      !tab &&
      !opts._tabAutoHealed &&
      !guestNeedsNameDialog
    ) {
      try {
        const payload = { origin_url: window.location.origin, deposit_method: tabDepositMethod };
        // In staff-bar-panel mode we always send guest_id + customer_name
        // so the backend creates a walk-in tab (customer_id=null) instead
        // of a tab keyed to the bartender. Without this every staff-driven
        // tab collides on the bartender's customer_id (IMG_2715).
        if ((!isCustomer || staffOrderMode) && canonicalGid) payload.guest_id = canonicalGid;
        // Owner ask Feb 2026: the name the customer types after "Place
        // Order & Start Tab" MUST show on the till's tab card.
        // Previously the guard was `(!isCustomer || staffOrderMode)`
        // which silently dropped the name for the most common case
        // (walk-in / first-time customer on a guest device) when other
        // logic flipped them into a member-ish state. We now send the
        // name whenever we have one; backend already disambiguates
        // member vs guest by `is_guest`.
        if (effectiveGuestName) payload.customer_name = effectiveGuestName;
        if (table?.id) payload.table_id = table.id;
        const { data } = await api.post('/payments/open-tab', payload);
        setTab(data);
        toast.success('Tab opened');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to open tab');
        return;
      }
    }
    // ----- TAKEAWAY MODE -----
    if (isTakeawayMode) {
      // State updates from the modal are async, so fall back to localStorage which we wrote
      // synchronously inside the dialog submit.
      let lsPhone = '';
      let lsName = '';
      try { lsPhone = localStorage.getItem('majnu:takeawayPhone') || ''; } catch { /* */ }
      try { lsName = localStorage.getItem('majnu:guestName:default') || ''; } catch { /* */ }
      const name = (takeawayDraft.name || effectiveGuestName || lsName || user?.name || '').trim();
      const phone = (takeawayDraft.phone || takeawayPhone || lsPhone || user?.phone || '').trim();
      if (!name || !phone) {
        // Open the modal that captures both — pre-fill with whatever we have.
        setTakeawayDraft({ name: name || effectiveGuestName || lsName || '', phone: phone || takeawayPhone || lsPhone || '' });
        setTakeawayInfoOpen(true);
        return;
      }
      setOrderLoading(true);
      try {
        const payload = {
          customer_name: name,
          customer_phone: phone,
          customer_email: user?.email || null,
          pickup_time: pickupTime,
          origin_url: window.location.origin,
          tip_amount: tipAmount || 0,
          items: cart.map(c => ({
            item_id: c.id,
            quantity: c.qty,
            variant_label: c.variantLabel || null,
            head_label: c.headLabel || null,
            spice_level: c.selectedSpice || null,
            instructions: c.instructions || null,
            mix_flavours: c.mixFlavours || null,
            add_ons: (c.selectedAddOns || []).map(a => ({ name: a.name, price: Number(a.price || 0), group_id: a.group_id || null })),
          })),
        };
        const { data } = await api.post('/orders/takeaway', payload);
        // Persist phone for next time
        try { localStorage.setItem('majnu:takeawayPhone', phone); } catch { /* */ }
        try { localStorage.setItem(`majnu:guestName:default`, name); } catch { /* */ }
        setCart([]); setCartOpen(false);
        if (data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.success('Order placed!');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to place takeaway order');
      } finally {
        setOrderLoading(false);
      }
      return;
    }

    // ----- DINE-IN MODE -----
    // Guest path — must have a name. If missing, open the guest checkout dialog instead of placing.
    if (!isCustomer) {
      if (!effectiveGuestName) {
        setGuestNameDraft('');
        setGuestCheckoutOpen(true);
        return;
      }
    }
    setOrderLoading(true);
    try {
      const payload = {
        table_id: table.id,
        items: cart.map(c => ({
          item_id: c.id,
          quantity: c.qty,
          variant_label: c.variantLabel || null,
          head_label: c.headLabel || null,
          spice_level: c.selectedSpice || null,
          instructions: c.instructions || null,
          mix_flavours: c.mixFlavours || null,
          add_ons: (c.selectedAddOns || []).map(a => ({ name: a.name, price: Number(a.price || 0), group_id: a.group_id || null })),
          // Manager-only "Custom Product" ad-hoc charge — backend overrides
          // name/price ONLY when the menu item is flagged is_custom_charge
          // AND the caller is manager/director (403 otherwise).
          custom_name: c.isCustomCharge ? c.customName : null,
          custom_price: c.isCustomCharge ? c.customPrice : null,
          custom_notes: c.isCustomCharge ? (c.customNotes || null) : null,
        })),
        payment_method: paymentMethod,
      };
      if (!isCustomer || staffOrderMode) {
        payload.customer_name = effectiveGuestName;
        // Staff order mode (bar-panel iframe): always send guest_id so
        // the backend keyed the tab to the walk-in customer at the
        // table, not the bartender. Mirrors the open-tab call above.
        if (canonicalGid) payload.guest_id = canonicalGid;
      }
      // Send the tab_id directly when we know it. The backend treats this
      // as the most-specific lookup hint and validates ownership server-
      // side. Eliminates the guest_id / customer_id / name-dedup desync
      // class of "No active tab" bugs (owner-reported May 31 2026 — the
      // IMG_2709 "Could not reopen your tab" screenshot was this exact
      // class of failure).
      // Self-heal retry: prefer the freshly-reopened tab id over the
      // stale `tab` state which may not have settled yet via setTab().
      if (paymentMethod === 'tab' && (opts._tabAutoHealedTabId || tab?.id)) {
        payload.tab_id = opts._tabAutoHealedTabId || tab.id;
      }
      const { data: order } = await api.post('/orders', payload);
      // EFTPOS = manager-comes-to-you. Skip Square checkout entirely;
      // staff will run the order on the physical EFTPOS terminal at the
      // table. The customer sees a blocking modal that clears once
      // staff hits "Paid (Card)" on the Orders page.
      if (paymentMethod === 'card') {
        setCart([]); setCartOpen(false);
        setMyOrders(prev => [order, ...prev.filter(o => o.id !== order.id)]);
        fetchOrders();
        setEftposAlert({
          orderId: order.id,
          name: (isCustomer ? user?.name : effectiveGuestName) || '',
          total: order.total,
        });
        return;
      }
      // Apple Pay (and any other Square-routed digital wallet) → kick the
      // customer over to Square's hosted checkout. Square automatically
      // renders the Apple Pay button on iOS Safari (and falls back to a
      // card form on other browsers), so we get Apple Pay UX for free
      // without needing a separate Apple Merchant ID / cert.
      if (paymentMethod === 'apple_pay') {
        const { data: checkout } = await api.post('/payments/create-checkout', {
          order_id: order.id,
          origin_url: window.location.origin,
          tip_amount: 0,
        });
        setCart([]); setCartOpen(false);
        window.location.href = checkout.url; return;
      }
      setCart([]); setCartOpen(false);
      // Optimistically add the freshly-placed order to myOrders so the cart
      // drawer / banner reflects it INSTANTLY — without this, the customer
      // sees "Your cart is empty" for the ~1s until the next fetchOrders
      // poll returns. Server truth reconciles on the next polling tick.
      setMyOrders(prev => [order, ...prev.filter(o => o.id !== order.id)]);
      // Refresh orders list (works for both members and guests with the new fetchOrders)
      fetchOrders();
      // Refresh "My Usuals" — the new order may push a combo over the
      // 3-order threshold so the chip appears immediately on next render.
      fetchUsuals();
      // Big modal alert for CASH orders — replaces the tiny toast that
      // customers were missing. They need a clear "go to the counter" prompt.
      if (paymentMethod === 'cash') {
        setCashCounterAlert({
          orderId: order.id,
          name: (isCustomer ? user?.name : effectiveGuestName) || '',
          total: order.total,
        });
      } else if (isCustomer) {
        toast.success('Order placed on your tab!');
      } else {
        // Guests: friendlier toast that confirms we saved their name
        toast.success(`Thanks ${effectiveGuestName.split(' ')[0]}! Your order is on its way.`, {
          description: 'We saved your name on this device — no typing next time.',
        });
      }
      // Owner directive: no auto-popup for membership during/after an order
      // — customers should not be interrupted while ordering. The "Become
      // Member" CTA now lives as a visible button in the header (see render)
      // so guests can opt-in on their own terms.
      if (!isCustomer) {
        setLastOrderId(order.id);
      }
    } catch (err) {
      // Tab top-up gate: backend returns 402 with a structured payload when
      // the customer's UNPAID balance + this order would push them over
      // the $250 rolling limit. Surface a prompt instead of a flat toast.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail && typeof detail === 'object' && detail.code === 'tab_topup_required') {
        setTabTopupPrompt(detail);
        toast.message('Tab balance reached limit — please top up to continue.', { duration: 4500 });
      } else if (err?.response?.status === 402 && detail && typeof detail === 'object' && detail.code === 'table_topup_required') {
        // Per-table $1000 unpaid ceiling (Feb 2026 owner ask). A friend
        // on the same table racked up the unpaid balance — we can't
        // top THEIR tab for them, so the right action is to flag a
        // staff member. Surface a clean toast (no modal — this isn't
        // a self-serve fix from the customer's side).
        toast.error(
          `Table at $${Number(detail.table_unpaid_limit || 1000).toFixed(0)} unpaid ceiling — please call staff to settle a tab before ordering more.`,
          { duration: 7500 }
        );
      } else if (
        err?.response?.status === 400 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('no active tab') &&
        paymentMethod === 'tab'
      ) {
        // Self-heal stale `tab` state: the customer's local UI still shows
        // "Tab Active" but the server lost the tab (manager force-cleared
        // the table, customer cancelled the last order on the tab, or the
        // tab was settled out-of-band). Re-open the tab transparently using
        // the deposit method they picked, then retry the order ONCE. If
        // the retry also fails we drop into the normal error toast — no
        // infinite loop.
        if (opts._tabAutoHealed) {
          toast.error('Could not reopen your tab — please pick a different payment method or ask the bartender.', { duration: 5000 });
          setTab(null);
          fetchTab();
          return;
        }
        setTab(null);
        toast.message('Reopening your tab…', { duration: 2000 });
        try {
          const reopenPayload = { origin_url: window.location.origin, deposit_method: tabDepositMethod };
          // Use the SAME canonical guest_id the failed order POST used, so
          // the freshly-opened tab is keyed identically to what /orders
          // will look for on the retry. `staffOrderMode` forces guest_id
          // even when the bartender is logged in — otherwise the reopen
          // collides on the bartender's customer_id (IMG_2715 root cause).
          if ((!isCustomer || staffOrderMode) && canonicalGid) reopenPayload.guest_id = canonicalGid;
          if (table?.id) reopenPayload.table_id = table.id;
          // Same fix as the primary open-tab call above — always send
          // the customer's typed name so the till tab card never goes
          // blank after an auto-heal cycle.
          if (effectiveGuestName) reopenPayload.customer_name = effectiveGuestName;
          const { data: reopened } = await api.post('/payments/open-tab', reopenPayload);
          setTab(reopened);
          // Retry once with the auto-heal flag + the reopened tab's
          // guest_id locked in so the recursive placeOrder uses the
          // EXACT same canonicalGid as the open-tab call we just made.
          // Without this, the retry's resolveGuestIdentity could return
          // a different id (especially right after a Day-Close
          // auto-forfeit when both the old + new tab match (table, name))
          // and we'd loop straight back to "no active tab" → dead-end
          // toast (the IMG_2611 May 31 regression).
          // ALSO pass `_tabAutoHealedTabId` so the retry's order POST
          // hits the new deterministic `tab_id` lookup path on the
          // backend — bypasses every guest_id mismatch path.
          await placeOrder({
            ...opts,
            _tabAutoHealed: true,
            _tabAutoHealedGid: reopened?.guest_id || canonicalGid,
            _tabAutoHealedTabId: reopened?.id || null,
          });
          return;
        } catch (reopenErr) {
          toast.error(reopenErr?.response?.data?.detail || 'Could not reopen your tab. Please try a different payment method.');
          fetchTab();
        }
      } else {
        toast.error((typeof detail === 'string' ? detail : null) || 'Failed to place order');
      }
    }
    finally { setOrderLoading(false); }
  };

  // --- Tab deposit / top-up handlers ---------------------------------------
  const payTabDepositByCard = async () => {
    if (!tab?.id) return;
    setTabDepositLoading(true);
    try {
      const { data } = await api.post(`/payments/tab-deposit-checkout/${tab.id}`, {
        origin_url: window.location.origin,
        guest_id: guestId,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not start card payment');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start card payment');
    } finally {
      setTabDepositLoading(false);
    }
  };
  const payTabTopupByCard = async (amount) => {
    if (!tabTopupPrompt?.tab_id) return;
    setTabTopupLoading(true);
    try {
      const { data } = await api.post(`/payments/tab-topup-checkout/${tabTopupPrompt.tab_id}`, {
        origin_url: window.location.origin,
        amount: amount || tabTopupPrompt.topup_minimum || 200,
        guest_id: guestId,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not start top-up payment');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start top-up payment');
    } finally {
      setTabTopupLoading(false);
    }
  };
  const requestStaffTabPayment = async (kind) => {
    // Customer flags the bartender that they're ready to pay cash/EFTPOS
    // for their deposit or top-up. Reuses the existing service-request
    // pipeline so the bartender's bell tray lights up.
    if (!table?.id) return;
    try {
      await api.post('/service-requests', {
        table_id: table.id,
        request_type: 'payment',
        guest_id: guestId,
        customer_name: (user?.name || guestName || '').trim() || undefined,
        note: kind === 'topup'
          ? `Tab top-up needed (min $${tabTopupPrompt?.topup_minimum || 200}) — collect cash/EFTPOS`
          : `Tab deposit needed ($${30}) — collect cash/EFTPOS`,
      });
      toast.success('Bartender notified — they will be with you shortly.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not notify staff');
    }
  };

  const submitGuestName = () => {
    const name = guestNameDraft.trim();
    if (!name) { toast.error('Please enter your name'); return; }
    try { localStorage.setItem(`majnu:guestName:${qrCode || 'default'}`, name); } catch { /* */ }
    setGuestName(name);
    setGuestCheckoutOpen(false);
    // Pass the name explicitly — don't rely on React state having flushed.
    placeOrder({ overrideGuestName: name });
  };

  const submitBecomeMember = async (e) => {
    e?.preventDefault?.();
    if (memberSubmitting) return;
    if (memberForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setMemberSubmitting(true);
    try {
      const { data } = await api.post('/auth/become-member', {
        table_id: table.id,
        first_name: memberForm.first_name.trim(),
        last_name: memberForm.last_name.trim(),
        email: memberForm.email.trim().toLowerCase(),
        phone: memberForm.phone.trim(),
        password: memberForm.password,
        last_order_id: lastOrderId,
        guest_id: guestId,
      });
      toast.success(data.message || 'Welcome — you are now a Majnu member.');
      // Clear guest mode and refresh page so /api/auth/me returns the new member
      try {
        localStorage.removeItem(`majnu:guest:${qrCode || 'default'}`);
        localStorage.removeItem(`majnu:guestName:${qrCode || 'default'}`);
      } catch (err) { console.error('[CustomerPage] error:', err); }
      setMemberDialogOpen(false);
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign up failed');
    } finally {
      setMemberSubmitting(false);
    }
  };

  const openTab = async (depositOverride) => {
    if (tabOpening || tab) return;
    setTabOpening(true);
    try {
      // Members are authenticated via cookie; walk-in guests pass their
      // per-device guest_id so the backend can key the tab to that device.
      // Also send the customer's display name so the backend can dedup
      // by (table_id, name) — catches the same human at the same table
      // even if their guest_id changed (cleared localStorage, incognito,
      // borrowed phone, etc).
      const chosenMethod = depositOverride || tabDepositMethod;
      const payload = {
        origin_url: window.location.origin,
        deposit_method: chosenMethod,
      };
      // Staff names MUST NOT leak onto walk-in tab cards (Feb 2026
      // ghost-tab fix). When a till_supervisor / bartender / manager
      // is signed in and clicks Open Tab on the customer page they
      // SHOULD be acting on behalf of a guest — fall back to the
      // typed `guestName` only. The backend mirrors this guard.
      const isStaff = !!(user && user.role && user.role !== 'customer');
      const memberName = isStaff ? '' : (user?.name || '');
      const displayName = (memberName || guestName || '').trim();
      if (!user) {
        // Resolve the durable server-issued guest_id for this (table, name)
        // BEFORE opening the tab. If we already had a session in the last
        // 4h, the server returns the same id and the tab dedup logic will
        // attach to / surface the existing tab idempotently.
        const resolvedGid = displayName
          ? await resolveGuestIdentity(displayName)
          : guestId;
        if (resolvedGid) payload.guest_id = resolvedGid;
      }
      if (table?.id) payload.table_id = table.id;
      if (displayName) payload.customer_name = displayName;
      const { data } = await api.post('/payments/open-tab', payload);
      setTab(data);
      // Apple Pay path — immediately route to Square's hosted $30 deposit
      // checkout so the customer can tap to pay with Apple Pay (or a card
      // if they're not on iOS). On success the Square webhook flips
      // tab.deposit_status to "paid" so we don't need to do anything here.
      if (chosenMethod === 'apple_pay') {
        try {
          const { data: checkout } = await api.post(`/payments/tab-deposit-checkout/${data.id}`, {
            origin_url: window.location.origin,
            guest_id: guestId,
          });
          if (checkout?.checkout_url) {
            window.location.href = checkout.checkout_url;
            return;
          }
        } catch (chkErr) {
          toast.error(chkErr.response?.data?.detail || 'Failed to start Apple Pay checkout');
        }
      }
      const successMsg = chosenMethod === 'cash'
        ? 'Tab opened · pay cash at the bar to settle for 10% off'
        : 'Tab opened · settle at the bar when ready';
      toast.success(successMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open tab');
    } finally {
      setTabOpening(false);
    }
  };

  // Build the leaf-set FIRST so filteredMenu can read it without a
  // temporal-dead-zone reference error. (drinksSectionLeaves is also
  // consumed by availableSubs() below.)
  const drinksSectionLeaves = useMemo(() => {
    if (activeDrinksSection === 'all') return null; // no filter
    const sec = drinksSections.find(s => s.section === activeDrinksSection);
    return sec ? new Set(sec.subcategories) : new Set();
  }, [activeDrinksSection, drinksSections]);

  const filteredMenu = (() => {
    // Manager-only "Custom Product" line item is pinned across every
    // category view so it's always one tap away — never hidden behind a
    // category filter. Customers never see it because /api/menu strips
    // is_manager_only entries.
    const customProductItem = menu.find(i => i.is_custom_charge);
    let items = activeCategory === 'all' ? menu : menu.filter(i => i.category === activeCategory || i.is_custom_charge);
    // Drinks 2-tier section filter (June 2026): when a top-level section
    // chip is active (e.g. "Spirits"), narrow to items whose leaf
    // subcategory belongs to that section. Bypassed in 'all' mode and on
    // search so customers can still discover via the search box.
    if (activeCategory === 'drinks' && drinksSectionLeaves && !searchQuery.trim()) {
      items = items.filter(i => i.is_custom_charge || drinksSectionLeaves.has(i.subcategory));
    }
    // Apply subcategory filter for any category that has one set
    const sub = activeSub[activeCategory];
    if (sub && sub !== 'all' && (activeCategory === 'shisha' || activeCategory === 'drinks' || activeCategory === 'food' || activeCategory === 'activities')) {
      items = items.filter(i => (i.subcategory || '') === sub || i.is_custom_charge);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      items = items.filter(i =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.description || '').toLowerCase().includes(q) ||
        (i.subcategory || '').toLowerCase().includes(q) ||
        i.is_custom_charge
      );
    }
    // Re-pin Custom Product to the very top (in case sub/category filter
    // moved it).
    if (customProductItem && items.includes(customProductItem)) {
      items = [customProductItem, ...items.filter(i => i !== customProductItem)];
    }
    return items;
  })();

  // Group: in "All" mode, group by category then subcategory. In category mode, just subcategory.
  const categoryOrder = ['drinks', 'food', 'shisha', 'activities'];
  const categoryLabels = { drinks: 'Drinks', food: 'Food', shisha: 'Shisha', activities: 'Activities' };

  // Subcategory display order per category. For drinks, this is now the
  // FLAT leaf order — the 2-tier nav (top-level section chips → leaf
  // chips) is rendered above from `drinksSections`, but when the user
  // hasn't filtered to a section we still want a sensible default order
  // (Cocktails first, Specials last).
  const subcatOrder = {
    shisha: ['Standard', 'Premium', 'Celebrate'],
    drinks: [
      // Cocktails
      'Cocktails', 'Signature Cocktails', 'Premium Cocktails',
      // Spirits
      'Whisky', 'Vodka', 'Gin', 'Rum', 'Tequila', 'Brandy', 'Cognac', 'Other Spirits',
      // Beer & Wine
      'Beer', 'Cider', 'Wine', 'Sparkling',
      // Mocktails
      'Mocktails', 'Signature Mocktails',
      // Hot Drinks
      'Chai', 'Tea', 'Coffee', 'Hot Chocolate', 'Specialty Hot Drinks',
      // Soft Drinks & Shakes
      'Soft Drinks', 'Juices', 'Energy Drinks', 'Milkshakes',
      // Specials kept (owner directive June 2026)
      'Specials / Combos',
    ],
    food: ['Bar Snacks', 'Majnu Street Mix', 'Tandoor Tadka', 'Burgers', 'Chef Specials', 'Wraps'],
    activities: ['Snooker', 'Pool', 'Foosball', 'Darts', 'Board Games'],
  };
  // Inline section order for the Spirits page drill-down — preserved
  // for backwards compatibility with any caller still referencing it.
  const spiritTypeOrder = ['Whisky', 'Vodka', 'Gin', 'Rum', 'Tequila', 'Brandy', 'Cognac', 'Other Spirits'];

  const sortSubcategories = (grouped, cat) => {
    const order = subcatOrder[cat] || [];
    const sorted = {};
    order.forEach(k => { if (grouped[k]) sorted[k] = grouped[k]; });
    Object.keys(grouped).forEach(k => { if (!sorted[k]) sorted[k] = grouped[k]; });
    return sorted;
  };

  // Internal marker for items missing a subcategory — rendered without a
  // sub-header so customers never see an ugly "Other" bucket.
  const UNCAT_KEY = '__uncategorized__';

  // Custom Mix shisha is rendered as its own hero banner at the top of the
  // Shisha section. Strip it out of the regular grouped grid so it doesn't
  // appear twice (once as the hero, once as a chip).
  const customMixItem = useMemo(
    () => menu.find(i => i.category === 'shisha' && i.is_custom_mix && i.is_available !== false) || null,
    [menu]
  );

  // Drill Spirits items into their spirit_type sub-buckets — historically
  // needed when every spirit lived under subcategory="Spirits". Post the
  // June 2026 migration each spirit's leaf subcategory is already its
  // spirit_type (Whisky/Vodka/etc.), so this function is now a no-op for
  // new data but still works for any legacy item that may exist after
  // a partial migration (defensive).
  const splitSpiritGroups = (grouped, cat) => {
    if (cat !== 'drinks' || !grouped['Spirits']) return grouped;
    const spirits = grouped['Spirits'];
    const rest = { ...grouped };
    delete rest['Spirits'];
    const sub = {};
    spirits.forEach(it => {
      const k = it.spirit_type || 'Other Spirits';
      if (!sub[k]) sub[k] = [];
      sub[k].push(it);
    });
    const ordered = {};
    spiritTypeOrder.forEach(st => { if (sub[st]) ordered[st] = sub[st]; });
    Object.keys(sub).forEach(k => { if (!ordered[k]) ordered[k] = sub[k]; });
    return { ...ordered, ...rest };
  };

  const buildSections = () => {
    const stripCustomMix = (arr) => arr.filter(i => !i.is_custom_mix);
    if (activeCategory !== 'all') {
      const grouped = {};
      stripCustomMix(filteredMenu).forEach(item => { const k = item.is_custom_charge ? 'Manager · Custom Product' : (item.subcategory || UNCAT_KEY); if (!grouped[k]) grouped[k] = []; grouped[k].push(item); });
      return [{ category: activeCategory, subcategories: sortSubcategories(splitSpiritGroups(grouped, activeCategory), activeCategory) }];
    }
    // All — group by category, then subcategory. Manager-only manual_charge
    // (Custom Product) is rendered as its own pinned-to-the-top section so
    // it's the first thing managers see in Staff Order Mode.
    const sections = [];
    const customProductItem = filteredMenu.find(i => i.is_custom_charge);
    if (customProductItem) {
      sections.push({
        category: 'manual_charge',
        label: 'Manager Tools',
        subcategories: [{ name: 'Custom Product', items: [customProductItem] }],
      });
    }
    categoryOrder.forEach(cat => {
      const catItems = stripCustomMix(filteredMenu.filter(i => i.category === cat && !i.is_custom_charge));
      if (catItems.length === 0) return;
      const grouped = {};
      catItems.forEach(item => { const k = item.subcategory || UNCAT_KEY; if (!grouped[k]) grouped[k] = []; grouped[k].push(item); });
      sections.push({ category: cat, label: categoryLabels[cat], subcategories: sortSubcategories(splitSpiritGroups(grouped, cat), cat) });
    });
    return sections;
  };
  const sections = buildSections();

  // Available subcategories per category, ordered by `subcatOrder`. For
  // drinks, when a top-level section is selected we constrain the chips
  // to that section's leaves only (e.g. picking "Spirits" shows Whisky /
  // Vodka / Gin / … chips, hiding Beer / Wine / etc.). When the section
  // is 'all' we show every leaf that actually has items.
  const availableSubs = (cat) => {
    if (cat !== 'shisha' && cat !== 'drinks' && cat !== 'food' && cat !== 'activities') return [];
    const catItems = menu.filter(i => i.category === cat);
    const counts = {};
    catItems.forEach(i => { const s = i.subcategory || ''; if (s) counts[s] = (counts[s] || 0) + 1; });
    let present = new Set(Object.keys(counts));
    if (cat === 'drinks' && drinksSectionLeaves) {
      // Intersect with the active section's leaves.
      present = new Set([...present].filter(s => drinksSectionLeaves.has(s)));
    }
    const ordered = (subcatOrder[cat] || []).filter(s => present.has(s));
    Array.from(present).forEach(s => { if (!ordered.includes(s)) ordered.push(s); });
    return ordered.map(s => ({ name: s, count: counts[s] }));
  };

  const totalItemsInCategory = (cat) => menu.filter(i => i.category === cat).length;

  // Hero backdrop image per category — uses the first available image. Memoised so
  // we don't rescan the menu list on every render.
  const categoryHero = useMemo(() => {
    const pick = (cat) => {
      const m = menu.find(i => i.category === cat && i.image_path);
      return m ? getImageUrl(m.image_path) : null;
    };
    const cache = { drinks: pick('drinks'), food: pick('food'), shisha: pick('shisha'), activities: pick('activities') };
    return (cat) => cache[cat] || null;
  }, [menu]);

  // Customer-facing status labels — collapse the 4-stage internal flow
  // (received → preparing → ready → served) into just 2 visible labels so
  // customers don't see "preparing" / "ready" churn. The staff panels still
  // see the full granular flow; this mapping is purely cosmetic for the
  // customer's cart view.
  const customerStatus = (s) => (s === 'served' || s === 'completed') ? 'served' : 'received';
  const customerStatusColors = {
    received: 'bg-blue-accent/20 text-blue-accent',
    served: 'bg-emerald-500/20 text-emerald-400',
  };

  // Atmospheric category-themed backdrops generated via Gemini Nano Banana
  // for the inside menu pages. Fetched once on mount; falls back to the
  // first menu-item image per category if backdrops haven't been generated
  // yet. Lives here (above early returns) so hooks-rules stays clean.
  const [categoryBackdrops, setCategoryBackdrops] = useState({});
  // Per-category "Select Category" dropdown open/close state. Default
  // closed on every category swap so each page reads clean.
  const [subPickerOpen, setSubPickerOpen] = useState(false);
  useEffect(() => { setSubPickerOpen(false); }, [activeCategory]);

  // Persist tab + drinks-section choice for next QR scan (skipped in staff
  // iframe mode so bartenders always start from "All"). Best-effort —
  // localStorage can throw in private mode / sandboxed iframes.
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);
      if (params.get('staff') === '1') return;
      window.localStorage.setItem('majnu:customer:lastTab', activeCategory);
    } catch { /* swallow */ }
  }, [activeCategory]);
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);
      if (params.get('staff') === '1') return;
      window.localStorage.setItem('majnu:customer:lastDrinksSection', activeDrinksSection);
    } catch { /* swallow */ }
  }, [activeDrinksSection]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/category-backdrops`);
        if (!cancelled) setCategoryBackdrops(res.data?.backdrops || {});
      } catch (e) {
        void e;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- LOADING / ERROR / LOGIN SCREENS ---
  // Hoist the cash-counter blocker as a top-level overlay so it renders on
  // every code path below — including the welcome screen, the table-error
  // screen, and the splash. Customers must NOT be able to escape it by
  // hitting back / refresh / navigating elsewhere on the same origin.
  const cashCounterOverlay = (
    <>
      <CashCounterModal
        alert={cashCounterAlert}
        onRingBell={table?.id ? (() => callService('payment')) : null}
        onEdit={(orderId) => {
          // Find the actual order in `myOrders` so OrderEditDialog gets
          // the full payload (items, totals etc). Falls back to a stub
          // shape if the order hasn't been fetched yet.
          const found = myOrders.find(o => o.id === orderId);
          if (found) setEditOrder(found);
        }}
        guestId={guestId}
        onMethodChanged={() => fetchOrders()}
      />
      <EftposTerminalModal
        alert={eftposAlert}
        onRingBell={table?.id ? (() => callService('payment_eftpos')) : null}
      />
    </>
  );

  if (tableError) return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center">
      <div className="text-center"><Wine className="w-12 h-12 text-red-400 mx-auto mb-4" /><h2 className="text-2xl font-serif text-white mb-2">Invalid Table</h2><p className="text-zinc-400 font-sans" data-testid="table-error">{tableError}</p></div>
      {cashCounterOverlay}
    </div>
  );
  if (!table && !isTakeawayMode) return (
    <div className="min-h-screen bg-bg-primary flex flex-col items-center justify-center" data-testid="customer-splash">
      <style>{`
        @keyframes splashFadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
        @keyframes splashPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(199, 135, 31, 0.45); } 50% { box-shadow: 0 0 0 24px rgba(199, 135, 31, 0); } }
        @keyframes splashSubtitle { 0% { opacity: 0; transform: translateY(8px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div className="flex flex-col items-center gap-3" style={{ animation: 'splashFadeIn 500ms ease-out' }}>
        <div className="px-4 py-3" style={{ animation: 'splashPulse 1.6s ease-out infinite' }}>
          <img src="/majnu-logo.png" alt="Majnu Lounge Bar Kitchen" className="h-24 w-auto object-contain" />
        </div>
      </div>
      {cashCounterOverlay}
    </div>
  );
  if (!canShop) return (
    <>
    <div className="min-h-screen bg-bg-primary relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'url(https://images.pexels.com/photos/20053277/pexels-photo-20053277.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95" />

      <div className="relative z-10 flex flex-col items-center min-h-screen px-6 py-8 overflow-y-auto w-full box-border" style={{paddingTop: 'max(env(safe-area-inset-top, 0px), 32px)'}}>
        {/* Logo */}
        <div className="text-center mb-6 pt-4">
          <img src="/majnu-logo.png" alt="Majnu Lounge Bar Kitchen" className="h-24 w-auto mx-auto object-contain" />
        </div>

        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/[0.03] border border-white/10 rounded-full mb-6">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-zinc-400 text-xs font-sans">Table {table.number}</span>
        </div>

        {/* Quick start: order without an account */}
        <div className="w-full max-w-sm mb-5">
          <Button
            onClick={() => {
              try { localStorage.setItem(`majnu:guest:${qrCode || 'default'}`, '1'); } catch { /* */ }
              setGuestMode(true);
            }}
            className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans"
            data-testid="continue-as-guest-button"
          >
            Start Ordering
          </Button>
          <p className="text-center text-zinc-600 text-[11px] font-sans mt-2">No account needed — just browse and order</p>
        </div>

        <div className="w-full max-w-sm flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-zinc-700 text-[10px] uppercase tracking-[0.2em] font-sans">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <div className="w-full max-w-sm">
          {/* Auth Mode Tabs */}
          {authMode !== 'reset' && (
            <div className="flex mb-6 border-b border-white/10">
              <button onClick={() => setAuthMode('signin')} className={`flex-1 pb-3 text-sm font-sans tracking-wide transition-all ${authMode === 'signin' ? 'text-gold border-b-2 border-gold' : 'text-zinc-500'}`} data-testid="auth-tab-signin">Sign In</button>
              <button onClick={() => setAuthMode('signup')} className={`flex-1 pb-3 text-sm font-sans tracking-wide transition-all ${authMode === 'signup' ? 'text-gold border-b-2 border-gold' : 'text-zinc-500'}`} data-testid="auth-tab-signup">Become a Member</button>
            </div>
          )}

          {/* SIGN IN */}
          {authMode === 'signin' && (
            <form onSubmit={handleSignin} data-testid="customer-signin-form" className="space-y-4 animate-fadeIn">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email or Mobile</Label>
                <div className="relative">
                  <User className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input
                    value={signinForm.identifier}
                    onChange={e => setSigninForm(p => ({ ...p, identifier: e.target.value }))}
                    className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm pl-6 placeholder:text-zinc-700"
                    placeholder="you@email.com or +61 4XX XXX XXX"
                    required
                    autoComplete="username"
                    data-testid="signin-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Password</Label>
                <div className="relative">
                  <KeyRound className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input type={showPassword ? 'text' : 'password'} value={signinForm.password} onChange={e => setSigninForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm pl-6 pr-8 placeholder:text-zinc-700" placeholder="Enter password" required data-testid="signin-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
              <div className="pt-1"><div className="w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" /></div>
              <Button type="submit" disabled={loginLoading} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="signin-submit-button">
                {loginLoading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Sign In'}
              </Button>
              <button type="button" onClick={() => { setAuthMode('reset'); setResetResult(null); }} className="text-gold/70 text-xs font-sans hover:text-gold block mx-auto" data-testid="forgot-password-link">Forgot password?</button>
            </form>
          )}

          {/* SIGN UP / MEMBERSHIP */}
          {authMode === 'signup' && (
            <form onSubmit={handleSignup} data-testid="customer-signup-form" className="space-y-4 animate-fadeIn">
              <div className="text-center mb-2">
                <h2 className="text-lg font-serif text-white">Become a Member</h2>
                <p className="text-zinc-500 text-xs font-sans">Join Majnu to start ordering</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">First Name</Label>
                  <Input value={loginForm.first_name} onChange={e => setLoginForm(p => ({ ...p, first_name: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="James" required data-testid="customer-first-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Last Name</Label>
                  <Input value={loginForm.last_name} onChange={e => setLoginForm(p => ({ ...p, last_name: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Smith" required data-testid="customer-last-name-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email</Label>
                <Input type="email" value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="james@email.com" required data-testid="customer-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Phone Number</Label>
                <Input type="tel" value={loginForm.phone} onChange={e => setLoginForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="+61 4XX XXX XXX" required data-testid="customer-phone-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Postcode</Label>
                  <Input value={loginForm.postcode} onChange={e => setLoginForm(p => ({ ...p, postcode: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="3000" required data-testid="customer-postcode-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Password</Label>
                  <Input type="password" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Min 4 chars" required data-testid="customer-password-input" />
                </div>
              </div>
              <div className="pt-1"><div className="w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" /></div>
              <Button type="submit" disabled={loginLoading} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="customer-login-button">
                {loginLoading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Join & Start Ordering'}
              </Button>
              <p className="text-center text-zinc-700 text-[10px] font-sans">By joining, you agree to receive exclusive offers from Majnu.</p>
            </form>
          )}

          {/* PASSWORD RESET via Twilio SMS OTP */}
          {authMode === 'reset' && (
            <div className="animate-fadeIn">
              <button onClick={() => { setAuthMode('signin'); setResetResult(null); }} className="flex items-center gap-1 text-zinc-500 text-xs font-sans hover:text-zinc-300 mb-4" data-testid="back-to-signin"><ChevronLeft className="w-3 h-3" /> Back to Sign In</button>
              {(!resetResult || !resetResult.step) && (
                <form onSubmit={handleResetRequest} className="space-y-4">
                  <h2 className="text-lg font-serif text-white">Reset Password</h2>
                  <p className="text-zinc-500 text-xs font-sans">Enter the email on your account. We'll send a 6-digit code to your inbox.</p>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email</Label>
                    <Input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="you@email.com" required data-testid="reset-email-input" />
                  </div>
                  <Button type="submit" disabled={loginLoading} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="reset-submit-button">
                    {loginLoading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Send Code'}
                  </Button>
                </form>
              )}
              {resetResult?.step === 'verify' && (
                <form onSubmit={handleResetVerify} className="space-y-4">
                  <h2 className="text-lg font-serif text-white">Enter Code</h2>
                  <p className="text-zinc-500 text-xs font-sans">A 6-digit code was sent to <span className="text-gold font-medium">{resetResult.email_masked || 'your inbox'}</span></p>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">6-Digit Code</Label>
                    <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={resetResult.code || ''} onChange={e => setResetResult(p => ({ ...p, code: e.target.value.replace(/\D/g, '') }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans tracking-[0.4em] text-center text-lg" placeholder="123456" required data-testid="reset-otp-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">New Password</Label>
                    <Input type="password" value={resetResult.new_password || ''} onChange={e => setResetResult(p => ({ ...p, new_password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Min 6 characters" required data-testid="reset-new-password-input" />
                  </div>
                  <Button type="submit" disabled={loginLoading} className="w-full h-12 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans" data-testid="reset-verify-button">
                    {loginLoading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Verify & Reset'}
                  </Button>
                  <button type="button" onClick={() => setResetResult(null)} className="w-full text-zinc-500 hover:text-zinc-300 text-xs font-sans" data-testid="reset-back-btn">Use a different number</button>
                </form>
              )}
              {resetResult?.step === 'done' && (
                <div className="space-y-4 text-center">
                  <div className="w-12 h-12 rounded-sm bg-emerald-500/10 flex items-center justify-center mx-auto"><KeyRound className="w-6 h-6 text-emerald-400" /></div>
                  <h2 className="text-lg font-serif text-white">Password Reset</h2>
                  <p className="text-zinc-400 text-sm font-sans">Your password has been reset successfully.</p>
                  <Button onClick={() => { setAuthMode('signin'); setResetResult(null); }} className="bg-gold text-black font-bold rounded-sm font-sans" data-testid="back-to-signin-after-reset">Sign In Now</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
      {cashCounterOverlay}
    </>
  );

  // Atmospheric category-themed backdrop for the inside menu pages —
  // mirrors the landing-page card design (hero image at low opacity +
  // diagonal accent gradient + radial blur blob in the corner). Switches
  // colour by activeCategory. Hooks themselves are declared earlier
  // (above any early returns) — this block just derives the per-render
  // backdrop config.
  const categoryBackdrop = (() => {
    if (activeCategory === 'all' || activeCategory === 'search') return null;
    const palette = {
      drinks:     { accent: 'rgba(59,130,246,0.85)',  blob: 'rgba(59,130,246,0.55)' },
      food:       { accent: 'rgba(245,158,11,0.85)',  blob: 'rgba(245,158,11,0.55)' },
      shisha:     { accent: 'rgba(167,139,250,0.9)',  blob: 'rgba(167,139,250,0.55)' },
      activities: { accent: 'rgba(16,185,129,0.9)',   blob: 'rgba(16,185,129,0.55)' },
    }[activeCategory];
    if (!palette) return null;
    const heroPath = categoryBackdrops[activeCategory];
    const hero = heroPath ? getImageUrl(heroPath) : categoryHero(activeCategory);
    return { ...palette, hero };
  })();

  // --- MAIN MENU VIEW ---
  return (
    <div className="min-h-screen bg-bg-primary pb-24 w-full box-border relative" style={{ paddingTop: `${headerH + announcementH}px` }}>
      {/* Closing-time gold banner — appears at 00:00 Melbourne with the last-call
          copy, then auto-updates to "we're closing now" at 00:30. Catches the
          customer even if they blocked Web Push. Multi-line layout with a
          Hide button so customers can dismiss it for the rest of their visit. */}
      {announcement && !dismissedAnnouncementIds.has(announcement.id) && (
        <div
          ref={announcementSetRef}
          className="fixed left-0 right-0 z-[45] px-4 py-3 shadow-[0_4px_18px_rgba(0,0,0,0.7)] border-b-2 border-gold"
          style={{ top: `${headerH}px`, background: '#1a1208', color: '#fde68a' }}
          data-testid={`closing-banner-${announcement.kind}`}
        >
          <button
            type="button"
            onClick={() => dismissAnnouncement(announcement.id)}
            className="absolute top-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded-sm bg-black/60 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors text-xs font-sans uppercase tracking-wider"
            aria-label="Hide closing announcement"
            data-testid={`closing-banner-hide-${announcement.kind}`}
          >
            <X className="w-3.5 h-3.5" />
            <span>Hide</span>
          </button>
          <div className="text-center pr-20 pl-2 space-y-1">
            {announcement.headline ? (
              <div className="font-serif text-base sm:text-lg font-semibold tracking-wide flex items-center justify-center gap-2">
                <Zap className="w-4 h-4 text-amber-300 animate-pulse" />
                <span>{announcement.headline}</span>
                <Zap className="w-4 h-4 text-amber-300 animate-pulse" />
              </div>
            ) : null}
            {announcement.subtitle ? (
              <div className="font-sans text-sm sm:text-base font-medium">{announcement.subtitle}</div>
            ) : null}
            {announcement.sub_subtitle ? (
              <div className="font-sans text-xs sm:text-sm text-amber-200/90">{announcement.sub_subtitle}</div>
            ) : null}
            {announcement.body_long ? (
              <div className="font-sans text-[11px] sm:text-xs text-amber-100/80 leading-snug max-w-md mx-auto pt-1">
                {announcement.body_long}
              </div>
            ) : !announcement.headline ? (
              <div className="text-xs sm:text-sm font-medium tracking-wide">
                <span className="mr-2 inline-block animate-pulse">●</span>
                {announcement.banner_text}
              </div>
            ) : null}
          </div>
        </div>
      )}      {/* Category atmospheric backdrop — fixed behind everything so the
          backdrop stays put while menu scrolls. Pointer-events none so it
          never blocks taps. (Per-user request: photo layer disabled —
          only the color gradient + radial glow remain so item descriptions
          stay legible.) */}
      {categoryBackdrop && (
        <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden>
          {/* Diagonal accent wash — same recipe as the landing card so the
              inside page reads as a deeper continuation of the landing tile. */}
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.78) 45%, ${categoryBackdrop.accent.replace(/0\.85\)|0\.9\)/, '0.40)')} 100%)` }} />
          {/* Bottom-darkening fade for legibility */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 100%)' }} />
          {/* Pulsing radial blob — anchored bottom-right like the landing card */}
          <div
            className="absolute -bottom-24 -right-20 w-[28rem] h-[28rem] rounded-full opacity-50"
            style={{ background: `radial-gradient(closest-side, ${categoryBackdrop.blob}, transparent 70%)`, filter: 'blur(60px)', animation: 'majnuPulseGlow 8s ease-in-out infinite' }}
          />
          {/* Second smaller blob on the top-left for depth */}
          <div
            className="absolute -top-16 -left-16 w-72 h-72 rounded-full opacity-40"
            style={{ background: `radial-gradient(closest-side, ${categoryBackdrop.blob}, transparent 70%)`, filter: 'blur(50px)', animation: 'majnuPulseGlow 10s ease-in-out infinite reverse' }}
          />
        </div>
      )}
      {/* Staff-mode banner — manager placing an order on behalf of the table.
          36px tall (matches STAFF_BANNER_H); the header below is shifted down
          by that amount and the wrapper's paddingTop bakes it in so nothing
          gets clipped behind the banner. */}
      {staffOrderMode && (
        <div className="fixed top-0 left-0 right-0 z-[70] bg-gold text-black px-4 text-xs font-sans font-bold tracking-[0.1em] uppercase text-center flex items-center justify-center" style={{ height: `${STAFF_BANNER_H}px` }} data-testid="staff-mode-banner">
          Staff Order · {tableLabel(table?.number)} · cash payment by default
        </div>
      )}
      {/* Manager-only "Custom Product" Dialog — opens when the manager
          taps the pinned-to-top Custom Product menu card. Lets them set
          ad-hoc name + price + qty + notes (damages, off-menu requests,
          manual fees). Customers never see this UI; the backend also
          403s any non-manager call. */}
      {canAddCustomProduct && (
        <Dialog open={customProductOpen} onOpenChange={setCustomProductOpen}>
          <DialogContent className="bg-bg-secondary border border-amber-500/40 max-w-sm" data-testid="custom-product-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-amber-400" />
                Custom Product
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-1">
              <p className="text-[11px] text-amber-200/80 font-sans bg-amber-500/10 border border-amber-500/30 rounded-sm p-2">
                Manager-only. Price is GST-inclusive. Booked under <strong>Misc/Manual Charges</strong> in Reports.
              </p>
              <div className="space-y-1">
                <Label htmlFor="cp-name" className="text-xs text-zinc-400 font-sans">Product name</Label>
                <Input
                  id="cp-name"
                  value={customProductDraft.name}
                  onChange={(e) => setCustomProductDraft(d => ({ ...d, name: e.target.value }))}
                  placeholder="e.g. Broken glass, Birthday cake, Extra service"
                  maxLength={80}
                  className="bg-bg-primary border-white/15 text-white"
                  data-testid="custom-product-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="cp-price" className="text-xs text-zinc-400 font-sans">Price (AUD, inc. GST)</Label>
                  <Input
                    id="cp-price"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0.01"
                    value={customProductDraft.price}
                    onChange={(e) => setCustomProductDraft(d => ({ ...d, price: e.target.value }))}
                    placeholder="0.00"
                    className="bg-bg-primary border-white/15 text-white font-mono"
                    data-testid="custom-product-price-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="cp-qty" className="text-xs text-zinc-400 font-sans">Quantity</Label>
                  <Input
                    id="cp-qty"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    step="1"
                    value={customProductDraft.qty}
                    onChange={(e) => setCustomProductDraft(d => ({ ...d, qty: e.target.value }))}
                    className="bg-bg-primary border-white/15 text-white font-mono"
                    data-testid="custom-product-qty-input"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cp-notes" className="text-xs text-zinc-400 font-sans">Notes (optional)</Label>
                <Input
                  id="cp-notes"
                  value={customProductDraft.notes}
                  onChange={(e) => setCustomProductDraft(d => ({ ...d, notes: e.target.value }))}
                  placeholder="Reason / context — visible on the receipt"
                  maxLength={240}
                  className="bg-bg-primary border-white/15 text-white"
                  data-testid="custom-product-notes-input"
                />
              </div>
              <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2 text-sm font-sans">
                <span className="text-amber-200">Line total</span>
                <span className="font-mono text-white">
                  ${(((parseFloat(customProductDraft.price) || 0) * (parseInt(customProductDraft.qty, 10) || 0)) || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setCustomProductOpen(false)}
                  className="flex-1 border-white/20 text-zinc-300 hover:bg-white/5 rounded-sm font-sans"
                  data-testid="custom-product-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={addCustomProductToCart}
                  className="flex-1 bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-sm font-sans"
                  data-testid="custom-product-add-button"
                >
                  Add to cart
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}      {/* Floating "Request a Song" button — sits ABOVE the Service
          Bell. Hidden when the YouTube API key isn't configured on the
          backend (probed via /api/youtube/status on mount) so we don't
          tease customers with a feature staff can't fulfil. */}
      {!isTakeawayMode && musicEnabled && (isCustomer || guestMode) && (
        <button
          onClick={() => setMusicModalOpen(true)}
          className="fixed bottom-36 right-4 z-40 h-12 pl-3 pr-4 rounded-full bg-bg-secondary text-gold shadow-lg shadow-black/40 flex items-center gap-2 border border-gold/40 hover:scale-105 active:scale-95 transition-transform font-sans"
          aria-label="Request a Song"
          data-testid="request-song-button"
        >
          <Music className="w-5 h-5" />
          <span className="text-xs uppercase tracking-[0.16em] font-semibold">Request a Song</span>
        </button>
      )}
      {/* Live song queue status pill — sits just above the Music button. Shows
          the customer's own position when their request is queued/playing,
          otherwise the queue length so guests get a sense of the vibe. */}
      {!isTakeawayMode && musicEnabled && (isCustomer || guestMode) && (() => {
        const playable = (musicQueue || []).filter(q => q.status === 'queued' || q.status === 'playing');
        if (playable.length === 0) return null;
        const mineIdx = playable.findIndex(q => q.guest_id && q.guest_id === guestId);
        const mine = mineIdx >= 0 ? playable[mineIdx] : null;
        const label = mine
          ? (mine.status === 'playing' ? 'Your song · NOW PLAYING' : `Your song · #${mineIdx + 1}`)
          : `${playable.length} ${playable.length === 1 ? 'song' : 'songs'} up next`;
        return (
          <div
            className={`fixed bottom-52 right-4 z-40 px-3 py-1.5 rounded-full text-[10px] font-sans uppercase tracking-[0.18em] font-bold backdrop-blur-md border ${mine ? 'bg-gold/15 border-gold/50 text-gold animate-pulse' : 'bg-black/60 border-white/15 text-zinc-300'}`}
            data-testid="music-queue-pill"
          >
            {label}
          </div>
        );
      })()}
      <MusicRequestModal
        open={musicModalOpen}
        onClose={() => {
          setMusicModalOpen(false);
          // Refresh the queue right away so the "Your song · #N" pill appears
          // without waiting on the 20s poll.
          if (musicEnabled) {
            api.get('/music-queue').then(({ data }) => {
              setMusicQueue(Array.isArray(data) ? data : []);
            }).catch(() => { /* silent */ });
          }
        }}
        tableId={table?.id}
        guestId={guestId}
        requesterName={(user?.name || guestName || '').trim() || undefined}
      />
      {/* Floating "Ring For Service" bell — bottom right by default, but
          customers can drag it anywhere on the screen (Apple AssistiveTouch
          style); the wrapper snaps to the nearest vertical edge on
          release and persists position to localStorage. Visible to
          signed-in customers, walk-in guests (with or without a name —
          backend defaults to "A guest"), and staff-mode managers.
          Hidden on the takeaway flow, and ALSO hidden once the 3-hour
          QR-scoped session has expired (Feb 2026 owner ask — stops the
          "customer rings from home days later" scenario). */}
      {!isTakeawayMode && (isCustomer || guestMode) && !serviceRequest && !sessionExpired && (
        <FloatingDraggable
          storageKey="majnu:serviceBellPos"
          defaultPosition={{ side: 'right', bottomPx: 80 }}
          width={88}
          height={72}
          data-testid="service-bell-floating-wrapper"
        >
          <button
            onClick={() => callService('help')}
            disabled={serviceRequestLoading}
            className="flex flex-col items-center gap-1 group disabled:opacity-60 font-sans"
            aria-label="Ring For Service"
            data-testid="call-service-button"
          >
            <span className="w-12 h-12 rounded-full bg-gold text-black shadow-lg shadow-gold/40 border-2 border-gold/70 flex items-center justify-center group-hover:scale-110 group-active:scale-95 transition-transform">
              <BellRing className="w-5 h-5" />
            </span>
            <span className="text-[9px] uppercase tracking-[0.12em] font-semibold text-gold leading-none drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]">Ring For<br/>Service</span>
          </button>
        </FloatingDraggable>
      )}
      {/* Session-expired chip in place of the bell — explains *why* the
          bell is gone and gives a one-tap CTA to refresh the QR scan. */}
      {!isTakeawayMode && guestMode && sessionExpired && !serviceRequest && (
        <FloatingDraggable
          storageKey="majnu:serviceBellPos"
          defaultPosition={{ side: 'right', bottomPx: 80 }}
          width={200}
          height={56}
          data-testid="session-expired-floating-wrapper"
        >
          <button
            onClick={() => window.location.reload()}
            className="max-w-[calc(100vw-2rem)] rounded-full border border-amber-500/40 bg-bg-secondary/95 backdrop-blur-md py-2 px-3 shadow-md flex items-center gap-2 group"
            data-testid="session-expired-chip"
            title="Your 3-hour table session has ended — scan the QR again to ring service or place new orders."
          >
            <span className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
              <BellRing className="w-3.5 h-3.5 text-amber-300/80" />
            </span>
            <div className="text-left leading-tight">
              <p className="text-amber-200 text-[10px] uppercase tracking-[0.14em] font-sans">
                Session ended
              </p>
              <p className="text-zinc-400 text-[9px] font-sans">
                Scan the QR to continue
              </p>
            </div>
          </button>
        </FloatingDraggable>
      )}
      {!isTakeawayMode && (isCustomer || guestMode) && serviceRequest && (
        <FloatingDraggable
          storageKey="majnu:serviceBellPos"
          defaultPosition={{ side: 'right', bottomPx: 80 }}
          width={180}
          height={32}
          data-testid="service-status-floating-wrapper"
        >
          <div className="max-w-[calc(100vw-2rem)] rounded-full border border-gold/30 bg-bg-secondary/95 backdrop-blur-md py-0.5 pl-0.5 pr-1.5 shadow-md flex items-center gap-1" data-testid="service-request-status">
          {serviceRequest.status === 'accepted' ? (
            <>
              <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                <Check className="w-2 h-2 text-emerald-400" />
              </div>
              <p className="text-white text-[10px] font-sans truncate max-w-[120px]">
                On the way{serviceRequest.accepted_by_name ? ` — ${serviceRequest.accepted_by_name}` : ''}
              </p>
              <button
                onClick={dismissService}
                disabled={serviceRequestLoading}
                className="shrink-0 ml-0.5 px-1.5 py-0.5 text-[9px] font-sans rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
                data-testid="dismiss-service-button"
                title="Tap once a staff member has assisted you"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <div className="w-4 h-4 rounded-full bg-gold/20 flex items-center justify-center shrink-0 animate-pulse">
                <BellRing className="w-2 h-2 text-gold" />
              </div>
              <p className="text-white text-[10px] font-sans">Service called</p>
              <button
                onClick={cancelService}
                disabled={serviceRequestLoading}
                className="shrink-0 ml-0.5 px-1.5 py-0.5 text-[9px] font-sans rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-50 transition-colors"
                data-testid="cancel-service-button"
              >
                Cancel
              </button>
            </>
          )}
          </div>
        </FloatingDraggable>
      )}

      {/* Header - Fixed at top (shifted down by the Staff Order banner when in staff mode) */}
      <header ref={headerRef} className="fixed left-0 right-0 z-40 bg-bg-primary border-b border-white/5" style={{ top: `${bannerOffset}px` }}>
        <div className="px-3 py-3 pt-4 grid grid-cols-[auto_1fr_auto] items-center gap-2">
          {/* Left: Hamburger nav */}
          <button
            onClick={() => setNavOpen(true)}
            className="p-2 text-zinc-300 hover:text-gold transition-colors"
            aria-label="Menu"
            data-testid="customer-nav-button"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Center: Logo */}
          <div className="flex justify-center min-w-0">
            <img
              src="/majnu-logo.png"
              alt="Majnu Lounge Bar Kitchen"
              className="h-9 w-auto object-contain"
              data-testid="customer-header"
            />
          </div>

          {/* Right: Tab badge + Cart */}
          <div className="flex items-center gap-2 justify-end">
            {!isTakeawayMode && tab && (
              <button
                type="button"
                onClick={() => setSettleTabOpen(true)}
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-gold/40 bg-gold/10 hover:bg-gold/20 text-gold font-sans text-xs transition"
                data-testid="header-settle-tab-button"
                title="Settle your tab"
              >
                <Receipt className="w-3.5 h-3.5" />
                Tab: ${tabRunningTotal.toFixed(2)}
                <span className="ml-1 text-[9px] uppercase tracking-[0.14em] opacity-80">Settle</span>
              </button>
            )}
          <Sheet open={cartOpen} onOpenChange={setCartOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="relative border-white/10 bg-white/5 text-white hover:border-gold hover:text-gold rounded-sm" data-testid="cart-button">
                <ShoppingCart className="w-4 h-4" />
                {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gold text-black text-xs rounded-full flex items-center justify-center font-bold">{cartCount}</span>}
                {cartCount === 0 && activeMyOrders.length > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" data-testid="pending-order-dot" title="You have a pending order" />}
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-bg-secondary border-l border-white/5 w-full sm:max-w-md overflow-y-auto">
              <SheetHeader className="flex flex-row items-center justify-between gap-2 pr-8">
                <SheetTitle className="font-serif text-2xl text-white">{isTakeawayMode ? 'Pickup Order' : 'Your Order'}</SheetTitle>
                {/* Bell hidden for takeaway — there is no table to call service for. */}
                {!isTakeawayMode && (serviceRequest && serviceRequest.status === 'accepted' ? (
                  <button
                    onClick={() => {
                      if (window.confirm('Has the staff already come over and helped you? Tap OK to mark this service request as done.')) {
                        dismissService();
                      }
                    }}
                    disabled={serviceRequestLoading}
                    className="rounded-full border border-emerald-500/30 bg-emerald-500/10 py-1 pl-1 pr-2.5 flex items-center gap-1.5 shrink-0 hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-colors disabled:opacity-60"
                    title="Tap to mark service as completed"
                    data-testid="cart-service-accepted"
                  >
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-emerald-300 text-[11px] font-sans">On the way · tap when done</span>
                  </button>
                ) : serviceRequest ? (
                  <button
                    onClick={cancelService}
                    disabled={serviceRequestLoading}
                    className="w-11 h-11 rounded-full bg-gold/10 border border-gold/40 text-gold flex items-center justify-center hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/40 transition-colors disabled:opacity-60 shrink-0 animate-pulse"
                    aria-label="Cancel service request"
                    data-testid="cart-cancel-service-button"
                  >
                    <BellRing className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => callService('help')}
                    disabled={serviceRequestLoading}
                    className="w-11 h-11 rounded-full bg-gold text-black border border-gold/70 shadow-lg shadow-gold/30 flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-60 shrink-0"
                    aria-label="Call for service"
                    data-testid="cart-call-service-button"
                  >
                    <BellRing className="w-5 h-5" />
                  </button>
                ))}
              </SheetHeader>
              <div className="mt-4 space-y-3 pb-8">
                {/* Customer's CURRENT pending orders — visible only inside cart, never on main page.
                    Defensive filter: backend already excludes terminal states, but if a stale doc
                    slips through (e.g. cached fetch), we hide it here too so the customer never
                    sees an order they can't act on. */}
                {activeMyOrders.map(order => {
                  const canCancel = order.order_status === 'active' &&
                                    order.payment_status !== 'paid' &&
                                    (order.items || []).every(it => !it.status || it.status === 'received');
                  // Pay Now shows for any unpaid order whose payment is still expected:
                  // - 'pending_cash' (cash chosen, switch to card retry)
                  // - 'pending'      (card OR Apple Pay chosen but Square was abandoned)
                  const canPayNow = order.payment_status === 'pending_cash' ||
                                    (order.payment_status === 'pending' && ['card', 'apple_pay'].includes(order.payment_method));
                  // Did the customer originally pick Apple Pay and bounce
                  // back without paying? Show a prominent retry button.
                  const isApplePayPending = order.payment_status === 'pending' && order.payment_method === 'apple_pay';
                  return (
                  <div key={order.id} className={`rounded-sm border p-3 space-y-2 ${order.order_status === 'completed' ? 'bg-emerald-500/[0.04] border-emerald-500/30' : canPayNow ? 'bg-red-500/[0.06] border-red-500/40' : 'bg-amber-500/[0.05] border-amber-500/30'}`} data-testid={`current-order-${order.id}`}>
                    {canPayNow && (
                      <div className="rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-2 flex items-start gap-2" data-testid={`payment-incomplete-banner-${order.id}`}>
                        <AlertCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-[11px] font-sans font-bold uppercase tracking-[0.1em] text-red-200">
                            Payment Incomplete
                          </p>
                          <p className="text-[11px] text-red-200/90 font-sans leading-snug mt-0.5">
                            {isApplePayPending
                              ? 'Apple Pay didn\u2019t complete. Retry below or pick another method — your order isn\u2019t sent until payment lands.'
                              : order.payment_method === 'card'
                                ? 'Card checkout didn\u2019t finish. Retry below or pick another method.'
                                : 'Tap a payment option below to complete this order.'}
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        {order.order_status === 'completed' ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-emerald-400" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-sans font-bold" data-testid={`order-status-label-${order.id}`}>Delivered</span>
                          </>
                        ) : canPayNow ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-red-300 font-sans font-bold" data-testid={`order-status-label-${order.id}`}>Awaiting Payment</span>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-sans font-bold" data-testid={`order-status-label-${order.id}`}>Getting Your Order Ready</span>
                          </>
                        )}
                      </div>
                      <span className={`text-[10px] font-mono ${order.order_status === 'completed' ? 'text-emerald-300/80' : canPayNow ? 'text-red-300/80' : 'text-amber-300/80'}`}>#{order.id.slice(0, 6).toUpperCase()}</span>
                    </div>
                    {order.items.map(item => (
                      <div key={item.id} className="flex justify-between items-center">
                        <span className="text-xs text-zinc-300 font-sans">{item.quantity}× {item.name}</span>
                        <Badge className={`${customerStatusColors[customerStatus(item.status)]} border-0 font-sans text-[10px] px-1.5 py-0`}>{customerStatus(item.status)}</Badge>
                      </div>
                    ))}
                    <div className={`flex justify-between items-center pt-2 border-t ${order.order_status === 'completed' ? 'border-emerald-500/20' : 'border-amber-500/20'}`}>
                      <Badge className={`${order.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : (order.payment_status === 'pending_cash' || order.payment_status === 'pending') ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-[10px]`}>
                        {order.payment_status === 'pending_cash' ? 'Pay Cash At Bar' : order.payment_status === 'pending' ? 'Awaiting Payment' : order.payment_status}
                      </Badge>
                      <span className="text-gold font-sans text-sm font-semibold">${order.total.toFixed(2)}</span>
                    </div>
                    {(canPayNow || canCancel || (order.payment_status !== 'paid' && order.order_status === 'active')) && (
                      <div className="space-y-2 pt-1">
                        {/* When the customer abandoned Square card / Apple Pay
                            checkout or chose cash but wants to switch — show
                            ALL payment options as a row so they can change
                            their mind without re-creating the order. If the
                            original method was Apple Pay we ALSO surface a
                            one-tap retry button at the top. */}
                        {canPayNow && isApplePayPending && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                // Ensure the order is back in pending+apple_pay
                                // state in case the customer fiddled with it.
                                if (order.payment_method !== 'apple_pay' || order.payment_status !== 'pending') {
                                  await api.post(`/orders/${order.id}/change-payment-method`, { payment_method: 'apple_pay', guest_id: guestId });
                                }
                                const { data: checkout } = await api.post('/payments/create-checkout', {
                                  order_id: order.id,
                                  origin_url: window.location.origin,
                                  tip_amount: 0,
                                });
                                window.location.href = checkout.url;
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Could not start Apple Pay — try EFTPOS or cash');
                              }
                            }}
                            className="w-full h-10 bg-black text-white border border-white/20 hover:bg-white/5 rounded-sm font-sans text-sm font-semibold"
                            data-testid={`retry-apple-pay-${order.id}`}
                          >
                            <Apple className="w-4 h-4 mr-1.5" />
                            Retry Apple Pay · ${order.total.toFixed(2)}
                          </Button>
                        )}
                        {canPayNow && (
                          <div className="grid grid-cols-3 gap-1.5" data-testid={`pay-options-${order.id}`}>
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  // EFTPOS = manager-comes-to-you. Switch
                                  // the order to card/pending if needed,
                                  // then fire the in-modal blocker + bell.
                                  if (order.payment_method !== 'card' || order.payment_status !== 'pending') {
                                    await api.post(`/orders/${order.id}/change-payment-method`, { payment_method: 'card', guest_id: guestId });
                                  }
                                  setEftposAlert({ orderId: order.id, name: order.customer_name, total: order.total });
                                  fetchOrders();
                                } catch (err) { toast.error(err.response?.data?.detail || 'EFTPOS switch failed'); }
                              }}
                              className="h-9 bg-gold text-black font-bold rounded-sm font-sans text-xs"
                              data-testid={`pay-card-${order.id}`}
                            >
                              <CreditCard className="w-3.5 h-3.5 mr-1" /> EFTPOS
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await api.post(`/orders/${order.id}/change-payment-method`, { payment_method: 'cash', guest_id: guestId });
                                  // Fire the existing pay-at-counter hard-blocker so customer can't slip away.
                                  setCashCounterAlert({ orderId: order.id, name: order.customer_name, total: order.total });
                                  fetchOrders();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Cash switch failed'); }
                              }}
                              className="h-9 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm font-sans text-xs"
                              data-testid={`pay-cash-${order.id}`}
                            >
                              <Banknote className="w-3.5 h-3.5 mr-1" /> Cash
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!tab}
                              onClick={async () => {
                                try {
                                  await api.post(`/orders/${order.id}/change-payment-method`, { payment_method: 'tab', guest_id: guestId });
                                  toast.success('Added to your tab');
                                  fetchOrders();
                                  fetchTab();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Could not add to tab'); }
                              }}
                              className="h-9 border-blue-accent/30 text-blue-accent hover:bg-blue-accent/10 disabled:opacity-30 rounded-sm font-sans text-xs"
                              data-testid={`pay-tab-${order.id}`}
                            >
                              <Receipt className="w-3.5 h-3.5 mr-1" /> Tab
                            </Button>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          {/* Edit button removed Feb 2026 per owner directive:
                              "No edit option required after placing the order."
                              Customers can still cancel as long as the kitchen
                              hasn't started (see canCancel below). Edits go
                              through staff via the Bar Panel / POS. */}
                          {canCancel && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (!window.confirm('Cancel this order? You can only cancel if the kitchen hasn\'t started.')) return;
                                try {
                                  // Guests authenticate via guest_id query param;
                                  // members via the auth cookie (server ignores
                                  // the query param when a valid member session
                                  // is present).
                                  const url = isCustomer
                                    ? `/orders/${order.id}/customer-cancel`
                                    : `/orders/${order.id}/customer-cancel?guest_id=${encodeURIComponent(guestId || '')}`;
                                  await api.put(url);
                                  toast.success('Order cancelled');
                                  fetchOrders();
                                } catch (err) { toast.error(err.response?.data?.detail || 'Cannot cancel'); }
                              }}
                              className="h-9 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-sm font-sans text-xs"
                            data-testid={`cancel-order-${order.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                        </div>
                      </div>
                    )}
                    {!canCancel && order.payment_status === 'paid' && order.order_status === 'active' && (
                      <p className="text-[10px] text-zinc-500 font-sans">To change a paid order, please ask a staff member.</p>
                    )}
                  </div>
                  );
                })}
                {cart.length === 0 ? (
                  activeMyOrders.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8 font-sans" data-testid="empty-cart">Your cart is empty</p>
                  ) : (
                    /* Customer has placed an order. Owner directive Feb 2026:
                       (1) no separate "Edit" button on the order card (handled
                       above); (2) the "Add more items" CTA lives INSIDE the
                       order summary panel — not as a separate floating block —
                       so it feels like the order is alive and growing; (3) if
                       a tab is active, the SETTLE TAB button must always be
                       reachable, including when the cart is empty. */
                    <div className="space-y-3 mt-3" data-testid="empty-cart-with-active-order">
                      {/* Owner ask Feb 2026: keep ordering CTA on top, then
                          the Tab Outstanding / Settle banner BELOW so the
                          customer sees "add more" first and only settles
                          when they're actually done. */}
                      <button
                        type="button"
                        onClick={() => setCartOpen(false)}
                        className="w-full rounded-sm border border-gold/30 bg-gold/[0.04] hover:bg-gold/[0.08] hover:border-gold/50 px-4 py-3.5 flex items-center justify-center gap-2 transition group"
                        data-testid="continue-shopping-button"
                      >
                        <Plus className="w-4 h-4 text-gold" />
                        <span className="text-gold font-sans text-sm font-semibold tracking-[0.04em]">
                          Add more items to your tab
                        </span>
                      </button>
                      <p className="text-[10px] text-zinc-600 font-sans text-center">
                        Drinks · Food · Shisha — keep ordering, pay once at the end.
                      </p>
                      {!isTakeawayMode && !staffOrderMode && tab && (
                        <div className="rounded-sm border border-gold/40 bg-gold/10 px-3 py-3 space-y-2.5" data-testid="tab-active-banner">
                          <div className="flex items-end justify-between gap-3">
                            <div>
                              <p className="text-gold font-sans text-[10px] font-semibold uppercase tracking-[0.16em]">Tab Outstanding</p>
                              <p className="text-zinc-400 font-sans text-[10px] mt-0.5">Ordered ${tabRunningTotal.toFixed(2)}{tabPaidAmount > 0 ? ` · Paid $${tabPaidAmount.toFixed(2)}` : ''}</p>
                            </div>
                            <p className="font-mono text-2xl text-gold leading-none" data-testid="tab-outstanding-amount">
                              ${tabOutstanding.toFixed(2)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSettleTabOpen(true)}
                            className="w-full h-10 rounded-sm border border-gold/40 bg-gold/15 hover:bg-gold/25 text-gold font-sans uppercase tracking-[0.18em] text-[11px] flex items-center justify-center gap-2 transition"
                            data-testid="cart-settle-tab-button"
                          >
                            <Receipt className="w-3.5 h-3.5" />
                            Settle Tab
                          </button>
                        </div>
                      )}
                    </div>
                  )
                ) :
                  cart.map(item => (
                    <div key={item.cartId} className="p-3 bg-white/[0.03] rounded-sm border border-white/5" data-testid={`cart-item-${item.cartId}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-white font-sans text-sm">{item.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {item.selectedSpice && (
                              <button onClick={() => setEditingCartItem(editingCartItem === item.cartId ? null : item.cartId)} className={`text-xs font-sans border px-2 py-0.5 rounded-sm ${SPICE_BADGE[item.selectedSpice] || SPICE_BADGE.Medium}`} data-testid={`edit-spice-${item.cartId}`}>
                                {item.selectedSpice} <span className="opacity-50 ml-1">edit</span>
                              </button>
                            )}
                            {(item.has_spice_level || item.category === 'food') && !item.selectedSpice && (
                              <button onClick={() => setEditingCartItem(item.cartId)} className="text-zinc-500 text-xs font-sans border border-white/10 px-2 py-0.5 rounded-sm" data-testid={`add-spice-${item.cartId}`}>
                                + Spice level
                              </button>
                            )}
                            {item.selectedAddOns?.length > 0 && <p className="text-zinc-500 text-xs font-sans">+ {item.selectedAddOns.map(a => a.name).join(', ')}</p>}
                          </div>
                          {item.instructions && (
                            <p className="text-amber-400/80 text-[11px] font-sans italic mt-1 break-words" data-testid={`cart-note-${item.cartId}`}>
                              📝 {item.instructions}
                            </p>
                          )}
                        </div>
                        <p className="text-gold text-sm font-sans font-semibold">${(item.unitPrice * item.qty).toFixed(2)}</p>
                      </div>
                      {editingCartItem === item.cartId && (
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans mb-1.5">Spice Level</p>
                          <div className="flex gap-1.5">
                            {spiceLevels.map(lvl => (
                              <button key={lvl} onClick={() => { setCart(prev => prev.map(c => c.cartId === item.cartId ? { ...c, selectedSpice: lvl } : c)); setEditingCartItem(null); }}
                                className={`flex-1 px-2 py-2 rounded-sm text-xs font-sans border transition-all text-center ${item.selectedSpice === lvl ? SPICE_STYLES[lvl].active : SPICE_STYLES[lvl].inactive}`}
                                data-testid={`cart-spice-${lvl}-${item.cartId}`}>
                                {lvl}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-1 bg-white/[0.04] rounded-sm border border-white/10">
                          <button onClick={() => updateQty(item.cartId, -1)} className="w-9 h-9 flex items-center justify-center text-zinc-300 hover:text-white active:bg-white/10 rounded-l-sm" data-testid={`cart-minus-${item.cartId}`}><Minus className="w-4 h-4" /></button>
                          <span className="text-white text-sm w-8 text-center font-sans font-semibold">{item.qty}</span>
                          <button onClick={() => updateQty(item.cartId, 1)} className="w-9 h-9 flex items-center justify-center text-zinc-300 hover:text-white active:bg-white/10 rounded-r-sm" data-testid={`cart-plus-${item.cartId}`}><Plus className="w-4 h-4" /></button>
                        </div>
                        <button onClick={() => setCart(prev => prev.filter(c => c.cartId !== item.cartId))} className="text-red-400 hover:text-red-300 text-xs font-sans px-2 py-1" data-testid={`cart-remove-${item.cartId}`}>Remove</button>
                      </div>
                    </div>
                  ))}
                {cart.length > 0 && (
                  <div className="pt-4 border-t border-white/5 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400 font-sans text-sm">Subtotal</span>
                      <span className="text-sm font-sans text-zinc-300" data-testid="cart-total">${cartTotal.toFixed(2)}</span>
                    </div>
                    <button onClick={() => setCart([])} className="text-xs text-zinc-500 hover:text-red-400 font-sans transition-colors" data-testid="clear-cart">Clear entire cart</button>

                    {/* Pickup time selector — TAKEAWAY ONLY */}
                    {isTakeawayMode && (
                      <div className="space-y-2" data-testid="pickup-time-selector">
                        <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Pickup Time</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          {[
                            { key: 'ASAP', label: 'ASAP' },
                            { key: '+15', label: '+15 min' },
                            { key: '+30', label: '+30 min' },
                            { key: '+45', label: '+45 min' },
                          ].map(opt => (
                            <button
                              key={opt.key}
                              onClick={() => setPickupTime(opt.key)}
                              className={`py-2 rounded-sm border text-[11px] font-sans transition-all ${pickupTime === opt.key ? 'border-gold/60 bg-gold/10 text-gold' : 'border-white/10 text-zinc-500 hover:border-white/20 hover:text-zinc-300'}`}
                              data-testid={`pickup-${opt.key}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {!isTakeawayMode && (
                    <div className="space-y-2">
                      {/* Owner spec Feb 2026 — FINAL ORDERING WORKFLOW: every
                          customer goes through a tab. Per-order payment method
                          is decided at SETTLE TAB time, not in the cart. So
                          customers never see the EFTPOS/Cash/Apple Pay/Start
                          Tab tile picker at the cart stage — single CTA only.
                          Staff iframe (?staff=1) is exempt — staff still need
                          to pick a method when ordering on someone's behalf. */}
                      {staffOrderMode ? (
                      <>
                      <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Payment Method</p>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'card', icon: CreditCard, label: 'EFTPOS' },
                          { key: 'cash', icon: Banknote, label: 'Pay Cash At Bar', promo: '10% Off' },
                          { key: 'apple_pay', icon: Apple, label: 'Apple Pay' },
                          { key: 'tab', icon: Receipt, label: 'Start Tab' },
                        ].map(m => {
                          const active = paymentMethod === m.key;
                          // Apple Pay gets its native-style black/white treatment
                          // when selected — mirrors the iOS Safari Apple Pay sheet
                          // so customers feel they're on a trusted branded surface.
                          const isApple = m.key === 'apple_pay';
                          const tileCls = active
                            ? (isApple
                                ? 'border-white bg-black text-white shadow-lg shadow-black/40'
                                : 'border-gold bg-gold/10 text-gold')
                            : 'border-white/10 text-zinc-400 hover:border-white/20';
                          return (
                            <button
                              key={m.key}
                              onClick={() => setPaymentMethod(m.key)}
                              className={`relative flex flex-col items-center gap-1 p-3 rounded-sm border transition-all font-sans text-xs ${tileCls}`}
                              data-testid={`payment-method-${m.key}`}
                            >
                              {/* 10% Off promo badge on the Pay Cash At Bar tile.
                                  Customer-facing nudge — actual discount is applied
                                  by the manager via the Cash 10% Off pill on the
                                  Live Orders panel when they collect the cash. */}
                              {m.promo && (
                                <span
                                  className={`absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold tracking-[0.08em] uppercase font-sans shadow-md border ${active ? 'bg-emerald-500/30 text-emerald-200 border-emerald-400/60' : 'bg-emerald-500/85 text-black border-emerald-300/80'}`}
                                  data-testid={`payment-method-${m.key}-promo`}
                                >
                                  {m.promo}
                                </span>
                              )}
                              <m.icon className="w-4 h-4" />{m.label}
                            </button>
                          );
                        })}
                      </div>
                      {paymentMethod === 'cash' && (
                        <p className="text-[10px] text-emerald-300 font-sans tracking-[0.04em] uppercase pl-1" data-testid="payment-method-cash-promo-hint">
                          10% off applied at the bar by staff when you pay in cash
                        </p>
                      )}
                      </>
                      ) : !tab ? (
                        /* Customer, no active tab → friendly "what happens
                           next" banner. No deposit / no upfront / settle at
                           the end — sets the right expectation BEFORE they
                           tap the CTA. */
                        <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-3 py-2.5" data-testid="cart-tab-explainer">
                          <div className="flex items-center justify-between gap-3 text-emerald-300 font-sans text-xs font-semibold tracking-[0.05em] uppercase">
                            <span className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                              No Deposit
                            </span>
                            <span className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                              Pay When You Leave
                            </span>
                          </div>
                        </div>
                      ) : (
                        /* Tab already open — show OUTSTANDING (what the
                           customer actually owes after deposit/top-ups)
                           prominently + the SETTLE TAB button. */
                        <div className="space-y-2" data-testid="tab-active-banner">
                          <div className="rounded-sm border border-gold/40 bg-gold/10 px-3 py-3">
                            <div className="flex items-end justify-between gap-3">
                              <div>
                                <p className="text-gold font-sans text-[10px] font-semibold uppercase tracking-[0.16em]">Tab Outstanding</p>
                                <p className="text-zinc-400 font-sans text-[10px] mt-0.5">Ordered ${tabRunningTotal.toFixed(2)}{tabPaidAmount > 0 ? ` · Paid $${tabPaidAmount.toFixed(2)}` : ''}</p>
                              </div>
                              <p className="font-mono text-2xl text-gold leading-none" data-testid="tab-outstanding-amount-cart">
                                ${tabOutstanding.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSettleTabOpen(true)}
                            className="w-full h-10 rounded-sm border border-gold/40 bg-gold/10 hover:bg-gold/20 text-gold font-sans uppercase tracking-[0.18em] text-[11px] flex items-center justify-center gap-2 transition"
                            data-testid="cart-settle-tab-button"
                          >
                            <Receipt className="w-3.5 h-3.5" />
                            Settle Tab
                          </button>
                        </div>
                      )}
                    </div>
                    )}

                    {/* Takeaway: card payment is implicit; show a clean info chip instead */}
                    {isTakeawayMode && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-sm border border-gold/20 bg-gold/5 text-gold text-xs font-sans" data-testid="takeaway-payment-info">
                        <CreditCard className="w-4 h-4" />
                        <span>Pay securely by card at checkout</span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="pt-2 border-t border-white/5 space-y-1.5">
                      {cashDiscountEligible && (
                        <div className="flex justify-between items-center" data-testid="cart-cash-discount-row">
                          <span className="text-emerald-300 font-sans text-[11px] uppercase tracking-[0.08em]">Cash · 10% Off</span>
                          <span className="text-emerald-300 font-sans text-sm font-mono" data-testid="cart-cash-discount">−${cashDiscountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-300 font-sans text-sm">{cashDiscountEligible ? 'Total at the Bar' : 'Total'}</span>
                        <span className="text-lg font-semibold text-gold font-sans" data-testid="grand-total">${grandTotal.toFixed(2)}</span>
                      </div>
                      {cashDiscountEligible && (
                        <p className="text-[10px] text-emerald-300/80 font-sans italic" data-testid="cart-cash-discount-caption">
                          Estimated — staff confirms the 10% off when you pay cash at the bar.
                        </p>
                      )}
                    </div>

                    <Button
                      onClick={() => placeOrder()}
                      disabled={orderLoading}
                      className="w-full h-12 bg-gold text-black font-bold tracking-wide rounded-sm hover:bg-gold-light font-sans"
                      data-testid="place-order-button"
                    >
                      {orderLoading
                        ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        : isTakeawayMode
                          ? `Pay & Order Pickup · $${cartTotal.toFixed(2)}`
                          : staffOrderMode
                            ? (paymentMethod === 'card'
                                ? `Pay $${grandTotal.toFixed(2)}`
                                : paymentMethod === 'apple_pay'
                                  ? `Apple Pay · $${grandTotal.toFixed(2)}`
                                  : paymentMethod === 'tab' && !tab
                                    ? `Open Tab & Place Order`
                                    : 'Place Order')
                            : tab
                              ? `Add to Tab · $${grandTotal.toFixed(2)}`
                              : `Place Order & Start Tab · $${grandTotal.toFixed(2)}`}
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
        </div>
        {/* Subtitle: customer identity / table */}
        <div className="px-5 pb-1.5 -mt-1 text-center">
          {isTakeawayMode ? (
            <p className="text-[11px] font-sans inline-flex items-center gap-2" data-testid="takeaway-mode-banner">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 uppercase tracking-[0.2em] text-[10px] font-bold">Takeaway</span>
              <span className="text-zinc-700">·</span>
              <span className="text-zinc-500">Order for pickup</span>
            </p>
          ) : guestMode && !isCustomer && guestName ? (
            <div className="text-[11px] font-sans inline-flex items-center gap-2 flex-wrap justify-center" data-testid="guest-greeting">
              <span className="inline-flex items-center gap-1 text-amber-300 uppercase tracking-[0.18em] text-[9px] font-bold bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm">
                Guest
              </span>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Hi ${guestName}! Tap OK to use a different name on this device.`)) {
                    try { localStorage.removeItem(`majnu:guestName:${qrCode || 'default'}`); } catch { /* sandboxed */ }
                    setGuestName('');
                    toast.success('Saved name cleared');
                  }
                }}
                className="text-gold hover:opacity-80 transition-opacity"
                title="Tap to change your guest name"
                data-testid="guest-greeting-name"
              >
                Hi, {guestName}
              </button>
              <span className="text-zinc-700">·</span>
              <span className="text-zinc-500">Table {user?.table_number || table.number}</span>
              <span className="text-zinc-700">·</span>
              <button
                type="button"
                onClick={() => {
                  // Exit guest mode and route the user to the sign-in screen.
                  // Also clear the LS guest flag so a mid-signin reload doesn't
                  // snap them back to guest mode. We keep guestId so backend
                  // can claim their orphan orders on signin.
                  try { localStorage.removeItem(`majnu:guest:${qrCode || 'default'}`); } catch { /* sandboxed */ }
                  setGuestMode(false);
                  setAuthMode('signin');
                }}
                className="text-emerald-300 hover:text-emerald-200 underline decoration-dotted underline-offset-2 transition-colors"
                data-testid="guest-greeting-signin"
              >
                Sign in to save history
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-500 font-sans">Table {user?.table_number || table.number}</p>
          )}
        </div>
        {/* Permanent Category Bar — narrow phones (iPhone SE etc.) couldn't
            fit 5 icon+label pairs in one row, so labels truncated to
            "Me…" / "Dri…" / "Shi…". We now hide the icons below `sm` and
            rely on the saturated background colour to carry each tab's
            identity, freeing the full word to render. Icons return on
            tablet+ widths where they look great alongside the text. */}
        <div className="px-2 py-2.5 flex items-center gap-1" data-testid="customer-category-tabs">
          {[
            { key: 'all', label: 'Menu', icon: UtensilsCrossed, iconActive: 'text-black', iconIdle: 'text-gold', activeBg: 'bg-gold', activeText: 'text-black' },
            { key: 'drinks', label: 'Drinks', icon: GlassWater, iconActive: 'text-white', iconIdle: 'text-blue-accent', activeBg: 'bg-blue-accent', activeText: 'text-white' },
            { key: 'food', label: 'Food', icon: Drumstick, iconActive: 'text-black', iconIdle: 'text-amber-300', activeBg: 'bg-amber-400', activeText: 'text-black' },
            { key: 'shisha', label: 'Shisha', icon: Flame, iconActive: 'text-white', iconIdle: 'text-violet-300', activeBg: 'bg-violet-500', activeText: 'text-white' },
            { key: 'activities', label: 'Play', icon: Target, iconActive: 'text-black', iconIdle: 'text-emerald-300', activeBg: 'bg-emerald-400', activeText: 'text-black' },
          ].filter(cat => !(isTakeawayMode && (cat.key === 'shisha' || cat.key === 'activities'))).map(cat => {
            const active = activeCategory === cat.key;
            return (
              <button
                key={cat.key}
                onClick={() => {
                  setActiveCategory(cat.key);
                  // Reset the drinks section filter so swapping to Food
                  // → Drinks → Food doesn't leave a stale Spirits chip
                  // narrowing the drinks view.
                  if (cat.key !== 'drinks') setActiveDrinksSection('all');
                  window.scrollTo(0, 0);
                }}
                className={`flex-1 min-w-0 flex items-center justify-center gap-1.5 px-1 py-2 rounded-sm text-[13px] sm:text-sm font-sans font-semibold tracking-wide transition-all ${active ? `${cat.activeBg} ${cat.activeText} shadow-md` : 'bg-white/[0.02] text-white border border-white/10 hover:bg-white/[0.05] hover:border-white/20'}`}
                data-testid={`nav-${cat.key}`}
              >
                <cat.icon className={`hidden sm:inline-block w-3.5 h-3.5 shrink-0 transition-colors ${active ? cat.iconActive : cat.iconIdle}`} />
                <span className="truncate">{cat.label}</span>
              </button>
            );
          })}
        </div>
        {/* Search Bar */}
        <div className="px-5 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menu..."
              className="w-full bg-white/[0.03] border border-white/10 rounded-sm pl-8 pr-8 py-1.5 text-sm text-white font-sans placeholder:text-zinc-600 focus:outline-none focus:border-gold/40"
              data-testid="customer-menu-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                data-testid="customer-menu-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        {/* Drinks 2-tier nav — top-level SECTION chips (Cocktails, Spirits,
            Beer & Wine, Mocktails, Hot Drinks, Soft Drinks & Shakes,
            Specials). Picking a section narrows the subcategory chip row
            below. June 2026 — owner directive to reduce category clutter. */}
        {activeCategory === 'drinks' && drinksSections.length > 0 && (
          <div className="px-5 pt-1 pb-2" data-testid="drinks-section-row">
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
              {[{section: 'all', label: 'All'}, ...drinksSections.map(s => ({section: s.section, label: s.section}))]
                .map(({section, label}) => {
                  const active = activeDrinksSection === section;
                  return (
                    <button
                      key={section}
                      type="button"
                      onClick={() => {
                        setActiveDrinksSection(section);
                        // Reset the leaf filter so the user sees every
                        // item in the new section by default.
                        setActiveSub(prev => ({ ...prev, drinks: 'all' }));
                      }}
                      className={`shrink-0 px-3 py-1.5 rounded-sm font-sans text-[12px] tracking-wide whitespace-nowrap transition-all border ${
                        active
                          ? 'bg-blue-accent text-white border-blue-accent shadow-sm font-semibold'
                          : 'bg-white/[0.03] text-zinc-300 border-white/10 hover:bg-white/[0.06] hover:border-white/20'
                      }`}
                      data-testid={`drinks-section-chip-${section.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                    >
                      {label}
                    </button>
                  );
                })}
            </div>
          </div>
        )}
        {/* Subcategory pills — replaces the old <select> dropdown. Horizontal
            scrollable row tinted with the active category's accent so the
            chips read as part of the category's colour identity. "All"
            chip resets to no filter.

            For Drinks specifically we suppress this picker when the
            top-tier section nav is on "All" — the 7-section nav
            (Cocktails / Spirits / Beer & Wine / Mocktails / Hot Drinks /
            Soft Drinks / Specials) already gives a clean taxonomy and
            showing the old 19-subcategory grid (Whisky / Vodka / Gin / …)
            underneath was confusing customers. Once the customer picks
            a section, the picker reappears with only that section's
            leaves — useful for drilling into Spirits → Whisky etc. */}
        {(activeCategory === 'shisha' || activeCategory === 'drinks' || activeCategory === 'food' || activeCategory === 'activities')
          && !(activeCategory === 'drinks' && activeDrinksSection === 'all')
          && availableSubs(activeCategory).length > 0 && (() => {
          const palette = {
            drinks:     { accent: 'rgba(59,130,246,0.95)', soft: 'rgba(59,130,246,0.16)', border: 'rgba(59,130,246,0.45)' },
            food:       { accent: 'rgba(245,158,11,0.95)', soft: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.45)' },
            shisha:     { accent: 'rgba(167,139,250,0.95)',soft: 'rgba(167,139,250,0.18)',border: 'rgba(167,139,250,0.45)' },
            activities: { accent: 'rgba(16,185,129,0.95)', soft: 'rgba(16,185,129,0.18)', border: 'rgba(16,185,129,0.45)' },
          }[activeCategory];
          const sel = activeSub[activeCategory];
          const chip = (key, label, count) => {
            const active = sel === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setActiveSub(prev => ({ ...prev, [activeCategory]: key }));
                  setSubPickerOpen(false);
                }}
                className={`majnu-chip flex items-center justify-center gap-1.5 px-2 py-2 rounded-sm border text-[11px] font-sans transition-all min-w-0 ${active ? 'text-black font-semibold' : 'text-white hover:brightness-125'}`}
                style={active
                  ? { background: palette.accent, borderColor: palette.accent, boxShadow: `0 0 12px -4px ${palette.accent}` }
                  : { background: 'rgba(0,0,0,0.45)', borderColor: palette.border }}
                data-testid={`subcategory-chip-${activeCategory}-${key.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span className="truncate">{label}</span>
                {typeof count === 'number' && (
                  <span
                    className="text-[9px] font-mono px-1 rounded-sm shrink-0"
                    style={active
                      ? { color: palette.accent, background: 'rgba(0,0,0,0.85)' }
                      : { color: palette.accent, background: palette.soft }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          };
          // Currently-selected sub label for the collapsed header.
          const subLabel = sel === 'all'
            ? 'All Categories'
            : (availableSubs(activeCategory).find(s => s.name === sel)?.name || sel);
          const subCount = sel === 'all'
            ? availableSubs(activeCategory).reduce((s, x) => s + x.count, 0)
            : (availableSubs(activeCategory).find(s => s.name === sel)?.count ?? null);
          return (
            <div className="px-5 pb-2">
              {/* Collapsed header — tap to reveal the grid. When a pill is
                  chosen the dropdown auto-collapses and reflects the choice. */}
              <button
                type="button"
                onClick={() => setSubPickerOpen(o => !o)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-sm border bg-black/40 backdrop-blur-[2px] text-white"
                style={{ borderColor: palette.border }}
                data-testid={`subcategory-picker-toggle-${activeCategory}`}
                aria-expanded={subPickerOpen}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-sans shrink-0">Select category</span>
                  <span className="font-sans text-sm truncate" style={{ color: palette.accent }}>{subLabel}</span>
                  {typeof subCount === 'number' && (
                    <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm shrink-0" style={{ color: palette.accent, background: palette.soft }}>{subCount}</span>
                  )}
                </span>
                <ChevronLeft className={`w-4 h-4 shrink-0 transition-transform ${subPickerOpen ? 'rotate-90' : '-rotate-90'}`} style={{ color: palette.accent }} />
              </button>
              {/* Expanded grid — same symmetric layout as before. */}
              {subPickerOpen && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5 mt-2" data-testid={`subcategory-pills-${activeCategory}`}>
                  {chip('all', 'All')}
                  {availableSubs(activeCategory).map(sub => chip(sub.name, sub.name, sub.count))}
                </div>
              )}
              {/* Hidden native select kept for backwards-compatible test selectors */}
              <select
                value={sel}
                onChange={(e) => setActiveSub(prev => ({ ...prev, [activeCategory]: e.target.value }))}
                className="sr-only"
                tabIndex={-1}
                aria-hidden
                data-testid={`subcategory-select-${activeCategory}`}
              >
                <option value="all">All</option>
                {availableSubs(activeCategory).map(sub => (
                  <option key={sub.name} value={sub.name}>{sub.name} ({sub.count})</option>
                ))}
              </select>
            </div>
          );
        })()}
        {/* ActiveOrderBanner LIVES INSIDE the fixed header so the ResizeObserver
            naturally adjusts body paddingTop to accommodate it. Putting it
            below the header (as a static block) caused it to be partially
            hidden behind the subcategory dropdown on Drinks/Food. Keeping it
            here means the customer ALWAYS sees their current order at a
            glance — even if they scroll past the menu. */}
        <ActiveOrderBanner orders={activeMyOrders} onOpenCart={() => setCartOpen(true)} />
      </header>

      {/* Failed-payment banner — appears when the customer's most recent card
          checkout was abandoned or voided by the reconciler. Pure UX nudge. */}
      <FailedPaymentBanner attempt={lastFailedAttempt} onDismiss={dismissFailedAttempt} />

      {/* Cash discount banner removed Feb 2026 per owner request — the
          10% off promo is still surfaced inside the cart drawer's
          payment-method picker, where it has more contextual impact. */}

      {/* Category Navigation */}
      {activeCategory === 'all' && !searchQuery.trim() ? (
        /* ============================================================
           Cinematic Menu Home — pure visual redesign.
           Identical handlers, identical data-testids. Just dressed up.
           ============================================================ */
        <div className="relative px-5 pt-5 pb-8 space-y-6 overflow-hidden" data-testid="menu-home">
          {/* Scoped animations + ambient backdrop styles. Keeping styles local
              avoids leaking keyframes globally and keeps the redesign self-contained. */}
          <style>{`
            @keyframes majnuFadeUp { 0% { opacity: 0; transform: translateY(14px); } 100% { opacity: 1; transform: translateY(0); } }
            @keyframes majnuDrift { 0%,100% { transform: translate(0,0); } 50% { transform: translate(20px,-18px); } }
            @keyframes majnuDriftAlt { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-22px,16px); } }
            @keyframes majnuShimmer { 0% { transform: translateX(-110%); } 100% { transform: translateX(110%); } }
            @keyframes majnuPulseGlow { 0%,100% { opacity: 0.45; } 50% { opacity: 0.85; } }
            @keyframes majnuSmokeRise { 0% { opacity: 0; transform: translateY(20px) scale(0.95); } 100% { opacity: 0.35; transform: translateY(0) scale(1); } }
            .majnu-fade { animation: majnuFadeUp 700ms ease-out both; }
            .majnu-grain { background-image: radial-gradient(rgba(255,255,255,0.018) 1px, transparent 1px); background-size: 3px 3px; }
            .majnu-card-shimmer::after {
              content: ""; position: absolute; inset: 0; pointer-events: none;
              background: linear-gradient(115deg, transparent 30%, rgba(199,135,31,0.10) 50%, transparent 70%);
              transform: translateX(-110%);
            }
            .majnu-card:hover .majnu-card-shimmer::after { animation: majnuShimmer 1200ms ease-out; }
            .majnu-card { transition: transform 500ms cubic-bezier(.2,.8,.2,1), border-color 400ms ease, box-shadow 400ms ease; }
            .majnu-card:hover { transform: translateY(-2px); }
            .majnu-card:active { transform: translateY(0) scale(0.995); }
            .majnu-hairline { background: linear-gradient(90deg, transparent, rgba(199,135,31,0.45), transparent); }
            .majnu-chip { transition: all 250ms ease; }
            .majnu-chip:hover { transform: translateY(-1px); }
            .no-scrollbar::-webkit-scrollbar { display: none; }
            .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          `}</style>

          {/* Ambient drifting orbs — gold + blue, deeply blurred. Sit behind everything. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-0">
            <div
              className="absolute -top-24 -left-16 w-[420px] h-[420px] rounded-full opacity-[0.18]"
              style={{ background: 'radial-gradient(closest-side, rgba(199,135,31,0.55), transparent 70%)', filter: 'blur(60px)', animation: 'majnuDrift 18s ease-in-out infinite' }}
            />
            <div
              className="absolute top-1/2 -right-20 w-[360px] h-[360px] rounded-full opacity-[0.14]"
              style={{ background: 'radial-gradient(closest-side, rgba(59,130,246,0.55), transparent 70%)', filter: 'blur(70px)', animation: 'majnuDriftAlt 22s ease-in-out infinite' }}
            />
            <div className="absolute inset-0 majnu-grain opacity-60" />
          </div>

          {/* ---------- Welcome marquee ---------- */}
          <section className="relative z-10 majnu-fade text-center pt-2 pb-1" style={{ animationDelay: '40ms' }}>
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="h-px w-10 majnu-hairline" />
              <span className="text-[10px] uppercase tracking-[0.38em] text-gold/80 font-sans">Tonight at Majnu</span>
              <span className="h-px w-10 majnu-hairline" />
            </div>
            <h1 className="font-serif text-white text-[28px] sm:text-[34px] leading-tight tracking-tight">
              <span className="block">An Evening,</span>
              <span className="block italic text-gold/90">Crafted.</span>
            </h1>
            <p className="mt-2 text-[12px] text-zinc-400 font-sans max-w-xs mx-auto leading-relaxed">
              Slow-poured spirits. Smoke that lingers. Plates from the tandoor. Browse the room.
            </p>
          </section>

          {/* ---------- Welcome-back re-order strip ---------- */}
          {/*
              Surfaces a guest's last 3 distinct items so a regular can
              one-tap re-order. Pulls from GET /api/guest-history/{id}
              which dedupes by item_id and drops items no longer on the
              active menu, so we never advertise something that 404s on
              Add. Hidden in staff mode AND when a tab is already open
              (post-arrival the order strip is in the way — they're
              already deep in their session).
          */}
          {!staffOrderMode && !tab && guestId && (
            <WelcomeBackStrip guestId={guestId} menu={menu} onQuickAdd={quickAdd} />
          )}

          {/* ---------- No-deposit trust ribbon ---------- */}
          {/*
              Slim reassurance for first-time scanners (Feb 2026): the
              old flow asked for a $30 deposit at tab-open, and word of
              that lingers. Surfacing the new policy at the top of the
              menu removes the implicit friction without dominating the
              landing layout. Hidden in:
                - Staff iframe mode (`?staff=1`) — bartender doesn't need
                  the customer copy.
                - Once a tab is already open — message becomes irrelevant.
              Single dismissible ribbon, remembered via localStorage so
              return customers don't see it on every visit.
          */}
          <NoDepositRibbon visible={!staffOrderMode && !tab} />

          {/* ---------- Favourites (logged-in only) — premium gold-accent shelf ---------- */}
          {isCustomer && favourites.length > 0 && (
            <section className="relative z-10 majnu-fade" style={{ animationDelay: '120ms' }} data-testid="favourites-row">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold/15 border border-gold/30">
                    <Star className="w-2.5 h-2.5 text-gold" />
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-gold/80 font-sans font-bold">Your Usuals</p>
                </div>
                <span className="text-[9px] text-zinc-600 font-sans uppercase tracking-[0.2em]">Tap to add</span>
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1">
                {favourites.map(item => (
                  <button
                    key={item.id}
                    onClick={() => quickAdd(item)}
                    className="majnu-chip shrink-0 min-w-[180px] bg-gradient-to-br from-gold/10 to-transparent border border-gold/20 rounded-sm px-3 py-2.5 text-left hover:border-gold/50 hover:from-gold/15"
                    data-testid={`favourite-${item.id}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-sm bg-black/40 border border-gold/20 flex items-center justify-center shrink-0">
                        {item.category === 'shisha' ? <Flame className="w-3.5 h-3.5 text-gold" /> :
                         item.category === 'drinks' ? <GlassWater className="w-3.5 h-3.5 text-gold" /> :
                         item.category === 'activities' ? <Target className="w-3.5 h-3.5 text-gold" /> :
                         <UtensilsCrossed className="w-3.5 h-3.5 text-gold" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-white font-sans font-medium leading-tight truncate">{item.name}</p>
                        <p className="text-[10px] text-zinc-500 font-sans leading-tight mt-0.5">
                          <span className="text-gold/80 font-mono">${(item.variants?.[0]?.price || item.price).toFixed(2)}</span>
                          <span className="text-zinc-700 mx-1.5">·</span>
                          <span>{item.order_count}× ordered</span>
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* ---------- Section label ---------- */}
          <section className="relative z-10 majnu-fade flex items-center gap-3" style={{ animationDelay: '180ms' }}>
            <span className="h-px flex-1 majnu-hairline" />
            <span className="text-[10px] uppercase tracking-[0.38em] text-zinc-500 font-sans">The Menu</span>
            <span className="h-px flex-1 majnu-hairline" />
          </section>

          {/* Kitchen closed 12am→5pm — landing-hub notice */}
          {!kitchenOpen && !staffOrderMode && (
            <section className="relative z-10 majnu-fade" style={{ animationDelay: '200ms' }}>
              <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-3.5 py-3" data-testid="kitchen-closed-banner">
                <p className="text-amber-300 text-sm font-sans font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> Kitchen closed
                </p>
                <p className="text-amber-200/70 text-xs font-sans mt-1">Food ordering is unavailable between 12:00am and 5:00pm. You can still order drinks{isTakeawayMode ? '' : ' & shisha'}.</p>
              </div>
            </section>
          )}
          {/* Last call 23:30→00:00 — live countdown to kitchen close */}
          {kitchenOpen && lastCallLeft > 0 && (
            <section className="relative z-10 majnu-fade" style={{ animationDelay: '200ms' }}>
              <div className="rounded-sm border border-red-500/40 bg-red-500/10 px-3.5 py-3" data-testid="last-call-banner">
                <p className="text-red-300 text-sm font-sans font-semibold flex items-center gap-2">
                  <Flame className="w-4 h-4 shrink-0 animate-pulse" /> Last call — kitchen closes at midnight
                </p>
                <p className="text-red-200/75 text-xs font-sans mt-1">Food ordering ends in <span className="font-mono font-bold text-red-200" data-testid="last-call-countdown">{lastCallClock}</span> — get your last orders in now.</p>
              </div>
            </section>
          )}

          {/* ---------- Cinematic category cards ---------- */}
          <section className="relative z-10 space-y-4">
            {/* Manager-only "Custom Product" quick-launch card — pinned to
                the top of the menu overview so managers can drop ad-hoc
                charges (damages, off-menu items, manual fees) in one tap.
                Customers never see this; `canAddCustomProduct` is gated on
                staffOrderMode + role and the backend 403s any non-manager
                attempt. */}
            {canAddCustomProduct && (
              <button
                type="button"
                onClick={() => { setCustomProductDraft({ name: '', price: '', qty: 1, notes: '' }); setCustomProductOpen(true); }}
                className="majnu-card majnu-card-shimmer relative overflow-hidden rounded-md border-2 border-amber-500/60 bg-gradient-to-br from-amber-500/20 via-amber-500/10 to-black w-full text-left p-5 flex items-center gap-4 hover:from-amber-500/30 hover:via-amber-500/15 transition-colors majnu-fade"
                style={{ animationDelay: '120ms', boxShadow: '0 30px 60px -30px rgba(245,158,11,0.4)' }}
                data-testid="open-custom-product-card"
              >
                <div className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
                  <Plus className="w-6 h-6 text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[9px] uppercase tracking-[0.2em] text-amber-300 font-bold bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 rounded-sm">Manager Only</span>
                  </div>
                  <p className="font-serif text-white text-xl leading-tight">Custom Product</p>
                  <p className="text-amber-200/80 font-sans text-[11px] mt-1">Ad-hoc charge · damages, off-menu, manual fee</p>
                </div>
                <ArrowRight className="w-5 h-5 text-amber-300 shrink-0" />
              </button>
            )}
            {[
              { key: 'drinks', label: 'Drinks', italic: 'Liquid Architecture',
                subtitle: 'Cocktails · Whiskey · Wines · Mocktails',
                Icon: GlassWater, accent: 'rgba(59,130,246,0.85)', accentSoft: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.32)',
                hide: false, delay: 240 },
              { key: 'food', label: 'Food', italic: 'From the Tandoor',
                subtitle: 'Bar Snacks · Tandoor Tadka · Street Mix',
                Icon: UtensilsCrossed, accent: 'rgba(245,158,11,0.85)', accentSoft: 'rgba(245,158,11,0.15)', borderColor: 'rgba(245,158,11,0.32)',
                hide: false, delay: 320 },
              { key: 'shisha', label: 'Shisha', italic: 'Slow Smoke',
                subtitle: 'Standard · Premium Darkside · Custom Mix',
                Icon: Flame, accent: 'rgba(167,139,250,0.9)', accentSoft: 'rgba(167,139,250,0.16)', borderColor: 'rgba(167,139,250,0.36)',
                hide: isTakeawayMode, delay: 400 },
              { key: 'activities', label: 'Activities', italic: 'Tables & Games',
                subtitle: 'Snooker · Pool · Foosball · Darts',
                Icon: Target, accent: 'rgba(16,185,129,0.9)', accentSoft: 'rgba(16,185,129,0.16)', borderColor: 'rgba(16,185,129,0.32)',
                hide: isTakeawayMode, delay: 480 },
            ].filter(c => !c.hide).map(c => {
              const subs = availableSubs(c.key);
              const count = totalItemsInCategory(c.key);
              const hero = categoryHero(c.key);
              const Icon = c.Icon;
              return (
                <div
                  key={c.key}
                  className="majnu-card majnu-card-shimmer relative overflow-hidden rounded-md border bg-black/40 backdrop-blur-[2px] majnu-fade"
                  style={{ borderColor: c.borderColor, animationDelay: `${c.delay}ms`, boxShadow: '0 30px 60px -30px rgba(0,0,0,0.65)' }}
                >
                  {/* Decorative layer — always renders the category's accent
                      gradient + smoke wisp so every card has the same
                      premium look (Activities has no hero image, but still
                      gets the emerald identity treatment). The hero photo
                      is layered in only when available. */}
                  <div className="absolute inset-0" aria-hidden>
                    {hero && (
                      <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.28]" loading="lazy" />
                    )}
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.55) 45%, ${c.accent.replace('0.85)', '0.32)').replace('0.9)', '0.36)')} 100%)` }} />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.55) 100%)' }} />
                    {/* Soft smoke wisp accent */}
                    <div
                      className="absolute -bottom-10 -right-8 w-56 h-56 rounded-full opacity-[0.28]"
                      style={{ background: `radial-gradient(closest-side, ${c.accent}, transparent 70%)`, filter: 'blur(40px)', animation: 'majnuPulseGlow 6s ease-in-out infinite' }}
                    />
                  </div>

                  {/* Primary tap target — exact same handler/testid as before */}
                  <button
                    onClick={() => { setActiveSub(p => ({ ...p, [c.key]: 'all' })); setActiveCategory(c.key); }}
                    className="relative w-full text-left p-5 sm:p-6 flex items-center gap-4"
                    data-testid={`section-${c.key}`}
                  >
                    <div
                      className="w-14 h-14 rounded-sm flex items-center justify-center shrink-0 border backdrop-blur-md"
                      style={{ background: `linear-gradient(135deg, ${c.accentSoft}, rgba(0,0,0,0.45))`, borderColor: c.borderColor }}
                    >
                      <Icon className="w-7 h-7" style={{ color: c.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <h2 className="font-serif text-white text-2xl leading-none">{c.label}</h2>
                        <span className="font-serif italic text-[13px] leading-none" style={{ color: c.accent }}>· {c.italic}</span>
                        {c.key === 'food' && !kitchenOpen && !staffOrderMode && (
                          <span className="text-[9px] uppercase tracking-[0.18em] font-sans font-bold text-amber-300 bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 rounded-sm" data-testid="food-card-kitchen-closed-chip">Kitchen closed · back 5pm</span>
                        )}
                      </div>
                      <p className="text-[11px] text-zinc-400 font-sans mt-1.5 truncate">{c.subtitle}</p>
                      {count > 0 && (
                        <p className="text-[10px] text-zinc-600 font-sans mt-1.5 uppercase tracking-[0.22em]">
                          {count} item{count === 1 ? '' : 's'} · Tap to browse
                        </p>
                      )}
                    </div>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center border shrink-0"
                      style={{ borderColor: c.borderColor, background: 'rgba(0,0,0,0.5)' }}
                    >
                      <ArrowRight className="w-4 h-4" style={{ color: c.accent }} />
                    </div>
                  </button>

                  {/* Subcategory pills — horizontal scroll, replaces the old select.
                      Keeps the existing data-testid on a hidden native select for any
                      automation that targets it, while presenting beautiful chips. */}
                  {subs.length > 0 && (
                    <div className="relative px-5 sm:px-6 pb-5">
                      <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-600 font-sans mb-2">Jump to</p>
                      <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
                        {subs.map(sub => (
                          <button
                            key={sub.name}
                            onClick={() => {
                              setActiveSub(p => ({ ...p, [c.key]: sub.name }));
                              setActiveCategory(c.key);
                              window.scrollTo(0, 0);
                            }}
                            className="majnu-chip shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-black/40 backdrop-blur-md hover:bg-black/60"
                            style={{ borderColor: c.borderColor }}
                            data-testid={`landing-subchip-${c.key}-${sub.name.toLowerCase().replace(/\s+/g,'-')}`}
                          >
                            <span className="text-[11px] font-sans text-white whitespace-nowrap">{sub.name}</span>
                            <span className="text-[9px] font-mono px-1 rounded-sm" style={{ color: c.accent, background: c.accentSoft }}>{sub.count}</span>
                          </button>
                        ))}
                      </div>
                      {/* Preserve the original native select for backwards-compatible test selectors */}
                      <select
                        value="placeholder"
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === 'placeholder') return;
                          setActiveSub(p => ({ ...p, [c.key]: v }));
                          setActiveCategory(c.key);
                          window.scrollTo(0, 0);
                        }}
                        className="sr-only"
                        tabIndex={-1}
                        aria-hidden
                        data-testid={`landing-subcategory-select-${c.key}`}
                      >
                        <option value="placeholder">Choose Category</option>
                        {subs.map(sub => (
                          <option key={sub.name} value={sub.name}>{sub.name} ({sub.count})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* ---------- Footer signature ---------- */}
          <section className="relative z-10 majnu-fade pt-4 pb-2 text-center" style={{ animationDelay: '500ms' }}>
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="h-px w-8 majnu-hairline" />
              <Flame className="w-3.5 h-3.5 text-gold/70" />
              <span className="h-px w-8 majnu-hairline" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-zinc-600 font-sans">
              Crafted in Melbourne · Open Late
            </p>
          </section>
        </div>
      ) : (
        <>
          {/* Menu Items */}
          <div className="relative z-10 px-5 pb-6 space-y-6 mt-2">
            {searchQuery.trim() && (
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans">
                {sections.length === 0
                  ? `No matches for "${searchQuery.trim()}"`
                  : `Search results for "${searchQuery.trim()}"`}
              </div>
            )}
            {sections.length === 0 && searchQuery.trim() && (
              <div className="text-center py-8">
                <p className="text-zinc-400 font-sans text-sm">Try a different keyword, or browse a category below.</p>
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="mt-3 text-gold text-xs font-sans uppercase tracking-[0.2em]"
                >
                  Clear search
                </button>
              </div>
            )}
            {!kitchenOpen && !staffOrderMode && (
              <div className="mb-4 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3.5 py-3" data-testid="kitchen-closed-banner">
                <p className="text-amber-300 text-sm font-sans font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> Kitchen closed
                </p>
                <p className="text-amber-200/70 text-xs font-sans mt-1">Food ordering is unavailable between 12:00am and 5:00pm. You can still order drinks & shisha.</p>
              </div>
            )}
            {kitchenOpen && lastCallLeft > 0 && (
              <div className="mb-4 rounded-sm border border-red-500/40 bg-red-500/10 px-3.5 py-3" data-testid="last-call-banner">
                <p className="text-red-300 text-sm font-sans font-semibold flex items-center gap-2">
                  <Flame className="w-4 h-4 shrink-0 animate-pulse" /> Last call — kitchen closes at midnight
                </p>
                <p className="text-red-200/75 text-xs font-sans mt-1">Food ordering ends in <span className="font-mono font-bold text-red-200" data-testid="last-call-countdown">{lastCallClock}</span> — get your last orders in now.</p>
              </div>
            )}
            {sections.map((section, si) => (
              <div key={`section-${section.category || si}`} className={section.category === 'food' && !kitchenOpen && !staffOrderMode ? 'opacity-55' : undefined}>
                {/* My Usuals — one-tap quick-add for combos the customer has
                    ordered 3+ times. Pinned at the top of the Drinks section
                    (any subcategory) so spirits regulars don't have to retap
                    the 4-group add-on flow on every visit. */}
                {section.category === 'drinks' && usuals.length > 0 && (() => {
                  const visible = usuals.filter(u => u.category === 'drinks');
                  if (visible.length === 0) return null;
                  return (
                    <div className="mb-5" data-testid="my-usuals-row">
                      <div className="flex items-baseline justify-between mb-2 px-0.5">
                        <h3 className="font-serif text-white text-base flex items-center gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-gold" />
                          My Usual
                          <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans ml-1">tap to re-order</span>
                        </h3>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-sans">{visible.length}</span>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
                        {visible.map(u => {
                          const addonSummary = (u.add_ons || []).map(a => a.name).join(' · ');
                          return (
                            <button
                              key={u.signature}
                              type="button"
                              onClick={() => addUsualToCart(u)}
                              className="snap-start shrink-0 w-[230px] text-left rounded-sm border border-gold/40 bg-gold/[0.06] hover:bg-gold/15 hover:border-gold/70 transition-all px-3 py-2.5 active:scale-[0.98]"
                              data-testid={`my-usual-card-${u.signature}`}
                            >
                              <div className="flex items-baseline justify-between gap-2 mb-1">
                                <span className="font-serif text-white text-sm leading-tight truncate">{u.name}</span>
                                <span className="font-mono text-gold text-sm shrink-0">${Number(u.price || 0).toFixed(2)}</span>
                              </div>
                              {addonSummary && (
                                <p className="text-[10.5px] text-zinc-400 font-sans leading-snug truncate">{addonSummary}</p>
                              )}
                              <div className="flex items-center justify-between mt-1.5">
                                <span className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-sans">Ordered {u.count}×</span>
                                <span className="text-[10px] font-sans text-gold flex items-center gap-1">
                                  <Plus className="w-3 h-3" />Add
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {/* Custom Mix CTA — owner ask Feb 2026: "make this look like
                    a button, not a banner". Pinned at the top of any
                    visible Shisha section. Tighter dimensions, solid
                    bordered chrome (no ambient glow washes), and a clear
                    button-style "BUILD →" pill on the right so the
                    affordance reads instantly as tappable. */}
                {section.category === 'shisha' && customMixItem && (
                  <button
                    type="button"
                    onClick={() => quickAdd(customMixItem)}
                    className="group relative w-full rounded-lg mb-5 border-2 border-gold/60 bg-gold/[0.07] hover:bg-gold/[0.12] hover:border-gold active:scale-[0.985] transition-all duration-150 shadow-[0_2px_0_rgba(212,175,55,0.25),0_8px_18px_-8px_rgba(0,0,0,0.6)]"
                    data-testid="custom-mix-hero-button"
                  >
                    <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
                      {/* Compact icon chip */}
                      <span className="shrink-0 w-10 h-10 rounded-md bg-gold text-black flex items-center justify-center shadow-sm">
                        <Wand2 className="w-5 h-5" />
                      </span>
                      {/* Title + sub */}
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-serif text-white text-sm sm:text-base leading-tight truncate">Create Your Own Mix</h3>
                          <span className="shrink-0 text-[8px] uppercase tracking-[0.16em] text-gold font-sans font-bold bg-gold/15 border border-gold/40 px-1.5 py-[1px] rounded-sm">Signature</span>
                        </div>
                        <p className="text-zinc-400 font-sans text-[11px] sm:text-xs mt-0.5 truncate">
                          Blend 2–3 premium flavours · one flat price
                        </p>
                      </div>
                      {/* Right-side: price stack + button-shaped CTA pill */}
                      <div className="shrink-0 flex items-center gap-2.5">
                        <span className="text-gold font-sans font-bold text-base sm:text-lg">${Number(customMixItem.price || 0).toFixed(0)}</span>
                        <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gold text-black text-[10px] uppercase tracking-[0.14em] font-sans font-bold group-hover:bg-gold-light transition-colors">
                          Build <ArrowRight className="w-3 h-3" />
                        </span>
                        <span className="sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-md bg-gold text-black group-hover:bg-gold-light transition-colors">
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </button>
                )}
                <div className="space-y-6">
                  {Object.entries(section.subcategories).map(([subName, items]) => {
                    const isShisha = items[0]?.category === 'shisha';
                    const isUncat = subName === UNCAT_KEY;

                    if (isShisha) {
                      // Inside-page section header mirrors the landing-page
                      // category-card aesthetic the user already loves:
                      // compact bordered icon block, serif label, italic
                      // accent word, hairline gold rule, "N flavours" caption.
                      // No heavy ambient washes — just clean tasting-room
                      // typography against the regular bg-primary.
                      const price = items[0]?.price || 0;
                      const isPremium = items[0]?.is_premium;
                      const isDarkside = subName === 'Premium Darkside';
                      const accent = 'rgba(167,139,250,0.9)';
                      const accentSoft = 'rgba(167,139,250,0.16)';
                      const borderColor = 'rgba(167,139,250,0.36)';
                      // Italic accent word per shisha subgroup so each section
                      // reads like a chapter heading rather than a label.
                      const italics = { 'Standard': 'House Blends', 'Premium': 'Reserve Selection', 'Premium Darkside': 'Curated Catalogue', 'Celebrate': 'For the Occasion', 'Custom': 'Build Your Own' };
                      const italic = italics[subName] || null;
                      return (
                        <section key={subName} className="relative">
                          {!isUncat && (
                          <div className="sticky -mx-5 px-5 py-3 bg-bg-primary/85 backdrop-blur-md z-30 mb-3" style={{ top: `${headerH}px` }}>
                            <div className="flex items-center gap-3 rounded-md border px-3.5 py-2.5 bg-black/40 backdrop-blur-[2px]" style={{ borderColor }}>
                              <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 border" style={{ background: `linear-gradient(135deg, ${accentSoft}, rgba(0,0,0,0.45))`, borderColor }}>
                                <Flame className="w-4 h-4" style={{ color: accent }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <h3 className="font-serif text-white text-base leading-none">{subName}</h3>
                                  {italic && <span className="font-serif italic text-[12px] leading-none" style={{ color: accent }}>· {italic}</span>}
                                </div>
                                <p className="text-[10px] text-zinc-500 font-sans mt-1.5 uppercase tracking-[0.22em]">{items.length} flavour{items.length === 1 ? '' : 's'}{isPremium ? ' · Premium' : ''}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="block font-serif text-gold text-base leading-none">${price.toFixed(0)}</span>
                                <span className="block text-[9px] uppercase tracking-[0.22em] text-zinc-600 font-sans mt-1.5">per head</span>
                              </div>
                            </div>
                          </div>
                          )}
                          {isDarkside ? (
                            <div className="grid grid-cols-1 gap-2 pb-4">
                              {items.map(item => {
                                const inCart = cart.filter(c => c.id === item.id);
                                const inCartQty = inCart.reduce((s, c) => s + c.qty, 0);
                                return (
                                  <div
                                    key={item.id}
                                    className={`relative rounded-sm transition-all border ${inCartQty > 0 ? 'border-gold/40 bg-gold/[0.06]' : 'border-white/10 bg-white/[0.02] hover:border-white/20'}`}
                                    data-testid={`menu-item-${item.id}`}
                                  >
                                    <button
                                      onClick={() => quickAdd(item)}
                                      className="block w-full text-left px-3.5 py-3 active:scale-[0.99] transition-transform"
                                      data-testid={`add-to-cart-${item.id}`}
                                    >
                                      <div className="flex items-baseline justify-between gap-3 mb-1">
                                        <h4 className={`font-serif text-base leading-tight ${inCartQty > 0 ? 'text-gold' : 'text-white'}`}>{item.name}</h4>
                                        {inCartQty > 0 && (
                                          <span className="text-[10px] uppercase tracking-[0.16em] font-sans text-gold shrink-0" data-testid={`cart-qty-badge-${item.id}`}>×{inCartQty}</span>
                                        )}
                                      </div>
                                      {item.description && (
                                        <p className="text-zinc-500 text-xs font-sans leading-relaxed">{item.description}</p>
                                      )}
                                    </button>
                                    {inCartQty > 0 && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); quickRemove(item); }}
                                        className="absolute top-2 right-2 w-7 h-7 rounded-sm bg-bg-secondary border border-gold/30 text-gold flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/40 transition-colors"
                                        aria-label={`Remove one ${item.name}`}
                                        data-testid={`remove-from-cart-${item.id}`}
                                      >
                                        <Minus className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                          <div className="grid grid-cols-3 gap-2 pb-4">
                            {items.map(item => {
                              const inCart = cart.filter(c => c.id === item.id);
                              const inCartQty = inCart.reduce((s, c) => s + c.qty, 0);
                              return (
                                <div
                                  key={item.id}
                                  className={`relative rounded-sm transition-all ${inCartQty > 0 ? 'bg-gold/15 text-gold border border-gold/30' : 'bg-white/[0.03] text-zinc-200 border border-white/10 hover:border-white/20'}`}
                                >
                                  <button
                                    onClick={() => quickAdd(item)}
                                    className={`block w-full px-2 ${inCartQty > 0 ? 'pt-6 pb-3' : 'py-3'} text-xs font-sans text-center truncate hover:text-gold active:scale-95 transition-colors`}
                                    data-testid={`add-to-cart-${item.id}`}
                                  >
                                    {item.name}
                                  </button>
                                  {inCartQty > 0 && (
                                    <>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); quickAdd(item); }}
                                        className="absolute top-0 left-0 w-6 h-6 rounded-tl-sm rounded-br-sm bg-bg-secondary border-r border-b border-gold/40 text-gold flex items-center justify-center hover:bg-gold/30"
                                        aria-label={`Add one ${item.name}`}
                                        data-testid={`add-one-${item.id}`}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                      <span
                                        className="absolute top-1 left-1/2 -translate-x-1/2 min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-black text-[11px] font-sans font-bold flex items-center justify-center shadow-sm pointer-events-none"
                                        data-testid={`cart-qty-badge-${item.id}`}
                                      >
                                        {inCartQty}
                                      </span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); quickRemove(item); }}
                                        className="absolute top-0 right-0 w-6 h-6 rounded-bl-sm rounded-tr-sm bg-bg-secondary border-l border-b border-gold/40 text-gold flex items-center justify-center hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/40"
                                        aria-label={`Remove one ${item.name}`}
                                        data-testid={`remove-from-cart-${item.id}`}
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          )}
                        </section>
                      );
                    }

                    // Inside-page section header for food / drinks / activities.
                    // Mirrors the landing-page card aesthetic so the menu
                    // reads as one continuous lounge experience: compact
                    // bordered icon block, serif label, italic accent word,
                    // and a short caption — no heavy ambient washes.
                    const cat = items[0]?.category;
                    const accent = (() => {
                      if (cat === 'drinks') return { icon: GlassWater, hex: 'rgba(59,130,246,0.85)', soft: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.32)' };
                      if (cat === 'food') return { icon: Drumstick, hex: 'rgba(245,158,11,0.85)', soft: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.32)' };
                      if (cat === 'activities') return { icon: Target, hex: 'rgba(16,185,129,0.9)', soft: 'rgba(16,185,129,0.16)', border: 'rgba(16,185,129,0.32)' };
                      return { icon: Sparkles, hex: 'rgba(212,175,55,0.9)', soft: 'rgba(212,175,55,0.15)', border: 'rgba(212,175,55,0.32)' };
                    })();
                    const AccentIcon = accent.icon;
                    return (
                    <section key={subName} className="relative">
                      {!isUncat && (
                      <div className="sticky -mx-5 px-5 py-3 bg-bg-primary/85 backdrop-blur-md z-30 mb-3" style={{ top: `${headerH}px` }}>
                        <div className="flex items-center gap-3 rounded-md border px-3.5 py-2.5 bg-black/40 backdrop-blur-[2px]" style={{ borderColor: accent.border }}>
                          <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 border" style={{ background: `linear-gradient(135deg, ${accent.soft}, rgba(0,0,0,0.45))`, borderColor: accent.border }}>
                            <AccentIcon className="w-4 h-4" style={{ color: accent.hex }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-serif text-white text-base leading-none truncate">{subName}</h3>
                            <p className="text-[10px] text-zinc-500 font-sans mt-1.5 uppercase tracking-[0.22em]">{items.length} {items.length === 1 ? 'pick' : 'picks'}</p>
                          </div>
                        </div>
                      </div>
                      )}
                      <div className="grid grid-cols-1 gap-3 pt-1">
                        {items.map(item => {
                          const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
                          const activeVariantLabel = hasVariants ? (selectedVariants[item.id] || item.variants[0].label) : null;
                          const activeVariant = hasVariants ? item.variants.find(v => v.label === activeVariantLabel) : null;
                          const displayPrice = activeVariant ? Number(activeVariant.price) : item.price;
                          const inCart = cart.filter(c => c.id === item.id);
                          const inCartQty = inCart.reduce((s, c) => s + c.qty, 0);
                          return (
                            <div key={item.id} className={`card-hover bg-white/[0.03] border rounded-sm overflow-hidden ${item.is_custom_charge ? 'border-amber-500/60 bg-gradient-to-br from-amber-500/10 to-transparent' : item.is_custom_mix ? 'border-gold/60 bg-gradient-to-br from-gold/5 to-transparent' : item.is_premium ? 'border-gold/20' : 'border-white/5'}`} data-testid={`menu-item-${item.id}`}>
                              <div className="flex gap-3 p-3">
                                {/* Square thumbnail on the left (kiosk-style density). Shisha intentionally
                                    has no thumbnail — it's a flavour-driven decision, not visual. */}
                                {item.category !== 'shisha' && getImageUrl(item.image_path) && (
                                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-sm overflow-hidden shrink-0 bg-bg-secondary">
                                    <img src={getImageUrl(item.image_path)} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <h3 className={`font-sans font-medium text-sm ${item.is_custom_charge ? 'text-amber-300' : item.is_custom_mix ? 'text-gold' : 'text-white'}`}>{item.name}</h3>
                                    {item.is_custom_charge && <span className="text-[10px] uppercase tracking-[0.15em] text-amber-300 font-bold bg-amber-500/10 border border-amber-500/40 px-1.5 py-0.5 rounded-sm">Manager Only</span>}
                                    {item.is_custom_mix && <span className="text-[10px] uppercase tracking-[0.15em] text-gold font-bold bg-gold/10 border border-gold/30 px-1.5 py-0.5 rounded-sm">Build Mix</span>}
                                    {item.is_premium && !item.is_custom_mix && <Sparkles className="w-3 h-3 text-gold" />}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {(item.tags || []).map(tag => {
                                      const s = tagStyles[tag];
                                      if (!s) return null;
                                      return <span key={tag} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-sans ${s.cls}`}>{s.icon && <s.icon className="w-2.5 h-2.5" />}{s.label}</span>;
                                    })}
                                    {item.flavor_profile && item.category === 'shisha' && <span className="text-[10px] text-zinc-500 font-sans">{flavorIcons[item.flavor_profile]} {item.flavor_profile}</span>}
                                  </div>
                                </div>
                                <span className="text-gold font-sans font-semibold ml-2">${displayPrice.toFixed(2)}</span>
                              </div>
                              <p className="text-zinc-500 text-xs font-sans mb-2 line-clamp-2">{item.description}</p>
                              {hasVariants && (
                                <div className="flex gap-1.5 mb-3" role="group" aria-label={`${item.name} size options`}>
                                  {item.variants.map(v => {
                                    const isActive = v.label === activeVariantLabel;
                                    return (
                                      <button
                                        key={v.label}
                                        type="button"
                                        onClick={() => setSelectedVariants(prev => ({ ...prev, [item.id]: v.label }))}
                                        className={`flex-1 text-[11px] font-sans uppercase tracking-wider rounded-sm py-1.5 transition-colors border ${isActive ? 'border-gold/60 bg-gold/15 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200'}`}
                                        data-testid={`variant-${item.id}-${v.label.toLowerCase()}`}
                                      >
                                        {v.label} <span className="ml-1 opacity-70">${Number(v.price).toFixed(2)}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              {item.category === 'food' && (
                                <div className="mb-3">
                                  <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1.5 flex items-center gap-1"><Flame className="w-2.5 h-2.5" /> Spice</p>
                                  <div className="flex gap-1.5" role="group" aria-label={`${item.name} spice level`}>
                                    {spiceLevels.map(lvl => {
                                      const activeSpice = selectedSpices[item.id] || 'Medium';
                                      const isActive = activeSpice === lvl;
                                      return (
                                        <button
                                          key={lvl}
                                          type="button"
                                          onClick={() => setSelectedSpices(prev => ({ ...prev, [item.id]: lvl }))}
                                          className={`flex-1 text-[11px] font-sans rounded-sm py-1.5 transition-colors border ${isActive ? SPICE_STYLES[lvl].active : SPICE_STYLES[lvl].inactive}`}
                                          data-testid={`card-spice-${item.id}-${lvl.toLowerCase()}`}
                                        >
                                          {lvl}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {item.category === 'food' && (
                                <div className="mb-3">
                                  {noteOpenFor === item.id || (instructionsDraft[item.id] && instructionsDraft[item.id].length > 0) ? (
                                    <>
                                      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1.5">Note for chef</p>
                                      <textarea
                                        value={instructionsDraft[item.id] || ''}
                                        onChange={(e) => setInstructionsDraft(prev => ({ ...prev, [item.id]: e.target.value.slice(0, 140) }))}
                                        placeholder="e.g. no onions, extra crispy, allergic to peanuts..."
                                        rows={2}
                                        className="w-full bg-transparent border border-white/10 text-white font-sans text-xs rounded-sm p-2 placeholder:text-zinc-600 focus:outline-none focus:border-gold/40 resize-none"
                                        data-testid={`card-note-${item.id}`}
                                      />
                                      <div className="flex justify-between items-center mt-1">
                                        <span className="text-[10px] text-zinc-600 font-sans">{(instructionsDraft[item.id] || '').length}/140</span>
                                        <button
                                          type="button"
                                          onClick={() => { setInstructionsDraft(prev => ({ ...prev, [item.id]: '' })); setNoteOpenFor(null); }}
                                          className="text-[10px] text-zinc-500 hover:text-zinc-300 font-sans"
                                          data-testid={`card-note-clear-${item.id}`}
                                        >
                                          Clear
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setNoteOpenFor(item.id)}
                                      className="text-[11px] font-sans text-zinc-500 hover:text-gold transition-colors flex items-center gap-1"
                                      data-testid={`card-note-add-${item.id}`}
                                    >
                                      <Plus className="w-3 h-3" /> Add note for chef
                                    </button>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center justify-end">
                                {inCartQty > 0 ? (
                                  <div className="flex items-center gap-1 bg-gold/10 border border-gold/30 rounded-sm" data-testid={`cart-stepper-${item.id}`}>
                                    <button
                                      onClick={() => quickRemove(item)}
                                      className="w-8 h-8 flex items-center justify-center text-gold hover:bg-red-500/15 hover:text-red-400 rounded-l-sm"
                                      aria-label="Remove one"
                                      data-testid={`remove-from-cart-${item.id}`}
                                    >
                                      <Minus className="w-3.5 h-3.5" />
                                    </button>
                                    <span className="text-gold text-xs font-sans font-semibold w-6 text-center">{inCartQty}</span>
                                    <button
                                      onClick={() => quickAdd(item)}
                                      className="w-8 h-8 flex items-center justify-center text-gold hover:bg-gold/20 rounded-r-sm"
                                      aria-label="Add one"
                                      data-testid={`add-to-cart-${item.id}`}
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <Button size="sm" onClick={() => quickAdd(item)} className="h-8 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 rounded-sm font-sans text-xs" data-testid={`add-to-cart-${item.id}`}>
                                    <Plus className="w-3 h-3 mr-1" /> Add
                                  </Button>
                                )}
                              </div>
                              </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Account Picker — appears only when multiple customer accounts share phone+password */}
      <Dialog open={!!signinPickerAccounts} onOpenChange={open => { if (!open) { setSigninPickerAccounts(null); setSigninPickLoading(null); } }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm" data-testid="account-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Choose your account</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500 font-sans -mt-1 mb-1">More than one account uses this phone number. Pick the profile you want to sign in to.</p>
          <div className="space-y-2 pt-2">
            {(signinPickerAccounts || []).map(acc => (
              <button
                key={acc.id}
                type="button"
                disabled={!!signinPickLoading}
                onClick={() => handleSigninPick(acc)}
                className="w-full flex items-center justify-between p-3 rounded-sm border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-gold/40 transition-colors text-left disabled:opacity-50 disabled:cursor-wait"
                data-testid={`pick-account-${acc.id}`}
              >
                <div className="min-w-0">
                  <p className="text-white font-sans text-sm truncate">{acc.name || `${acc.first_name || ''} ${acc.last_name || ''}`.trim() || 'Customer'}</p>
                  <p className="text-zinc-500 text-xs font-sans truncate">{acc.email}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gold shrink-0 ml-2" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize Dialog */}
      <Dialog open={!!customizeItem} onOpenChange={open => { if (!open) setCustomizeItem(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          {customizeItem && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-white">{customizeItem.name}{customizeVariant ? ` (${customizeVariant.label})` : ''}</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex justify-between"><span className="text-zinc-400 font-sans text-sm">Base price</span><span className="text-gold font-sans">${(customizeVariant ? Number(customizeVariant.price) : Number(customizeItem.price)).toFixed(2)}</span></div>
                {/* In-dialog variant picker (Single / Double / Bottle for spirits,
                    Glass / Bottle for wines). The card already lets the customer
                    pick one and locks it in via customizeVariant, but exposing it
                    here lets them change their mind without closing the dialog. */}
                {Array.isArray(customizeItem.variants) && customizeItem.variants.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Size</p>
                    <div className="grid grid-cols-3 gap-2">
                      {customizeItem.variants.map(v => {
                        const isActive = customizeVariant && customizeVariant.label === v.label;
                        return (
                          <button
                            key={v.label}
                            type="button"
                            onClick={() => { setCustomizeVariant(v); setSelectedVariants(prev => ({ ...prev, [customizeItem.id]: v.label })); }}
                            className={`flex flex-col items-start text-left px-2 py-1.5 rounded-sm border transition-all text-xs font-sans ${isActive ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                            data-testid={`customize-variant-${v.label.toLowerCase()}`}
                          >
                            <span className="font-medium uppercase tracking-wider text-[11px]">{v.label}</span>
                            <span className="text-[10px] opacity-80 mt-0.5">${Number(v.price).toFixed(2)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(customizeItem.has_spice_level || customizeItem.category === 'food') && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Spice Level</p>
                    <div className="grid grid-cols-3 gap-2">
                      {spiceLevels.map(lvl => (
                        <button key={lvl} onClick={() => setSelectedSpice(lvl)} className={`px-2 py-1.5 rounded-sm text-xs font-sans border transition-all ${selectedSpice === lvl ? SPICE_STYLES[lvl].active : SPICE_STYLES[lvl].inactive}`} data-testid={`spice-${lvl}`}>{lvl}</button>
                      ))}
                    </div>
                  </div>
                )}
                {customizeItem.head_options?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Choose your head</p>
                    <div className="grid grid-cols-2 gap-2">
                      {customizeItem.head_options.map((h) => {
                        const isActive = selectedHead && selectedHead.label === h.label;
                        // Fruit thumbnail emoji per head label — owner-approved
                        // visual cue so customers recognise the head at a
                        // glance without reading the label. These are product
                        // imagery, not UI icons.
                        const labelLower = (h.label || '').toLowerCase();
                        let fruitIcon = null;
                        // Order matters: check 'pineapple' before 'apple' since
                        // 'pineapple'.includes('apple') is true and would
                        // otherwise incorrectly assign the apple emoji.
                        if (labelLower.includes('pineapple')) fruitIcon = '🍍';
                        else if (labelLower.includes('apple')) fruitIcon = '🍎';
                        else if (labelLower.includes('orange')) fruitIcon = '🍊';
                        return (
                          <button
                            key={h.label}
                            type="button"
                            onClick={() => setSelectedHead(h)}
                            className={`flex flex-col items-start text-left px-3 py-2 rounded-sm border transition-all text-xs font-sans ${isActive ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                            data-testid={`head-${h.label.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <span className="font-medium flex items-center gap-1.5">
                              {fruitIcon && <span className="text-base leading-none" aria-hidden="true">{fruitIcon}</span>}
                              <span>{h.label}</span>
                            </span>
                            <span className="text-[11px] opacity-80 mt-0.5">{Number(h.price) > 0 ? `+ $${Number(h.price).toFixed(2)}` : 'Included'}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {customizeItem.add_ons?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Add-ons</p>
                    {customizeItem.add_ons.map((addon) => {
                      const selected = selectedAddOns.find(a => a.name === addon.name);
                      const isFree = Number(addon.price) === 0;
                      return (
                        <button key={addon.name} onClick={() => setSelectedAddOns(prev => selected ? prev.filter(a => a.name !== addon.name) : [...prev, addon])}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-sm border transition-all text-sm font-sans ${selected ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400'}`} data-testid={`addon-${addon.name}`}>
                          <span>{addon.name}</span>
                          {!isFree && <span>+${addon.price.toFixed(2)}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Universal grouped add-ons — used for the spirits prompts
                    (Serving Style / Ice / Garnish / Additional Requests).
                    Single-select groups behave like radios; multi-select
                    groups toggle the option in/out of an array. All groups
                    are optional — customer can skip and just Add to Cart. */}
                {customizeItem.addon_groups?.length > 0 && customizeItem.addon_groups.map((group) => {
                  const sel = groupSelections[group.id];
                  const isSingle = group.select_mode === 'single';
                  return (
                    <div key={group.id} className="space-y-2" data-testid={`addon-group-${group.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">{group.label}</p>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">
                          {isSingle ? 'Pick one · optional' : 'Pick any · optional'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {(group.options || []).map((opt) => {
                          const active = isSingle ? sel === opt.name : Array.isArray(sel) && sel.includes(opt.name);
                          return (
                            <button
                              key={opt.name}
                              type="button"
                              onClick={() => {
                                setGroupSelections(prev => {
                                  if (isSingle) {
                                    // Tap again to deselect (group is optional)
                                    return { ...prev, [group.id]: prev[group.id] === opt.name ? '' : opt.name };
                                  }
                                  const arr = Array.isArray(prev[group.id]) ? prev[group.id] : [];
                                  const next = arr.includes(opt.name) ? arr.filter(n => n !== opt.name) : [...arr, opt.name];
                                  return { ...prev, [group.id]: next };
                                });
                              }}
                              className={`flex flex-col items-start text-left px-3 py-2 rounded-sm border transition-all text-xs font-sans ${active ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                              data-testid={`addon-group-${group.id}-${opt.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <span className="font-medium">{opt.name}</span>
                              <span className="text-[10px] opacity-80 mt-0.5">
                                {Number(opt.price) > 0 ? `+ $${Number(opt.price).toFixed(2)}` : 'Free'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <Separator className="bg-white/5" />
                <div className="flex justify-between text-white font-sans font-semibold">
                  <span>Total</span>
                  <span className="text-gold">${((customizeVariant ? Number(customizeVariant.price) : Number(customizeItem.price)) + selectedAddOns.reduce((s, a) => s + Number(a.price || 0), 0) + flattenGroupSelections(customizeItem).reduce((s, a) => s + Number(a.price || 0), 0) + (selectedHead ? Number(selectedHead.price || 0) : 0)).toFixed(2)}</span>
                </div>
                <Button onClick={addToCartWithOptions} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="confirm-customize-button">Add to Cart</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Past + Current orders are NOT shown on the main menu page.
          Customers see their current order only inside the Cart sheet (above cart items). */}

      {/* Rewards */}
      {rewards.length > 0 && (
        <div className="px-4 pb-4">
          <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2"><Gift className="w-3 h-3 text-gold" /> My Rewards</h3>
          <div className="flex gap-2 overflow-x-auto pb-2">{rewards.map(r => <div key={r.id} className="flex-shrink-0 px-3 py-2 bg-gold/10 border border-gold/20 rounded-sm" data-testid={`reward-${r.id}`}><p className="text-gold font-sans text-xs font-semibold">{r.reward_name}</p></div>)}</div>
        </div>
      )}

      {/* Floating Cart */}
      {cartCount > 0 && !cartOpen && (
        <div className="fixed bottom-0 left-0 right-0 p-5 bg-bg-secondary border-t border-white/5 z-30">
          <Button onClick={() => setCartOpen(true)} className="w-full h-12 bg-gold text-black font-bold rounded-sm font-sans flex justify-between items-center px-6 hover:bg-gold-light" data-testid="floating-cart-button">
            <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> {cartCount} items</span><span>${cartTotal.toFixed(2)}</span>
          </Button>
        </div>
      )}

      {/* Spin Wheel */}
      {spinEligible && <button onClick={() => setShowSpin(true)} className="fixed bottom-20 right-4 z-30 w-12 h-12 rounded-full bg-gold text-black flex items-center justify-center shadow-lg shadow-gold/20 animate-bounce" data-testid="spin-trigger"><Gift className="w-5 h-5" /></button>}
      {showSpin && <SpinWheel onClose={() => { setShowSpin(false); fetchSpinEligibility(); fetchRewards(); }} />}
      {feedbackOrder && <FeedbackPrompt orderId={feedbackOrder} onClose={() => setFeedbackOrder(null)} />}

      {/* Profile Dialog */}
      <Dialog open={showProfile} onOpenChange={(v) => { setShowProfile(v); if (!v) setProfileEditMode(false); }}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">My Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2 max-h-[65vh] overflow-y-auto">
            {!profileEditMode ? (
              // ---- READ-ONLY VIEW ----
              <>
                <div className="space-y-3" data-testid="profile-view">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mb-1">First Name</p>
                      <p className="text-white text-sm font-sans" data-testid="profile-view-first-name">{profileForm.first_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mb-1">Last Name</p>
                      <p className="text-white text-sm font-sans" data-testid="profile-view-last-name">{profileForm.last_name || '—'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mb-1">Email</p>
                    <p className="text-white text-sm font-sans break-all" data-testid="profile-view-email">{profileForm.email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mb-1">Phone</p>
                    <p className="text-white text-sm font-sans" data-testid="profile-view-phone">{profileForm.phone || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans mb-1">Postcode</p>
                    <p className="text-white text-sm font-sans" data-testid="profile-view-postcode">{profileForm.postcode || '—'}</p>
                  </div>
                </div>
                <Button
                  onClick={() => setProfileEditMode(true)}
                  className="w-full bg-gold text-black font-bold rounded-sm font-sans text-sm hover:bg-gold-light"
                  data-testid="profile-edit-button"
                >
                  Edit
                </Button>
              </>
            ) : (
              // ---- EDIT FORM ----
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">First Name</Label>
                    <Input value={profileForm.first_name || ''} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-first-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Last Name</Label>
                    <Input value={profileForm.last_name || ''} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-last-name" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email</Label>
                  <Input type="email" value={profileForm.email || ''} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Phone</Label>
                  <Input value={profileForm.phone || ''} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Postcode</Label>
                  <Input value={profileForm.postcode || ''} onChange={e => setProfileForm(p => ({ ...p, postcode: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-postcode" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => setProfileEditMode(false)} variant="outline" className="border-white/10 text-zinc-300 hover:text-white rounded-sm font-sans text-sm" data-testid="profile-cancel-edit">Cancel</Button>
                  <Button onClick={handleProfileSave} disabled={profileLoading} className="bg-gold text-black font-bold rounded-sm font-sans text-sm hover:bg-gold-light" data-testid="save-profile-button">
                    {profileLoading ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Save Changes'}
                  </Button>
                </div>
              </>
            )}

            <Separator className="bg-white/5" />

            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold">Change Password</p>
              <Input type="password" placeholder="New password" value={passwordForm.new_password} onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-new-password" />
              <Input type="password" placeholder="Confirm password" value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans text-sm" data-testid="profile-confirm-password" />
              <Button onClick={handlePasswordChange} variant="outline" className="w-full border-white/10 text-zinc-300 hover:text-white hover:border-white/20 rounded-sm font-sans text-sm" data-testid="change-password-button">Change Password</Button>
            </div>

            <Separator className="bg-white/5" />

            <Button
              onClick={() => { setShowProfile(false); logout(); }}
              variant="outline"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/50 rounded-sm font-sans text-sm font-semibold"
              data-testid="profile-signout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===================== Customer Nav Sheet ===================== */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent className="bg-bg-secondary border-l border-white/5 w-72" data-testid="customer-nav-sheet">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl text-white">Account</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-1">
            <button
              onClick={() => openNavView('dashboard')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-zinc-300 hover:bg-white/5 hover:text-gold transition-colors font-sans text-sm"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </button>
            {isCustomer && (
              <button
                onClick={() => { setNavOpen(false); navigate('/book'); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-gold bg-gold/5 hover:bg-gold/10 transition-colors font-sans text-sm border border-gold/20"
                data-testid="nav-book-table"
              >
                <Calendar className="w-4 h-4" /> Book a Table
              </button>
            )}
            {/* Become Member CTA — opt-in only, surfaced for guests so they
                can choose membership without being interrupted by popups. */}
            {!isCustomer && (
              <button
                onClick={() => {
                  setNavOpen(false);
                  setMemberForm(p => ({
                    ...p,
                    first_name: p.first_name || (guestName || '').split(' ')[0] || '',
                    last_name: p.last_name || (guestName || '').split(' ').slice(1).join(' ') || '',
                  }));
                  setMemberDialogOpen(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-gold bg-gold/5 hover:bg-gold/10 transition-colors font-sans text-sm border border-gold/20"
                data-testid="nav-become-member"
              >
                <BadgeCheck className="w-4 h-4" /> Become a Member
              </button>
            )}
            <button
              onClick={() => openNavView('profile')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-zinc-300 hover:bg-white/5 hover:text-gold transition-colors font-sans text-sm"
              data-testid="nav-profile"
            >
              <User className="w-4 h-4" /> My Profile
            </button>
            <button
              onClick={() => openNavView('membership')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-zinc-300 hover:bg-white/5 hover:text-gold transition-colors font-sans text-sm"
              data-testid="nav-membership"
            >
              <BadgeCheck className="w-4 h-4" /> My Membership
            </button>
            <Separator className="my-3 bg-white/5" />
            <button
              onClick={() => { setNavOpen(false); logout(); }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-red-400 hover:bg-red-500/10 transition-colors font-sans text-sm"
              data-testid="nav-signout"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ===================== Dashboard Dialog ===================== */}
      <Dialog open={activeNavView === 'dashboard'} onOpenChange={(v) => !v && setActiveNavView(null)}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-gold" /> Today's Specials
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2" data-testid="customer-dashboard">
            {specialsLoading ? (
              <p className="text-zinc-500 text-sm font-sans">Loading…</p>
            ) : specials.length === 0 ? (
              <p className="text-zinc-500 text-sm font-sans py-6 text-center">No specials right now. Check back next week!</p>
            ) : (
              specials.map(s => (
                <div key={s.id} className="bg-white/[0.03] border border-emerald-500/20 rounded-sm overflow-hidden" data-testid={`special-${s.id}`}>
                  {s.image_path && (
                    <div className="w-full h-32 overflow-hidden">
                      <img src={getImageUrl(s.image_path)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-white text-base font-serif">{s.name}</h3>
                      <p className="text-gold font-sans font-semibold whitespace-nowrap">${Number(s.price).toFixed(2)}</p>
                    </div>
                    {s.special_note && (
                      <p className="mt-1.5 inline-block text-emerald-300 text-[11px] font-sans bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-2 py-0.5" data-testid={`special-note-${s.id}`}>
                        {s.special_note}
                      </p>
                    )}
                    {s.description && (
                      <p className="mt-2 text-zinc-500 text-xs font-sans">{s.description}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ===================== Membership Dialog (placeholder) ===================== */}
      <Dialog open={activeNavView === 'membership'} onOpenChange={(v) => !v && setActiveNavView(null)}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-gold" /> My Membership
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center" data-testid="customer-membership-placeholder">
            <BadgeCheck className="w-12 h-12 text-gold/40 mx-auto mb-4" />
            <p className="text-zinc-300 font-sans text-sm">Membership rewards are coming soon.</p>
            <p className="text-zinc-500 font-sans text-xs mt-2">Earn points on every order, unlock perks, and get exclusive offers — keep visiting Majnu!</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest tip prompt — REMOVED. Tipping deprecated on customer flow. */}


      {/* Takeaway info — captures name + phone before checkout */}
      <Dialog open={takeawayInfoOpen} onOpenChange={setTakeawayInfoOpen}>
        <DialogContent className="bg-bg-primary border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Pickup details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-zinc-400 text-sm font-sans">
              We&apos;ll text the pickup confirmation to your phone — your name helps us hand the bag to the right person.
            </p>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Name</Label>
              <Input
                autoFocus
                value={takeawayDraft.name}
                onChange={e => setTakeawayDraft(p => ({ ...p, name: e.target.value }))}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700"
                placeholder="Sarah"
                maxLength={40}
                data-testid="takeaway-info-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Mobile (required)</Label>
              <Input
                type="tel"
                value={takeawayDraft.phone}
                onChange={e => setTakeawayDraft(p => ({ ...p, phone: e.target.value }))}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700"
                placeholder="+61 4XX XXX XXX"
                data-testid="takeaway-info-phone"
              />
            </div>
            <Button
              onClick={() => {
                const name = (takeawayDraft.name || '').trim();
                const phone = (takeawayDraft.phone || '').trim();
                if (!name) { toast.error('Please enter your name'); return; }
                if (!phone || phone.replace(/\D/g, '').length < 6) { toast.error('Please enter a valid mobile number'); return; }
                try { localStorage.setItem('majnu:takeawayPhone', phone); } catch { /* */ }
                try { localStorage.setItem('majnu:guestName:default', name); } catch { /* */ }
                setGuestName(name);
                setTakeawayPhone(phone);
                setTakeawayInfoOpen(false);
                setTimeout(() => placeOrder(), 100);
              }}
              className="w-full h-12 bg-gold text-black font-bold rounded-sm font-sans"
              data-testid="takeaway-info-submit"
            >
              Continue to Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest checkout — ask for name before placing first order.
          DialogContent (Shadcn) is now top-aligned on mobile so the input
          and Place Order button stay above the iOS keyboard. */}
      <Dialog open={guestCheckoutOpen} onOpenChange={setGuestCheckoutOpen}>
        <DialogContent className="bg-bg-primary border border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Your name</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-zinc-400 text-sm font-sans">
              Tell us your name so we can identify you.
            </p>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Your name</Label>
              <Input
                autoFocus
                value={guestNameDraft}
                onChange={e => setGuestNameDraft(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') submitGuestName(); }}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700"
                placeholder="Sarah"
                maxLength={40}
                data-testid="guest-name-input"
              />
            </div>
            <Button onClick={submitGuestName} className="w-full h-12 bg-gold text-black font-bold rounded-sm font-sans" data-testid="guest-name-submit">
              Place Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* HARD-BLOCKER "Pay at the Bar" prompt — non-dismissable, only
          auto-clears when staff marks the order paid (or voids it). */}
      <CashCounterModal
        alert={cashCounterAlert}
        onRingBell={() => callService('payment')}
        onEdit={(orderId) => {
          const found = myOrders.find(o => o.id === orderId);
          if (found) setEditOrder(found);
        }}
        guestId={guestId}
        onMethodChanged={() => fetchOrders()}
      />
      {/* HARD-BLOCKER "Time to Collect Payment via EFTPOS" — manager
          comes to the table with the EFTPOS terminal. Clears once staff
          marks the order paid via the "Paid (Card)" button. */}
      <EftposTerminalModal
        alert={eftposAlert}
        onRingBell={() => callService('payment_eftpos')}
      />

      {/* Become a Member — post-order CTA. Owner removed the auto-10% discount;
          membership now positions as a prestige/loyalty programme without
          a blanket sign-up promotion. */}
      <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
        <DialogContent className="bg-bg-primary border border-gold/20 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-gold" /> Become our prestigious member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBecomeMember} className="space-y-3 pt-2" data-testid="become-member-form">
            <p className="text-zinc-400 text-sm font-sans">
              Become our <span className="text-gold font-medium">prestigious member</span> and be rewarded — save your details to skip this step next time and access exclusive member perks.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">First Name</Label>
                <Input value={memberForm.first_name} onChange={e => setMemberForm(p => ({ ...p, first_name: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Sarah" required data-testid="member-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Last Name</Label>
                <Input value={memberForm.last_name} onChange={e => setMemberForm(p => ({ ...p, last_name: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Smith" data-testid="member-last-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Email</Label>
              <Input type="email" value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="sarah@email.com" required data-testid="member-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Phone</Label>
              <Input type="tel" value={memberForm.phone} onChange={e => setMemberForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="+61 4XX XXX XXX" required data-testid="member-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-sans">Password</Label>
              <Input type="password" value={memberForm.password} onChange={e => setMemberForm(p => ({ ...p, password: e.target.value }))} className="bg-transparent border-0 border-b border-white/15 rounded-none focus:border-gold focus:ring-0 text-white h-10 font-sans text-sm placeholder:text-zinc-700" placeholder="Min 6 characters" required data-testid="member-password" />
            </div>
            <Button type="submit" disabled={memberSubmitting} className="w-full h-11 bg-gold text-black font-bold rounded-sm font-sans" data-testid="member-submit">
              {memberSubmitting ? 'Creating…' : 'Become a Member'}
            </Button>
            <button type="button" onClick={() => setMemberDialogOpen(false)} className="w-full text-zinc-500 hover:text-zinc-300 text-xs font-sans py-1" data-testid="member-skip">
              No thanks, maybe next time
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <OrderEditDialog
        open={!!editOrder}
        onOpenChange={(o) => !o && setEditOrder(null)}
        order={editOrder}
        actorRole="customer"
        onSaved={() => { setEditOrder(null); fetchOrders(); }}
      />
      {/* Tab top-up prompt. Fired when /api/orders returns 402 with
          code 'tab_topup_required'. Owner ask Feb 2026 — keep this
          short and friendly: customers don't need accounting jargon,
          they just need to know they have to pay the pending amount
          to keep ordering. */}
      <Dialog open={!!tabTopupPrompt} onOpenChange={(o) => !o && setTabTopupPrompt(null)}>
        <DialogContent className="bg-bg-secondary border border-amber-500/40 max-w-sm" data-testid="tab-topup-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-400" />
              Please pay your pending amount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-zinc-200 font-sans leading-relaxed">
              You have <strong className="text-amber-300 font-mono">${Number(tabTopupPrompt?.unpaid_balance || 0).toFixed(2)}</strong> unpaid on your tab.
              Please pay it to place more orders.
            </p>
            <div className="space-y-2 pt-1">
              <Button
                onClick={() => payTabTopupByCard(Math.max(Number(tabTopupPrompt?.unpaid_balance || 0), Number(tabTopupPrompt?.topup_minimum || 200)))}
                disabled={tabTopupLoading}
                className="w-full h-11 bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-sans font-bold"
                data-testid="tab-topup-pay-card"
              >
                {tabTopupLoading
                  ? '…'
                  : `Pay $${Math.max(Number(tabTopupPrompt?.unpaid_balance || 0), Number(tabTopupPrompt?.topup_minimum || 200)).toFixed(0)} by card`}
              </Button>
              <Button
                onClick={() => { requestStaffTabPayment('topup'); setTabTopupPrompt(null); }}
                variant="outline"
                className="w-full h-11 border-amber-500/60 text-amber-200 hover:bg-amber-500/20 rounded-sm font-sans"
                data-testid="tab-topup-call-staff"
              >
                Pay cash / EFTPOS at table
              </Button>
              <Button
                onClick={() => setTabTopupPrompt(null)}
                variant="ghost"
                className="w-full h-9 text-zinc-400 hover:text-zinc-200 rounded-sm font-sans text-xs"
                data-testid="tab-topup-dismiss"
              >
                Not now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Custom Shisha Mix builder. `availableFlavours` derives from the
          existing shisha menu items (Standard + Premium) so it stays in
          sync automatically as the manager adds new flavours. */}
      <CustomMixDialog
        open={!!mixItem}
        onOpenChange={(o) => !o && setMixItem(null)}
        item={mixItem}
        availableFlavours={menu
          .filter(m => m.category === 'shisha' && !m.is_custom_mix && m.is_available !== false)
          .map(m => ({ id: m.id, name: m.name, subcategory: m.subcategory || '' }))}
        onAdd={addMixToCart}
      />

      {/* Settle Tab — Feb 2026 FINAL ORDERING WORKFLOW */}
      <SettleTabDialog
        open={settleTabOpen}
        onOpenChange={setSettleTabOpen}
        tab={tab}
        outstanding={Math.max(0, tabRunningTotal - Number(tab?.paid_amount || 0))}
        tableTabs={tableTabs}
        tableId={table?.id || null}
        tableNumber={table?.number || null}
        guestId={guestId}
        customerName={user?.name || user?.first_name || tab?.customer_name || ''}
      />
    </div>
  );
}

/* ============================================================
   WELCOME-BACK RE-ORDER STRIP
   Surfaces the guest's last 3 distinct ordered items so a regular
   can one-tap re-order without navigating the menu. Pulls from
   GET /api/guest-history/{guestId} which dedupes by item_id and
   drops items no longer on the active menu, so we never advertise
   something that 404s on Add.

   Hidden states (parent already gates on staffOrderMode + tab):
     • Backend returns no items (first-time guest or all-deleted items)
     • Single dismiss button — sticks for 7 days so a customer who said
       "no thanks" doesn't see it again next scan but eventually we
       re-surface to catch newly added regulars.
   ============================================================ */
function WelcomeBackStrip({ guestId, menu, onQuickAdd }) {
  const [data, setData] = React.useState(null);
  const [hidden, setHidden] = React.useState(() => {
    try {
      const t = parseInt(localStorage.getItem('majnu:welcomeBack:hiddenUntil') || '0', 10);
      return t > Date.now();
    } catch { return false; }
  });

  React.useEffect(() => {
    if (!guestId || hidden) return;
    let cancelled = false;
    api.get(`/guest-history/${guestId}`, { params: { limit: 3 } })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { /* swallow — first-time guest is the happy path */ });
    return () => { cancelled = true; };
  }, [guestId, hidden]);

  if (hidden || !data || !data.items || data.items.length === 0) return null;

  const hideForAWhile = () => {
    setHidden(true);
    try {
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem('majnu:welcomeBack:hiddenUntil', String(Date.now() + sevenDaysMs));
    } catch { /* swallow */ }
  };

  const handleReorder = (histItem) => {
    // Always resolve back to the live menu item so the customize-routing
    // in quickAdd has the full metadata (head_options, addon_groups,
    // variants). Falls back to the history record (with sensible defaults)
    // if the menu hasn't loaded yet — better than blocking the tap.
    const liveItem = menu.find(m => m.id === histItem.item_id) || {
      id: histItem.item_id,
      name: histItem.name,
      price: histItem.price,
      category: histItem.category,
      subcategory: histItem.subcategory,
    };
    onQuickAdd(liveItem);
  };

  const firstName = (data.guest_name || '').trim().split(/\s+/)[0];

  return (
    <section
      className="relative z-10 majnu-fade mx-2 sm:mx-0 mt-2 mb-1"
      style={{ animationDelay: '60ms' }}
      data-testid="welcome-back-strip"
    >
      <div className="rounded-sm border border-gold/30 bg-gradient-to-br from-gold/[0.08] to-transparent backdrop-blur-sm p-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-gold font-sans text-[11px] uppercase tracking-[0.16em]">
            {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
            <span className="text-zinc-500 normal-case tracking-normal ml-1">· your usual?</span>
          </p>
          <button
            onClick={hideForAWhile}
            className="shrink-0 w-5 h-5 rounded-sm text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.05] flex items-center justify-center transition"
            aria-label="Hide for now"
            data-testid="welcome-back-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x" data-testid="welcome-back-items">
          {data.items.map(it => (
            <button
              key={it.item_id}
              onClick={() => handleReorder(it)}
              className="shrink-0 w-32 snap-start rounded-sm border border-white/10 bg-white/[0.03] hover:border-gold/40 hover:bg-gold/[0.06] transition p-2 text-left"
              data-testid={`welcome-back-item-${it.item_id}`}
            >
              <p className="font-serif text-white text-xs leading-tight line-clamp-2 min-h-[2.2em]">{it.name}</p>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-gold font-mono text-[11px]">${Number(it.price).toFixed(2)}</span>
                <span className="text-[9px] uppercase tracking-[0.12em] text-emerald-400 font-sans font-semibold flex items-center gap-0.5">
                  <Plus className="w-3 h-3" /> Add
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ============================================================
   NO-DEPOSIT TRUST RIBBON
   Slim reassurance for first-time QR scanners. Per the Feb 2026
   workflow rewrite, opening a tab no longer requires any deposit
   — but word lingers, so we surface the policy at the top of the
   menu. Dismissible; the dismissal is remembered per-device via
   localStorage so returning customers don't see it every visit.
   ============================================================ */
function NoDepositRibbon({ visible }) {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem('majnu:noDepositRibbon:dismissed') === '1'; }
    catch { return false; }
  });
  if (!visible || dismissed) return null;
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem('majnu:noDepositRibbon:dismissed', '1'); } catch { /* swallow */ }
  };
  return (
    <section
      className="relative z-10 majnu-fade mx-2 sm:mx-0 mt-1 mb-1"
      style={{ animationDelay: '90ms' }}
      data-testid="no-deposit-ribbon"
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-emerald-500/30 bg-emerald-500/[0.06] backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 border border-emerald-500/40 shrink-0"
            aria-hidden="true"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </span>
          <p className="text-emerald-200 font-sans text-[11px] sm:text-xs leading-tight">
            <span className="font-semibold tracking-[0.04em]">Open a tab</span>
            <span className="text-emerald-300/70 mx-1.5">·</span>
            <span className="text-emerald-300/90">no deposit</span>
            <span className="text-emerald-300/70 mx-1.5">·</span>
            <span className="text-emerald-300/90">pay when you leave</span>
          </p>
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 w-6 h-6 rounded-sm text-emerald-300/60 hover:text-emerald-200 hover:bg-emerald-500/15 flex items-center justify-center transition"
          aria-label="Dismiss"
          data-testid="no-deposit-ribbon-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </section>
  );
}

